/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ISPGenerator from "./components/ISPGenerator.jsx";
import {
  buildSectionMarkLookup,
  calculateSubjectResult,
  getFoundationOverallPossible,
  getFoundationSubjectDefinition,
} from "./foundationPhase";
import FeatureTour, { TourButton, useFeatureTour } from "./components/FeatureTour.jsx";
import HelpTip from "./components/HelpTip.jsx";
import SectionSetupHelper from "./components/SectionSetupHelper.jsx";
import MarkedTests from "./components/MarkedTests.jsx";

// First visit to a subject: a walk through everything on the page, top to bottom.
const SUBJECT_TOUR = [
  { title: "Your class mark book", body: "Everything for this class and subject lives on this page: learners, assessments, marks and results. This tour takes about two minutes, and you can replay it any time with 'Take the tour'." },
  { target: ".term-tabs", title: "Choose the term first", body: "Assessments, marks and results below are all for the selected term. Check the term before you capture marks; if it isn't the current school term, MarkSure asks you to confirm before saving." },
  { target: ".header-meta", title: "This term at a glance", body: "How many learners are in the class, and how many sections and assessments you've set up for this term." },
  { target: ".setup-card", title: "Set up the term's assessments", body: "Before you capture marks, add the term's tests, assignments and projects here, each with a max mark and weight. Press this card's own 'Take the tour' button for a field-by-field guide." },
  { target: "[data-tour='import']", title: "Import marks from Excel", body: "Open this, download the Excel template, fill in each learner's name, surname and a column per assessment, then upload it. MarkSure matches the learners and works out the totals." },
  { target: ".add-student-card", title: "Add one learner", body: "For a learner who joins during the term or was missed in an import. Type their first name and surname and they're added to this class." },
  { target: ".roster-scan-card", title: "Scan a class list", body: "Take a photo of a printed or handwritten class list. MarkSure reads the names, you check them, and the whole class is added in one step." },
  { target: "[data-tour='pass']", title: "Pass requirements", body: "This subject uses your school's pass mark, support and distinction thresholds. Open it only if this subject needs different ones." },
  { target: ".modern-table .cell-input", title: "Type the marks", body: "Click a cell and type the mark. Press Enter or the down arrow to move to the next learner, and the arrow keys to move around. Each mark is saved when you leave its cell. A mark above the maximum is brought down to the maximum, with a warning." },
  { target: "[data-tour='save-marks']", title: "Save Changes", body: "Saves every mark you've changed in one go, handy after a long capturing session. It shows 'Saved' when everything is stored." },
  { target: "[data-tour='voice']", title: "Voice marking", body: "Capture marks by speaking: choose the assessment, press start, then say a learner's name and their mark. Each mark is saved as it's recognised." },
  { target: ".compact-table-toggle", title: "Results columns", body: "Show or hide the totals, percentages and CAPS levels at the end of each row, so the table fits your screen while you capture." },
  { target: ".learner-levels-mini", title: "Learner levels", body: "How many learners are at each CAPS level from 1 to 7. Click it to see the full graph." },
  { target: ".table-toolbar-risk-action", title: "Top 10 and At Risk", body: "Show your ten strongest learners, or the learners below the pass mark who may need extra support." },
  { target: "button[aria-label='Generate ISP (Word)']", title: "Individual Support Plans", body: "The blue button in a learner's row drafts an Individual Support Plan from their marks and downloads it as a Word document for you to finish." },
  { target: ".stats-section-title", title: "Term analytics", body: "Class averages and how each assessment went, updated as you capture marks. Use them for reports and moderation." },
  { title: "You're ready", body: "Start by setting up this term's assessments, then capture or import the marks. Press 'Take the tour' at the top of the page whenever you need a reminder." },
];

// First time the teacher opens "Assessment Sections": what each field means.
const ASSESSMENT_TOUR = [
  { title: "Setting up an assessment", body: "An assessment, like a test or project, is made of sections: a question or part of the paper, each with a max mark. Captured and AI-marked marks go into these sections." },
  { target: ".ssh", title: "Let MarkSure set it up", body: "Not sure how to split your paper? Upload your memo or question paper and MarkSure reads its sections and marks, or start from a common layout for this subject. You check everything before it's added." },
  { target: "[data-tour='assessment-name']", title: "Or add sections yourself", body: "For example 'Term 1 Test'. Sections with the same name and category are grouped into one assessment." },
  { target: "[data-tour='section-name']", title: "Add a section", body: "A question or part of the paper, such as 'Question 1'. A short test can also be one section for the whole paper." },
  { target: "[data-tour='max-mark']", title: "Max mark", body: "What this section is out of." },
  { target: "[data-tour='weight']", title: "Overall weight", body: "How much the whole assessment counts towards the term mark. All sections of one assessment share the same weight." },
  { target: "[data-tour='category']", title: "Category", body: "Optional. Labels the assessment, such as Exam or Assignment 1, so it's grouped correctly in reports and exports." },
  { target: "[data-tour='add-section']", title: "Add it", body: "Add each section in turn. They appear in the list below, where you can still edit names, marks and weights." },
];

// --- SVG Icons ---
const Icons = {
  Chart: () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="20" x2="18" y2="10"></line>
      <line x1="12" y1="20" x2="12" y2="4"></line>
      <line x1="6" y1="20" x2="6" y2="14"></line>
    </svg>
  ),
  Check: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  Save: () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
      <polyline points="17 21 17 13 7 13 7 21"></polyline>
      <polyline points="7 3 7 8 15 8"></polyline>
    </svg>
  ),
  Print: () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 6 2 18 2 18 9"></polyline>
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
      <rect x="6" y="14" width="12" height="8"></rect>
    </svg>
  ),
  Trash: () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  ),
  Plus: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Copy: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  ),
  Info: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  ),
  ChevronDown: ({ className = "" }) => (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  ),
  Layers: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
      <polyline points="2 17 12 22 22 17"></polyline>
      <polyline points="2 12 12 17 22 12"></polyline>
    </svg>
  ),
  Clipboard: () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
    </svg>
  ),
};

const WorkflowModal = ({ title, onClose, children }) => (
  <div
    className="workflow-modal-backdrop"
    role="presentation"
    onMouseDown={onClose}
  >
    <section
      className="workflow-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header className="workflow-modal-header">
        <h2>{title}</h2>
        <button
          type="button"
          className="workflow-modal-close"
          onClick={onClose}
          aria-label={`Close ${title}`}
        >
          ×
        </button>
      </header>
      <div className="workflow-modal-content">{children}</div>
    </section>
  </div>
);

const LearnerLevelsChart = ({
  distribution,
  highestCount,
  compact = false,
}) => (
  <div
    className={`learner-levels-chart${compact ? " learner-levels-chart--compact" : ""}`}
    role="img"
    aria-label={`Learner levels: ${distribution.map((item) => `Level ${item.level}, ${item.count} learners`).join("; ")}`}
  >
    {distribution.map((item) => (
      <div
        className={`learner-levels-chart__bar level-${item.level}`}
        key={item.level}
      >
        {!compact && <strong>{item.count}</strong>}
        <div className="learner-levels-chart__track" aria-hidden="true">
          <span style={{ height: `${(item.count / highestCount) * 100}%` }} />
        </div>
        <span>L{item.level}</span>
      </div>
    ))}
  </div>
);

const normalizeGroupText = (value) => String(value || "").trim();

// Some shared PHP hosts enforce a 2 MB multipart request limit that cannot be
// raised from an application .user.ini. Keep class-list scans below that limit
// in the browser while retaining enough resolution for handwriting OCR.
const prepareRosterImageForUpload = async (file) => {
  const safeUploadBytes = 1500 * 1024;
  if (file.size <= safeUploadBytes) return file;

  const image = await new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected image could not be prepared."));
    };
    img.src = objectUrl;
  });
  let scale = Math.min(
    1,
    2200 / Math.max(image.naturalWidth, image.naturalHeight),
  );
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const quality = Math.max(0.58, 0.86 - attempt * 0.06);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) throw new Error("The selected image could not be prepared.");
    if (blob.size <= safeUploadBytes || attempt === 5) {
      return new File(
        [blob],
        `${file.name.replace(/\.[^.]+$/, "") || "classlist"}-scan.jpg`,
        { type: "image/jpeg" },
      );
    }
    scale *= 0.82;
  }
  return file;
};

const formatScannedMarks = (marks = []) =>
  marks
    .filter((mark) => mark?.section_name && Number.isFinite(Number(mark.mark)))
    .map((mark) => `${mark.section_name}: ${mark.mark}`)
    .join("; ");

const parseScannedMarks = (value) =>
  String(value || "")
    .split(/[;\n]/)
    .map((entry) => {
      const separator = entry.lastIndexOf(":");
      if (separator < 1) return null;
      const section_name = entry.slice(0, separator).trim();
      const mark = Number(
        entry
          .slice(separator + 1)
          .trim()
          .replace(",", "."),
      );
      return section_name && Number.isFinite(mark)
        ? { section_name, mark }
        : null;
    })
    .filter(Boolean);

const normalizeAssessmentName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const parsePositiveNumber = (value, fallback = 1) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getDefaultSubjectPassMark = (subjectName) => {
  const lower = String(subjectName || "")
    .trim()
    .toLowerCase();
  return lower.includes("home language") || lower.includes("huistaal")
    ? 50
    : 40;
};

const getDefaultSupportThreshold = () => 55;
const getDefaultDistinctionThreshold = () => 70;

// MarkSure follows the usual South African four-term school-year pattern. This
// intentionally uses the teacher's local date so the warning remains useful
// even when the API is unavailable.
const getDetectedAcademicTerm = (date = new Date()) => {
  const month = date.getMonth();
  if (month <= 2) return 1;
  if (month <= 5) return 2;
  if (month <= 8) return 3;
  return 4;
};

const normalizeVoiceText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getSpokenMark = (transcript) => {
  const numericMatches = normalizeVoiceText(transcript).match(
    /\b\d{1,3}(?:\.\d+)?\b/g,
  );
  if (numericMatches?.length)
    return Number(numericMatches[numericMatches.length - 1]);
  const values = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
    thirty: 30,
    forty: 40,
    fifty: 50,
    sixty: 60,
    seventy: 70,
    eighty: 80,
    ninety: 90,
    hundred: 100,
  };
  const words = normalizeVoiceText(transcript).split(" ");
  let current = 0;
  let found = false;
  for (const word of words) {
    if (values[word] === undefined) continue;
    found = true;
    current =
      word === "hundred" ? Math.max(1, current) * 100 : current + values[word];
  }
  return found ? current : null;
};

const getAssessmentGroupKey = (section) => {
  const assessmentGroup = normalizeGroupText(section?.assessment_group);
  if (assessmentGroup) {
    return `group:${assessmentGroup.toLowerCase()}`;
  }
  const category = normalizeGroupText(section?.category);
  if (category) {
    return `category:${category.toLowerCase()}`;
  }
  return `section:${section?.id}`;
};

const getAssessmentGroupLabel = (section) => {
  const assessmentGroup = normalizeGroupText(section?.assessment_group);
  if (assessmentGroup) {
    return assessmentGroup;
  }
  const category = normalizeGroupText(section?.category);
  if (category) {
    return category;
  }
  return section?.section_name || "Assessment";
};

const buildAssessmentGroups = (sections) => {
  const groups = new Map();
  (sections || []).forEach((section) => {
    const key = getAssessmentGroupKey(section);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: getAssessmentGroupLabel(section),
        sections: [],
        rawMax: 0,
        weights: new Set(),
        primaryGroupName: normalizeGroupText(section?.assessment_group),
        usesCategoryFallback:
          !normalizeGroupText(section?.assessment_group) &&
          !!normalizeGroupText(section?.category),
      });
    }
    const group = groups.get(key);
    group.sections.push(section);
    group.rawMax += parseFloat(section?.max_mark) || 0;
    group.weights.add(parsePositiveNumber(section?.weight, 1));
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      currentWeight: group.weights.size > 0 ? Array.from(group.weights)[0] : 1,
      hasMixedWeights: group.weights.size > 1,
    }))
    .sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), undefined, {
        sensitivity: "base",
      }),
    );
};

const buildExcelSectionDescriptor = (section, groupLabelOverride = "") => {
  const sectionLabel = normalizeGroupText(
    section?.section_name || "Assessment",
  );
  const groupLabel = normalizeGroupText(
    groupLabelOverride || getAssessmentGroupLabel(section),
  );

  return {
    sectionLabel,
    groupLabel,
    normalizedSectionLabel: sectionLabel.toLowerCase(),
    normalizedGroupLabel: groupLabel.toLowerCase(),
    compositeKey: `${groupLabel.toLowerCase()}|${sectionLabel.toLowerCase()}`,
  };
};

const isGroupedExcelTemplate = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) {
    return false;
  }

  const primaryHeader = Array.isArray(rows[0]) ? rows[0] : [];
  const secondaryHeader = Array.isArray(rows[1]) ? rows[1] : [];
  const firstCell = normalizeGroupText(primaryHeader[0]).toLowerCase();
  const secondCell = normalizeGroupText(primaryHeader[1]).toLowerCase();

  if (firstCell !== "first name" || secondCell !== "last name") {
    return false;
  }

  const secondRowNameCellsBlank =
    !normalizeGroupText(secondaryHeader[0]) &&
    !normalizeGroupText(secondaryHeader[1]);
  const hasSecondarySectionLabels = secondaryHeader
    .slice(2)
    .some((value) => normalizeGroupText(value));
  return secondRowNameCellsBlank && hasSecondarySectionLabels;
};

const buildSectionImportLookup = (sectionList, headerMetaById = {}) => {
  const sectionsByCompositeKey = new Map();
  const sectionsByLabel = new Map();

  (sectionList || []).forEach((section) => {
    const descriptor = buildExcelSectionDescriptor(
      section,
      normalizeGroupText(
        headerMetaById?.[section.id]?.groupLabel ||
          getAssessmentGroupLabel(section),
      ),
    );

    if (!sectionsByCompositeKey.has(descriptor.compositeKey)) {
      sectionsByCompositeKey.set(descriptor.compositeKey, []);
    }
    sectionsByCompositeKey.get(descriptor.compositeKey).push(section);

    if (!sectionsByLabel.has(descriptor.normalizedSectionLabel)) {
      sectionsByLabel.set(descriptor.normalizedSectionLabel, []);
    }
    sectionsByLabel.get(descriptor.normalizedSectionLabel).push(section);
  });

  return { sectionsByCompositeKey, sectionsByLabel };
};

const shiftNextSectionMatch = (lookup, key) => {
  const bucket = lookup.get(key);
  if (!bucket || bucket.length === 0) {
    return null;
  }
  return bucket.shift() || null;
};

const buildImportedSectionColumns = (rows, groupedTemplate) => {
  const groupHeader = Array.isArray(rows[0]) ? rows[0] : [];
  const sectionHeader =
    groupedTemplate && Array.isArray(rows[1]) ? rows[1] : groupHeader;
  const maxColumnCount = Math.max(groupHeader.length, sectionHeader.length);
  const columns = [];

  for (
    let excelColIndex = 2;
    excelColIndex < maxColumnCount;
    excelColIndex += 1
  ) {
    const groupLabel = groupedTemplate
      ? normalizeGroupText(groupHeader[excelColIndex])
      : "";
    const sectionLabel = normalizeGroupText(sectionHeader[excelColIndex]);
    if (!groupLabel && !sectionLabel) {
      continue;
    }

    columns.push({
      excelColIndex,
      groupLabel,
      sectionLabel,
      normalizedSectionLabel: sectionLabel.toLowerCase(),
      compositeKey: `${groupLabel.toLowerCase()}|${sectionLabel.toLowerCase()}`,
    });
  }

  return columns;
};

const getSubjectTermStorageKey = (subjectId) =>
  `subjectdetail.currentTerm.${subjectId}`;

const getStoredSubjectTerm = (subjectId) => {
  if (typeof window === "undefined" || !subjectId) {
    return getDetectedAcademicTerm();
  }

  const storedValue = window.localStorage.getItem(
    getSubjectTermStorageKey(subjectId),
  );
  const parsedTerm = parseInt(storedValue || "", 10);
  return [1, 2, 3, 4].includes(parsedTerm)
    ? parsedTerm
    : getDetectedAcademicTerm();
};

export default function Subjectdetail() {
  // Modal for resource suggestions/interventions
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceSuggestions, setResourceSuggestions] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);

  // Fetch resources for the current subject/grade
  const fetchResourceSuggestions = async () => {
    setLoadingResources(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(
        (process.env.REACT_APP_API_URL || "http://localhost/api") +
          "/fetch_library.php",
        {
          params: { grade: subjectGrade, subject: subjectname },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setResourceSuggestions(res.data.resources || []);
    } catch (e) {
      setResourceSuggestions([]);
    }
    setLoadingResources(false);
  };
  // Collapse state for Assessment Sections
  const [sectionsCollapsed, setSectionsCollapsed] = useState(true);
  const assessmentTour = useFeatureTour("assessment-setup", !sectionsCollapsed);
  // Collapse state for Import/Export and Add Student
  const [importExportCollapsed, setImportExportCollapsed] = useState(true);
  const [addStudentCollapsed, setAddStudentCollapsed] = useState(true);
  const [passRequirementsCollapsed, setPassRequirementsCollapsed] =
    useState(true);
  // --- Top 10 Students by Percentage ---
  const [showTop10, setShowTop10] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedTerm = Number(searchParams.get("term"));
  const requestedTerm = [1, 2, 3, 4].includes(linkedTerm) ? linkedTerm : null;
  const linkedStudent = searchParams.get("student");
  const linkedSection = searchParams.get("section");
  const focusedPendingTarget = useRef(null);

  // --- State ---
  const [currentTerm, setCurrentTerm] = useState(
    () => requestedTerm || getStoredSubjectTerm(id),
  );
  const [termReadySubjectId, setTermReadySubjectId] = useState(() => id || "");
  const [students, setStudents] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionMarks, setSectionMarks] = useState([]);
  const [marksBeingEdited, setMarksBeingEdited] = useState({});
  const [subjectname, setSubjectName] = useState("Loading...");
  const [subjectGrade, setSubjectGrade] = useState("");
  const [subjectClass, setSubjectClass] = useState("");
  const [subjectGradingMode, setSubjectGradingMode] = useState("standard");
  const [foundationTemplateCode, setFoundationTemplateCode] = useState("");
  const [foundationTemplateDefinition, setFoundationTemplateDefinition] =
    useState(null);
  const [subjectPassRequirements, setSubjectPassRequirements] = useState(null);
  const [savingPassRequirements, setSavingPassRequirements] = useState(false);
  const [loading, setLoading] = useState(true);
  const subjectTour = useFeatureTour("subject-page", !loading);

  // UI State
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvProgress, setCsvProgress] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showRosterScan, setShowRosterScan] = useState(false);
  const [rosterImage, setRosterImage] = useState(null);
  const [rosterPreview, setRosterPreview] = useState("");
  const [rosterScanning, setRosterScanning] = useState(false);
  const [rosterRows, setRosterRows] = useState([]);
  const [rosterError, setRosterError] = useState("");
  const [rosterImporting, setRosterImporting] = useState(false);
  const [showCompletionCelebration, setShowCompletionCelebration] =
    useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showLearnerLevelsModal, setShowLearnerLevelsModal] = useState(false);
  const [markedRuns, setMarkedRuns] = useState([]);
  const [markedTestsView, setMarkedTestsView] = useState(null);
  const [voiceSectionId, setVoiceSectionId] = useState("");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voiceCandidate, setVoiceCandidate] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState("");
  const [voiceListening, setVoiceListening] = useState(false);
  const [savingVoiceMark, setSavingVoiceMark] = useState(false);
  const [saveAllDisabled, setSaveAllDisabled] = useState(false);
  const [saveComplete, setSaveComplete] = useState(false);
  // Results are part of normal mark entry; users can explicitly hide them when needed.
  const [showTableResults, setShowTableResults] = useState(true);
  const [markLimitNotice, setMarkLimitNotice] = useState("");
  const saveCompleteTimerRef = useRef(null);
  const completionStateRef = useRef({ key: "", wasComplete: false });
  const completionTimerRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const voiceRestartTimerRef = useRef(null);
  const voiceSessionActiveRef = useRef(false);
  const confirmedOffTermEntriesRef = useRef(new Set());

  const stopVoiceListening = useCallback(() => {
    voiceSessionActiveRef.current = false;
    if (voiceRestartTimerRef.current) {
      window.clearTimeout(voiceRestartTimerRef.current);
      voiceRestartTimerRef.current = null;
    }
    speechRecognitionRef.current?.stop?.();
    speechRecognitionRef.current = null;
    setVoiceListening(false);
  }, []);

  const confirmTermForMarkEntry = useCallback(() => {
    const detectedTerm = getDetectedAcademicTerm();
    if (
      currentTerm === detectedTerm ||
      confirmedOffTermEntriesRef.current.has(currentTerm)
    ) {
      return true;
    }

    const confirmed = window.confirm(
      `MarkSure detects that it is currently Term ${detectedTerm}. Do you really want to enter marks in Term ${currentTerm}?`,
    );

    if (confirmed) {
      confirmedOffTermEntriesRef.current.add(currentTerm);
    }
    return confirmed;
  }, [currentTerm]);

  useEffect(
    () => () => {
      if (saveCompleteTimerRef.current) {
        window.clearTimeout(saveCompleteTimerRef.current);
      }
      if (completionTimerRef.current) {
        window.clearTimeout(completionTimerRef.current);
      }
      stopVoiceListening();
    },
    [stopVoiceListening],
  );

  // Forms
  const [newStudent, setNewStudent] = useState({ firstName: "", lastName: "" });
  const rosterFileInputRef = useRef(null);
  const rosterCameraInputRef = useRef(null);
  const [newSection, setNewSection] = useState({
    section_name: "",
    weight: 1,
    max_mark: 100,
    category: "",
    assessment_group: "",
  });
  const [addingSection, setAddingSection] = useState(false);
  const [assessmentWeightDrafts, setAssessmentWeightDrafts] = useState({});
  const [assessmentNameDrafts, setAssessmentNameDrafts] = useState({});
  const [sectionNameDrafts, setSectionNameDrafts] = useState({});
  const [savingAssessmentNameKey, setSavingAssessmentNameKey] = useState("");
  const [savingAssessmentGroupKey, setSavingAssessmentGroupKey] = useState("");

  // Debounce State
  const [sectionMaxMarks, setSectionMaxMarks] = useState({});

  // Notes State
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Copy Section State
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [otherSubjects, setOtherSubjects] = useState([]);
  const [copyingSection, setCopyingSection] = useState(false);

  // Refs - Initialize once with useRef and useCallback
  const markInputRefs = useRef({});
  const maxMarkTimersRef = useRef({});
  const getMarkInputRef = useCallback((sectionId, studentId) => {
    if (!markInputRefs.current[sectionId]) {
      markInputRefs.current[sectionId] = {};
    }
    if (!markInputRefs.current[sectionId][studentId]) {
      markInputRefs.current[sectionId][studentId] = React.createRef();
    }
    return markInputRefs.current[sectionId][studentId];
  }, []);

  // --- Helpers ---
  const calculateLevel = useCallback((percentage) => {
    const rounded = Math.round(percentage);
    if (rounded >= 80) return 7;
    if (rounded >= 70) return 6;
    if (rounded >= 60) return 5;
    if (rounded >= 50) return 4;
    if (rounded >= 40) return 3;
    if (rounded >= 30) return 2;
    return 1;
  }, []);

  const levelToSymbol = useCallback((level) => {
    switch (level) {
      case 7:
        return "A*";
      case 6:
        return "A";
      case 5:
        return "B";
      case 4:
        return "C";
      case 3:
        return "D";
      case 2:
        return "E";
      case 1:
        return "F";
      default:
        return level;
    }
  }, []);

  const formatGrade = useCallback(
    (level) => {
      const gradeDisplayFormat =
        localStorage.getItem("gradeDisplayFormat") || "levels";
      return gradeDisplayFormat === "symbols" ? levelToSymbol(level) : level;
    },
    [levelToSymbol],
  );

  const activePassRequirements = useMemo(() => {
    const defaultPass = getDefaultSubjectPassMark(subjectname);
    const defaultSupport = getDefaultSupportThreshold();
    const defaultDistinction = getDefaultDistinctionThreshold();
    const parseThreshold = (value) => {
      if (value === "" || value == null) return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };
    const passOverride = parseThreshold(
      subjectPassRequirements?.pass_mark_override,
    );
    const supportOverride = parseThreshold(
      subjectPassRequirements?.support_threshold_override,
    );
    const distinctionOverride = parseThreshold(
      subjectPassRequirements?.distinction_threshold_override,
    );

    return {
      default_pass_mark:
        subjectPassRequirements?.default_pass_mark ?? defaultPass,
      default_support_threshold:
        subjectPassRequirements?.default_support_threshold ?? defaultSupport,
      default_distinction_threshold:
        subjectPassRequirements?.default_distinction_threshold ??
        defaultDistinction,
      pass_mark_override: passOverride,
      support_threshold_override: supportOverride,
      distinction_threshold_override: distinctionOverride,
      pass_mark:
        passOverride ?? subjectPassRequirements?.pass_mark ?? defaultPass,
      support_threshold:
        supportOverride ??
        subjectPassRequirements?.support_threshold ??
        defaultSupport,
      distinction_threshold:
        distinctionOverride ??
        subjectPassRequirements?.distinction_threshold ??
        defaultDistinction,
      has_override:
        passOverride !== null ||
        supportOverride !== null ||
        distinctionOverride !== null ||
        Boolean(subjectPassRequirements?.has_override),
    };
  }, [subjectPassRequirements, subjectname]);

  // --- Calculate Stats Per Section (Optimized with Memoization) ---
  const getSectionStats = useCallback(
    (section) => {
      const validMarks = [];
      const studentIds = new Set(); // Track unique students
      const sectionId = parseInt(section.id);

      for (const mark of sectionMarks) {
        if (
          parseInt(mark.section_id) === sectionId &&
          mark.mark !== "" &&
          mark.mark != null &&
          !isNaN(parseFloat(mark.mark))
        ) {
          validMarks.push(parseFloat(mark.mark));
          studentIds.add(parseInt(mark.student_id)); // Add unique student ID
        }
      }

      if (validMarks.length === 0) return null;

      const count = studentIds.size; // Count unique students, not marks
      const max = Math.max(...validMarks);
      const min = Math.min(...validMarks);
      const sum = validMarks.reduce((a, b) => a + b, 0);
      const avg = sum / validMarks.length;
      const maxPossible = section.max_mark || 100;
      const avgPerc = (avg / maxPossible) * 100;

      return { count, max, min, avg, avgPerc };
    },
    [sectionMarks],
  );

  // --- NOTES HELPERS ---
  const getDueDateStatus = useCallback((dueDate) => {
    if (!dueDate) return { status: "future", label: "", daysLeft: null };
    const due = new Date(dueDate);
    const now = new Date();
    const diff = due - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0)
      return { status: "overdue", label: "Overdue", daysLeft: Math.abs(days) };
    if (days === 0)
      return { status: "due-today", label: "Due Today", daysLeft: 0 };
    if (days <= 2)
      return {
        status: "due-soon",
        label: `Due in ${days} day${days > 1 ? "s" : ""}`,
        daysLeft: days,
      };
    if (days <= 7)
      return {
        status: "due-week",
        label: `Due in ${days} days`,
        daysLeft: days,
      };
    return { status: "future", label: `Due in ${days} days`, daysLeft: days };
  }, []);

  const handleCreateNote = async (e) => {
    e.preventDefault();
    const cleanText = noteText.trim();
    if (!cleanText) return;

    setSavingNote(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/subject_notes.php`,
        {
          action: "create",
          subject_id: id,
          note_text: cleanText,
          is_important: isImportant ? 1 : 0,
          due_date: dueDate || null,
        },
      );

      if (!res.data.success) {
        alert(res.data.error || "Failed to save note");
      } else {
        setNoteText("");
        setIsImportant(false);
        setDueDate("");
        // Reload notes
        const notesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/subject_notes.php?subject_id=${id}`,
        );
        setNotes(notesRes.data.notes || []);
        // Notify other views (notes page, dashboard) to refresh
        try {
          window.dispatchEvent(
            new CustomEvent("notesUpdated", { detail: { subject_id: id } }),
          );
        } catch (e) {}
      }
    } catch (error) {
      alert(error.response?.data?.error || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/subject_notes.php`,
        {
          action: "delete",
          note_id: noteId,
        },
      );
      if (res.data.success) {
        setNotes(notes.filter((n) => n.id !== noteId));
        try {
          window.dispatchEvent(
            new CustomEvent("notesUpdated", { detail: { subject_id: id } }),
          );
        } catch (e) {}
      } else {
        alert(res.data.error || "Failed to delete note");
      }
    } catch (error) {
      alert(error.response?.data?.error || "Failed to delete note");
    }
  };

  const handleToggleNoteDone = async (noteId, currentDone) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/subject_notes.php`,
        {
          action: "update",
          note_id: noteId,
          is_done: currentDone ? 0 : 1,
        },
      );
      if (res.data.success) {
        setNotes(
          notes.map((n) =>
            n.id === noteId ? { ...n, is_done: currentDone ? 0 : 1 } : n,
          ),
        );
        try {
          window.dispatchEvent(
            new CustomEvent("notesUpdated", { detail: { subject_id: id } }),
          );
        } catch (e) {}
      }
    } catch (error) {
      console.error("Failed to update note status");
    }
  };

  // --- COPY SECTION LOGIC ---
  const handleOpenCopyModal = async () => {
    setShowCopyModal(true);

    // Fetch other subjects owned by teacher
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/fetch_dashboard.php`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // Filter to only other subjects (not current one)
      const others = (res.data.subjects || []).filter(
        (s) => String(s.subject_id) !== String(id),
      );
      setOtherSubjects(others);
    } catch (error) {
      alert("Failed to load other subjects");
      setShowCopyModal(false);
    }
  };

  const handleCopyAllAssessments = async (targetSubjectId) => {
    if (!id) {
      alert("Error: Subject ID not found");
      return;
    }

    const sourceId = parseInt(id);
    if (isNaN(sourceId)) {
      alert("Error: Invalid subject ID");
      return;
    }

    if (!targetSubjectId) {
      alert("Error: Target subject ID not found");
      return;
    }

    setCopyingSection(true);
    try {
      const token = localStorage.getItem("auth_token");
      const payload = {
        source_subject_id: sourceId,
        target_subject_id: parseInt(targetSubjectId),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/duplicate_section.php`,
        JSON.stringify(payload),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        alert(`${res.data.message}`);
        setShowCopyModal(false);
        // Could reload sections here if we want, but typically user navigates to target subject
      } else {
        alert(res.data.error || "Copy failed");
      }
    } catch (error) {
      console.error(
        "Copy error details:",
        error.response?.data || error.message,
      );
      alert(error.response?.data?.error || "Failed to copy assessments");
    } finally {
      setCopyingSection(false);
    }
  };

  // --- LOGIC: Grid Navigation (Optimized with cached indexes) ---
  const handleGridNavigation = useCallback(
    (e, currentStudentId, currentSectionId) => {
      const allowedKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Enter",
      ];
      if (!allowedKeys.includes(e.key)) return;

      e.preventDefault();

      const studentIndex = students.findIndex(
        (s) => String(s.student_id) === String(currentStudentId),
      );
      const sectionIndex = sections.findIndex(
        (s) => String(s.id) === String(currentSectionId),
      );

      let nextStudentId = currentStudentId;
      let nextSectionId = currentSectionId;

      if (e.key === "ArrowDown" || e.key === "Enter") {
        if (studentIndex < students.length - 1)
          nextStudentId = students[studentIndex + 1].student_id;
      } else if (e.key === "ArrowUp") {
        if (studentIndex > 0)
          nextStudentId = students[studentIndex - 1].student_id;
      } else if (e.key === "ArrowRight") {
        if (sectionIndex < sections.length - 1)
          nextSectionId = sections[sectionIndex + 1].id;
      } else if (e.key === "ArrowLeft") {
        if (sectionIndex > 0) nextSectionId = sections[sectionIndex - 1].id;
      }

      const ref = getMarkInputRef(nextSectionId, nextStudentId);
      if (ref && ref.current) {
        ref.current.focus();
        ref.current.select();
      }
    },
    [students, sections, getMarkInputRef],
  );

  // --- LOGIC: Bulk Save ---
  const handleBulkSave = async () => {
    const hasMarksToSave = Object.values(marksBeingEdited).some(
      (value) => value !== "" && value !== null && value !== undefined,
    );
    if (hasMarksToSave && !confirmTermForMarkEntry()) return;

    setSaveComplete(false);
    if (saveCompleteTimerRef.current) {
      window.clearTimeout(saveCompleteTimerRef.current);
    }
    setSaveAllDisabled(true);
    try {
      // Separate marks into updates and deletes for better handling
      const marksToCreate = [];
      const marksToUpdate = [];
      const marksToDelete = [];

      // Only process marks that were actually edited (in marksBeingEdited)
      Object.entries(marksBeingEdited).forEach(([key, value]) => {
        const [studentId, sectionId] = key.split("-");
        const section = sections.find(
          (s) => String(s.id) === String(sectionId),
        );
        const existingMark = sectionMarks.find(
          (m) =>
            parseInt(m.student_id) === parseInt(studentId) &&
            parseInt(m.section_id) === parseInt(sectionId),
        );

        // Check if mark is empty/null (should be deleted)
        if (value === "" || value === null || value === undefined) {
          if (existingMark && existingMark.id) {
            // Has ID - it exists in DB, needs deletion
            marksToDelete.push(existingMark.id);
          }
          // Else: never saved, skip it
        } else {
          // Has a value - create or update
          const markData = {
            student_id: parseInt(studentId),
            section_id: parseInt(sectionId),
            subject_id: id,
            term: currentTerm,
            mark: value,
            max_mark: section ? section.max_mark : 100,
          };

          if (existingMark && existingMark.id) {
            markData.id = existingMark.id;
            marksToUpdate.push(markData);
          } else {
            marksToCreate.push(markData);
          }
        }
      });

      // Handle deletions
      if (marksToDelete.length > 0) {
        for (const markId of marksToDelete) {
          try {
            await axios.delete(
              `${process.env.REACT_APP_API_URL}/section_marks.php?id=${markId}`,
            );
          } catch (e) {
            console.error(`Failed to delete mark ${markId}:`, e);
          }
        }
        // Remove deleted marks from state
        setSectionMarks((prev) =>
          prev.filter((m) => !marksToDelete.includes(m.id)),
        );
      }

      // Handle creates and updates
      if (marksToCreate.length > 0 || marksToUpdate.length > 0) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/save_bulk_marks.php`,
          {
            create: marksToCreate,
            update: marksToUpdate,
          },
        );

        if (response.data && response.data.success) {
          // Update marks with returned IDs for newly created marks
          if (
            response.data.created_ids &&
            Object.keys(response.data.created_ids).length > 0
          ) {
            setSectionMarks((prev) =>
              prev.map((m) => {
                const key = `${m.student_id}-${m.section_id}`;
                const newId = response.data.created_ids[key];
                return newId && !m.id ? { ...m, id: newId } : m;
              }),
            );
          }
        } else {
          throw new Error(response.data?.error || "Unknown error");
        }
      }

      setSaveComplete(true);
      saveCompleteTimerRef.current = window.setTimeout(
        () => setSaveComplete(false),
        2200,
      );
    } catch (error) {
      alert("Save error: " + (error.response?.data?.error || error.message));
    } finally {
      setSaveAllDisabled(false);
    }
  };

  // --- LOGIC: Excel Upload ---
  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!confirmTermForMarkEntry()) {
      event.target.value = "";
      return;
    }

    setCsvUploading(true);
    setCsvProgress("Reading file...");

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          alert("Excel file is empty or missing data rows");
          setCsvUploading(false);
          return;
        }

        const groupedTemplate = isGroupedExcelTemplate(jsonData);
        const sectionColumns = buildImportedSectionColumns(
          jsonData,
          groupedTemplate,
        );
        const dataStartRowIndex = groupedTemplate ? 2 : 1;

        if (sectionColumns.length === 0) {
          alert(
            "Excel must have at least one section column after First Name and Last Name",
          );
          setCsvUploading(false);
          return;
        }

        let workingSections = [...sections];
        let sectionLookup = buildSectionImportLookup(
          workingSections,
          sectionHeaderMeta,
        );
        const mappedSectionColumns = [];
        let sectionsCreated = 0;
        let skippedColumns = 0;

        setCsvProgress("Analyzing sections...");

        for (const column of sectionColumns) {
          let existingSection = null;

          if (column.groupLabel) {
            existingSection = shiftNextSectionMatch(
              sectionLookup.sectionsByCompositeKey,
              column.compositeKey,
            );
          }

          if (!existingSection) {
            existingSection = shiftNextSectionMatch(
              sectionLookup.sectionsByLabel,
              column.normalizedSectionLabel,
            );
          }

          if (existingSection) {
            mappedSectionColumns.push({
              sectionId: existingSection.id,
              excelColIndex: column.excelColIndex,
            });
          } else {
            if (isFoundationSubject) {
              skippedColumns += 1;
              continue;
            }

            try {
              const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/task_sections.php`,
                {
                  subject_id: id,
                  term: currentTerm,
                  section_name:
                    column.sectionLabel || column.groupLabel || "Assessment",
                  weight: 1,
                  max_mark: 100,
                  category: column.groupLabel,
                  assessment_group: column.groupLabel,
                },
              );

              if (res.data.success && res.data.section_id) {
                const newSection = {
                  id: res.data.section_id,
                  section_name:
                    column.sectionLabel || column.groupLabel || "Assessment",
                  weight: 1,
                  max_mark: 100,
                  category: column.groupLabel,
                  assessment_group: column.groupLabel,
                };
                workingSections.push(newSection);
                mappedSectionColumns.push({
                  sectionId: newSection.id,
                  excelColIndex: column.excelColIndex,
                });
                sectionsCreated++;
              }
            } catch (err) {}
          }
        }

        if (mappedSectionColumns.length === 0) {
          alert("Could not match sections.");
          setCsvUploading(false);
          return;
        }

        const totalRows = Math.max(jsonData.length - dataStartRowIndex, 0);

        setCsvProgress(`Processing ${totalRows} students...`);

        const batchSize = 10;
        let successCount = 0;
        let studentsCreated = 0;
        let rejectedMarks = 0;
        const workingStudents = [...students];
        const workingSectionMarks = [...sectionMarks];
        const processStudentRow = async (row) => {
          const firstName = String(row[0] || "").trim();
          const lastName = String(row[1] || "").trim();
          if (!firstName || !lastName) {
            return;
          }

          try {
            let student = workingStudents.find(
              (s) =>
                s.first_name.toLowerCase() === firstName.toLowerCase() &&
                s.last_name.toLowerCase() === lastName.toLowerCase(),
            );

            if (!student) {
              try {
                const addRes = await axios.post(
                  `${process.env.REACT_APP_API_URL}/add_student_with_marks.php`,
                  {
                    subject_id: id,
                    first_name: firstName,
                    last_name: lastName,
                  },
                );
                if (addRes.data.success) {
                  student = {
                    student_id: addRes.data.student_id,
                    first_name: firstName,
                    last_name: lastName,
                  };
                  workingStudents.push(student);
                  studentsCreated++;
                }
              } catch (e) {
                return;
              }
            }

            if (student) {
              for (const { sectionId, excelColIndex } of mappedSectionColumns) {
                const rawMark = row[excelColIndex];
                const mark = parseFloat(rawMark);
                if (!isNaN(mark)) {
                  const markObj = workingSectionMarks.find(
                    (m) =>
                      parseInt(m.student_id) === parseInt(student.student_id) &&
                      parseInt(m.section_id) === parseInt(sectionId),
                  );
                  try {
                    if (markObj && markObj.id) {
                      const updateRes = await axios.put(
                        `${process.env.REACT_APP_API_URL}/section_marks.php`,
                        { id: markObj.id, mark: mark },
                      );
                      if (updateRes.data?.error)
                        throw new Error(updateRes.data.error);
                      markObj.mark = mark;
                    } else {
                      const saveRes = await axios.post(
                        `${process.env.REACT_APP_API_URL}/section_marks.php`,
                        {
                          student_id: student.student_id,
                          section_id: parseInt(sectionId, 10),
                          mark,
                        },
                      );
                      if (saveRes.data?.error)
                        throw new Error(saveRes.data.error);
                      if (saveRes.data?.success && saveRes.data?.id) {
                        workingSectionMarks.push({
                          id: saveRes.data.id,
                          student_id: student.student_id,
                          section_id: parseInt(sectionId, 10),
                          mark,
                        });
                      }
                    }
                    successCount++;
                  } catch (err) {
                    rejectedMarks++;
                  }
                }
              }
            }
          } catch (rowError) {}
        };

        for (
          let batchStart = dataStartRowIndex;
          batchStart < jsonData.length;
          batchStart += batchSize
        ) {
          const batchEnd = Math.min(batchStart + batchSize, jsonData.length);
          const batchPromises = [];

          for (let i = batchStart; i < batchEnd; i++) {
            const row = jsonData[i];
            if (!row || row.length < 2) continue;
            batchPromises.push(processStudentRow(row));
          }
          await Promise.all(batchPromises);
          const processed = Math.min(batchEnd - 1, totalRows);
          setCsvProgress(`Processing... ${processed}/${totalRows}`);
          await new Promise((resolve) => setTimeout(resolve, 100)); // Rate limit
        }

        setCsvUploading(false);
        setCsvProgress("");

        // Refresh the full subject view so students, totals, sections, and marks all stay in sync.
        try {
          await loadSubjectData({ showLoader: false });
        } catch (refreshErr) {
          console.error("Error refreshing data:", refreshErr);
        }

        setImportResult({
          term: currentTerm,
          sectionsCreated,
          studentsCreated,
          successCount,
          rejectedMarks,
          skippedColumns,
        });
      } catch (error) {
        setCsvUploading(false);
        alert("Error processing Excel: " + error.message);
      }
    };
  };

  const foundationSubjectDefinition = useMemo(
    () =>
      getFoundationSubjectDefinition(
        subjectname,
        subjectGrade,
        subjectGradingMode,
        foundationTemplateCode,
        currentTerm,
        foundationTemplateDefinition,
      ),
    [
      subjectname,
      subjectGrade,
      subjectGradingMode,
      foundationTemplateCode,
      currentTerm,
      foundationTemplateDefinition,
    ],
  );
  const isFoundationSubject = !!foundationSubjectDefinition;
  const foundationWeightsByLabel = useMemo(
    () =>
      (foundationSubjectDefinition?.components || []).reduce(
        (acc, component) => {
          acc[String(component.label || "").toLowerCase()] =
            component.weight ?? null;
          return acc;
        },
        {},
      ),
    [foundationSubjectDefinition],
  );
  const sectionHeaderMeta = useMemo(() => {
    const palette = ["#f4fbf5", "#f8f6eb", "#eef7fb", "#f7f1fb", "#fbf4ef"];
    const groupSizes = (sections || []).reduce((acc, section) => {
      const groupKey = getAssessmentGroupKey(section);
      acc[groupKey] = (acc[groupKey] || 0) + 1;
      return acc;
    }, {});
    let lastGroupKey = "";
    let groupIndex = -1;

    return sections.reduce((acc, section) => {
      const groupLabel = normalizeGroupText(getAssessmentGroupLabel(section));
      const groupKey = getAssessmentGroupKey(section);
      const sectionLabel = normalizeGroupText(
        section.section_name || "Assessment",
      );
      const showGroupLabel =
        isFoundationSubject ||
        groupSizes[groupKey] > 1 ||
        groupLabel.toLowerCase() !== sectionLabel.toLowerCase();
      if (groupKey !== lastGroupKey) {
        groupIndex += 1;
        lastGroupKey = groupKey;
      }

      acc[section.id] = {
        groupLabel,
        groupKey,
        groupIndex,
        tint: showGroupLabel ? palette[groupIndex % palette.length] : undefined,
        showGroupLabel,
      };
      return acc;
    }, {});
  }, [isFoundationSubject, sections]);
  const hasGroupedColumnHeaders = useMemo(
    () => Object.values(sectionHeaderMeta).some((meta) => meta.showGroupLabel),
    [sectionHeaderMeta],
  );
  const studentCalculationMarkLookup = useMemo(
    () => buildSectionMarkLookup(sectionMarks),
    [sectionMarks],
  );

  // --- Computed Data (Optimized - Only recalculates when dependencies change) ---
  const studentsWithComputed = useMemo(() => {
    return students.map((student) => {
      const studentId = parseInt(student.student_id, 10);
      let rawSectionMarkSum = 0;
      let rawSectionMaxSum = 0;

      sections.forEach((section) => {
        const markObj = studentCalculationMarkLookup.get(
          `${studentId}-${section.id}`,
        );
        const mark =
          markObj && markObj.mark !== "" && markObj.mark != null
            ? parseFloat(markObj.mark)
            : null;
        const max = parseFloat(section.max_mark) || 100;

        if (mark !== null && !Number.isNaN(mark) && max > 0) {
          rawSectionMarkSum += mark;
          rawSectionMaxSum += max;
        }
      });

      const result = calculateSubjectResult({
        subjectName: subjectname,
        subjectGrade,
        gradingMode: subjectGradingMode,
        foundationTemplateCode,
        foundationDefinition: foundationSubjectDefinition,
        term: currentTerm,
        sections,
        markLookup: studentCalculationMarkLookup,
        studentId,
      });

      const average = result.percentage;
      const level = average !== null ? calculateLevel(average) : null;

      return {
        ...student,
        average,
        level,
        rawMarkSum: rawSectionMarkSum,
        rawMaxSum: rawSectionMaxSum,
        finalMarkSum: result.totalAchieved,
        finalMaxSum: result.totalPossible,
        isFoundation: result.isFoundation,
      };
    });
  }, [
    students,
    subjectname,
    subjectGrade,
    subjectGradingMode,
    foundationTemplateCode,
    foundationSubjectDefinition,
    currentTerm,
    sections,
    studentCalculationMarkLookup,
    calculateLevel,
  ]);

  const top10Students = useMemo(() => {
    return [...studentsWithComputed]
      .filter((s) => s.average !== null)
      .sort((a, b) => (b.average ?? -1) - (a.average ?? -1))
      .slice(0, 10);
  }, [studentsWithComputed]);
  const top10Ids = useMemo(
    () => top10Students.map((s) => s.student_id),
    [top10Students],
  );

  const levelDistribution = useMemo(() => {
    const counts = Array.from({ length: 7 }, (_, index) => ({
      level: index + 1,
      count: 0,
    }));
    studentsWithComputed.forEach((student) => {
      const level = Number(student.level);
      if (Number.isInteger(level) && level >= 1 && level <= 7) {
        counts[level - 1].count += 1;
      }
    });
    return counts;
  }, [studentsWithComputed]);
  const learnersWithLevels = useMemo(
    () => levelDistribution.reduce((total, item) => total + item.count, 0),
    [levelDistribution],
  );
  const highestLevelCount = useMemo(
    () => Math.max(1, ...levelDistribution.map((item) => item.count)),
    [levelDistribution],
  );

  const exportLearnerLevels = useCallback(() => {
    const rows = levelDistribution.map((item) => ({
      Level: item.level,
      Learners: item.count,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `Term ${currentTerm} levels`,
    );
    XLSX.writeFile(
      workbook,
      `${String(subjectname || "subject").replace(/[^a-z0-9]+/gi, "_")}_term_${currentTerm}_learner_levels.xlsx`,
    );
  }, [levelDistribution, currentTerm, subjectname]);

  // --- Bottom 10 (At Risk) Learners ---
  const [showBottom10, setShowBottom10] = useState(false);
  // Learners at risk: 55% and below
  const atRiskLearners = useMemo(() => {
    return [...studentsWithComputed]
      .filter(
        (s) =>
          s.average !== null &&
          s.average <= activePassRequirements.support_threshold,
      )
      .sort((a, b) => (a.average ?? 101) - (b.average ?? 101));
  }, [studentsWithComputed, activePassRequirements.support_threshold]);

  const openRevisionPlan = () => {
    localStorage.setItem(
      "marksure_ai_revision_context",
      JSON.stringify({ subjectId: id, term: currentTerm }),
    );
    navigate("/ai-tools");
  };
  const assessmentGroups = useMemo(
    () => buildAssessmentGroups(sections),
    [sections],
  );
  const foundationAssessmentGroupOptions = useMemo(
    () =>
      (foundationSubjectDefinition?.components || [])
        .map((component) => normalizeGroupText(component?.label))
        .filter(Boolean),
    [foundationSubjectDefinition],
  );
  const matchingAssessmentGroup = useMemo(() => {
    const normalizedName = normalizeGroupText(
      newSection.assessment_group,
    ).toLowerCase();
    if (!normalizedName) return null;
    return (
      assessmentGroups.find(
        (group) =>
          normalizeGroupText(group.primaryGroupName).toLowerCase() ===
          normalizedName,
      ) || null
    );
  }, [assessmentGroups, newSection.assessment_group]);
  const recordedMarksCount = useMemo(() => {
    return sectionMarks.filter((mark) => mark.mark !== "" && mark.mark != null)
      .length;
  }, [sectionMarks]);

  // --- Marks Map for O(1) lookup during render ---
  const markedRunByCell = useMemo(() => {
    const cells = new Map();
    markedRuns.forEach((run) => {
      run.section_ids.forEach((sectionId) => {
        cells.set(`${run.student_id}-${sectionId}`, run);
      });
    });
    return cells;
  }, [markedRuns]);

  const marksMap = useMemo(() => {
    const map = new Map();
    for (const mark of sectionMarks) {
      map.set(`${mark.student_id}-${mark.section_id}`, mark);
    }
    return map;
  }, [sectionMarks]);

  useEffect(() => {
    if (loading) return;

    const key = `${id}-${currentTerm}`;
    const hasMarkForEveryLearnerAndAssessment =
      students.length > 0 &&
      sections.length > 0 &&
      students.every((student) =>
        sections.every((section) => {
          const mark = marksMap.get(
            `${student.student_id}-${section.id}`,
          )?.mark;
          return mark !== "" && mark !== null && mark !== undefined;
        }),
      );

    // Do not celebrate marks that were already complete when this subject was opened.
    if (completionStateRef.current.key !== key) {
      completionStateRef.current = {
        key,
        wasComplete: hasMarkForEveryLearnerAndAssessment,
      };
      return;
    }

    if (
      hasMarkForEveryLearnerAndAssessment &&
      !completionStateRef.current.wasComplete
    ) {
      setShowCompletionCelebration(true);
      completionTimerRef.current = window.setTimeout(
        () => setShowCompletionCelebration(false),
        4200,
      );
    }
    completionStateRef.current.wasComplete =
      hasMarkForEveryLearnerAndAssessment;
  }, [id, currentTerm, loading, students, sections, marksMap]);

  const openVoiceMarking = useCallback(() => {
    setVoiceSectionId(String(sections[0]?.id || ""));
    setVoiceTranscript("");
    setVoiceCandidate(null);
    setVoiceStatus(
      "Choose an assessment, then say the learner’s name and mark. For example: “Thabo Mokoena, seventy two.”",
    );
    setShowVoiceModal(true);
  }, [sections]);

  const saveVoiceMark = useCallback(
    async (candidate) => {
      if (!candidate || !confirmTermForMarkEntry()) return;
      const { student, section, mark } = candidate;
      if (mark < 0 || mark > Number(section.max_mark)) {
        setVoiceStatus(
          `The mark must be between 0 and ${section.max_mark} for ${section.section_name}.`,
        );
        return;
      }

      setSavingVoiceMark(true);
      setVoiceStatus(
        `Saving ${student.first_name} ${student.last_name}'s mark…`,
      );
      try {
        const existingMark = marksMap.get(
          `${student.student_id}-${section.id}`,
        );
        let savedId = existingMark?.id;
        if (savedId) {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/section_marks.php`,
            { id: savedId, mark },
          );
        } else {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/section_marks.php`,
            { student_id: student.student_id, section_id: section.id, mark },
          );
          savedId = response.data?.id;
        }
        setSectionMarks((previous) => {
          const index = previous.findIndex(
            (item) =>
              String(item.student_id) === String(student.student_id) &&
              String(item.section_id) === String(section.id),
          );
          const nextMark = {
            ...(index >= 0 ? previous[index] : {}),
            id: savedId,
            student_id: student.student_id,
            section_id: section.id,
            mark: String(mark),
          };
          return index >= 0
            ? previous.map((item, itemIndex) =>
                itemIndex === index ? nextMark : item,
              )
            : [...previous, nextMark];
        });
        setVoiceCandidate(null);
        setVoiceStatus(
          `Saved: ${student.first_name} ${student.last_name} — ${mark} / ${section.max_mark}. Ready for the next learner.`,
        );
      } catch (error) {
        setVoiceStatus(
          error.response?.data?.error ||
            "The mark could not be saved. Please try again.",
        );
      } finally {
        setSavingVoiceMark(false);
      }
    },
    [marksMap, confirmTermForMarkEntry],
  );

  const startVoiceListening = useCallback(() => {
    if (voiceSessionActiveRef.current) return;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceStatus(
        "Voice entry is not supported by this browser. Please use Chrome or Edge over a secure HTTPS connection.",
      );
      return;
    }
    const section = sections.find(
      (item) => String(item.id) === String(voiceSectionId),
    );
    if (!section) {
      setVoiceStatus("Choose an assessment before starting the microphone.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-ZA";
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      setVoiceListening(true);
      setVoiceStatus("Listening…");
    };
    recognition.onend = () => {
      // Browsers can end a recognition pass after a pause despite
      // continuous mode. Restart it unless the teacher chose Stop.
      if (!voiceSessionActiveRef.current) {
        setVoiceListening(false);
        return;
      }
      voiceRestartTimerRef.current = window.setTimeout(() => {
        if (!voiceSessionActiveRef.current) return;
        try {
          recognition.start();
        } catch (error) {
          voiceSessionActiveRef.current = false;
          setVoiceStatus(
            "Voice listening stopped. Select Start continuous marking to try again.",
          );
        }
      }, 250);
    };
    recognition.onerror = (event) => {
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        voiceSessionActiveRef.current = false;
        setVoiceListening(false);
        setVoiceStatus(
          "Microphone permission was not granted. Allow microphone access, then try again.",
        );
        return;
      }
      if (event.error !== "no-speech" && event.error !== "aborted") {
        setVoiceStatus(
          "I could not hear that clearly. Keep going, or repeat the learner name and mark.",
        );
      }
    };
    recognition.onresult = (event) => {
      // A continuous recognition session accumulates previous results;
      // use the latest result so earlier marks are never saved again.
      const transcript = event.results[event.resultIndex][0].transcript;
      const spokenMark = getSpokenMark(transcript);
      const normalizedTranscript = normalizeVoiceText(transcript);
      const matchingStudents = students.filter((student) => {
        const fullName = normalizeVoiceText(
          `${student.first_name} ${student.last_name}`,
        );
        return fullName && normalizedTranscript.includes(fullName);
      });
      const fallbackMatches = matchingStudents.length
        ? matchingStudents
        : students.filter((student) => {
            const firstName = normalizeVoiceText(student.first_name);
            return firstName && normalizedTranscript.includes(firstName);
          });
      setVoiceTranscript(transcript);
      if (fallbackMatches.length !== 1 || !Number.isFinite(spokenMark)) {
        setVoiceCandidate(null);
        setVoiceStatus(
          "I could not confidently match one learner and a mark. Say the full learner name followed by the mark, then try again.",
        );
        return;
      }
      saveVoiceMark({ student: fallbackMatches[0], section, mark: spokenMark });
    };
    speechRecognitionRef.current = recognition;
    voiceSessionActiveRef.current = true;
    recognition.start();
  }, [sections, students, voiceSectionId, saveVoiceMark]);

  useEffect(() => {
    if (!isFoundationSubject) {
      return;
    }

    const defaultGroup = foundationAssessmentGroupOptions[0] || "";
    if (!defaultGroup) {
      return;
    }

    setNewSection((prev) => {
      const currentGroup = normalizeGroupText(prev.assessment_group);
      const nextGroup = foundationAssessmentGroupOptions.includes(currentGroup)
        ? currentGroup
        : defaultGroup;
      const nextCategory = normalizeGroupText(prev.category) || nextGroup;

      if (currentGroup === nextGroup && prev.category === nextCategory) {
        return prev;
      }

      return {
        ...prev,
        assessment_group: nextGroup,
        category: nextCategory,
      };
    });
  }, [foundationAssessmentGroupOptions, isFoundationSubject]);

  // --- Excel Export ---
  const handleExportExcel = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();

    const nameLower = (subjectname || "").toLowerCase();
    const isHomeLanguage =
      /^G[0-3]_HL$/i.test(String(foundationTemplateCode || "").trim()) ||
      nameLower.includes("home language") ||
      nameLower.includes("huistaal");
    const passMark = isHomeLanguage ? 50 : 40;

    const localMarksMap = new Map();
    for (const mark of sectionMarks) {
      localMarksMap.set(`${mark.student_id}-${mark.section_id}`, mark);
    }

    const activeSections = sections.filter((s) => parseFloat(s.weight) !== 0);
    const totalMaxMark = foundationSubjectDefinition
      ? getFoundationOverallPossible(foundationSubjectDefinition)
      : activeSections.reduce(
          (sum, s) => sum + (parseFloat(s.max_mark) || 0),
          0,
        );

    const thinBorder = {
      top: { style: "thin", color: { argb: "FFD9D9D9" } },
      bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
      left: { style: "thin", color: { argb: "FFD9D9D9" } },
      right: { style: "thin", color: { argb: "FFD9D9D9" } },
    };

    const fillGrey = (argb) => ({
      type: "pattern",
      pattern: "solid",
      fgColor: { argb },
    });

    const styleRange = (sheet, startRow, endRow, startCol, endCol, style) => {
      for (let r = startRow; r <= endRow; r++) {
        for (let c = startCol; c <= endCol; c++) {
          const cell = sheet.getCell(r, c);
          if (style.font) cell.font = { ...(cell.font || {}), ...style.font };
          if (style.fill) cell.fill = style.fill;
          if (style.border) cell.border = style.border;
          if (style.alignment)
            cell.alignment = { ...(cell.alignment || {}), ...style.alignment };
        }
      }
    };

    // ==================== SHEET 1: MARKS ====================
    const ws1 = workbook.addWorksheet(`Term ${currentTerm} Marks`);

    const categoryDefs = [];

    const categoryMap = {};
    activeSections.forEach((s) => {
      const categoryName = (s.category || "").trim() || "Uncategorized";
      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = {
          name: categoryName,
          totalMax: 0,
          sectionIds: [],
        };
        categoryDefs.push(categoryMap[categoryName]);
      }
      categoryMap[categoryName].totalMax += parseFloat(s.max_mark) || 0;
      categoryMap[categoryName].sectionIds.push(s.id);
    });

    // Compute dimensions upfront (needed for info rows)

    const categoryStartCol = 4 + sections.length * 3;
    const overallStartCol = categoryStartCol + categoryDefs.length * 3;
    const ws1TotalCols = overallStartCol + 2;
    const midCol = Math.ceil(ws1TotalCols / 2);

    // Info row 1: Subject | Term
    const infoRow1 = Array(ws1TotalCols).fill("");
    infoRow1[0] = "Subject:";
    infoRow1[1] = subjectname;
    infoRow1[midCol - 1] = "Term:";
    infoRow1[midCol] = String(currentTerm);
    ws1.addRow(infoRow1); // row 1

    // Info row 2: Educator | Grade | Class
    const infoRow2 = Array(ws1TotalCols).fill("");
    infoRow2[0] = "Educator:";
    infoRow2[midCol - 1] = "Grade:";
    infoRow2[midCol] = subjectGrade;
    infoRow2[midCol + 1] = "Class:";
    infoRow2[midCol + 2] = subjectClass;
    ws1.addRow(infoRow2); // row 2

    const headerRow0 = ["No", "Surname", "Name"];
    sections.forEach((s) => {
      headerRow0.push(`${s.section_name} (${s.max_mark})`);
      headerRow0.push("");
      headerRow0.push("");
    });
    categoryDefs.forEach((cat) => {
      headerRow0.push(`TOTAL ${cat.name.toUpperCase()} (${cat.totalMax})`);
      headerRow0.push("");
      headerRow0.push("");
    });
    headerRow0.push(`OVERALL TOTAL (${totalMaxMark})`);
    headerRow0.push("");
    headerRow0.push("");

    const headerRow1 = ["", "", ""];
    sections.forEach((s) => {
      headerRow1.push(s.max_mark);
      headerRow1.push("%");
      headerRow1.push("Lvl");
    });
    categoryDefs.forEach((cat) => {
      headerRow1.push(cat.totalMax);
      headerRow1.push("%");
      headerRow1.push("Lvl");
    });
    headerRow1.push(totalMaxMark);
    headerRow1.push("%");
    headerRow1.push("Lvl");

    ws1.addRow(headerRow0); // row 3
    ws1.addRow(headerRow1); // row 4

    const secStats = {};
    activeSections.forEach((s) => {
      secStats[s.id] = {
        marks: [],
        levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
        passedCount: 0,
        failedCount: 0,
      };
    });

    const catStats = {};
    categoryDefs.forEach((cat) => {
      catStats[cat.name] = {
        percentages: [],
        levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
        passedCount: 0,
        failedCount: 0,
      };
    });

    const overallRawData = [];
    let overallPassedCount = 0;
    let overallFailedCount = 0;
    const overallLevelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };

    studentsWithComputed.forEach((student, idx) => {
      const row = [idx + 1, student.last_name, student.first_name];
      const studentId = parseInt(student.student_id);
      const studentCatTotals = {};
      categoryDefs.forEach((cat) => {
        studentCatTotals[cat.name] = { markSum: 0, maxSum: 0, hasMark: false };
      });

      sections.forEach((section) => {
        const markObj = localMarksMap.get(`${studentId}-${section.id}`);
        const markVal =
          markObj && markObj.mark !== "" && markObj.mark != null
            ? parseFloat(markObj.mark)
            : null;
        const max = parseFloat(section.max_mark) || 100;

        row.push(markVal !== null ? markVal : "");

        if (markVal !== null && max > 0) {
          const p = (markVal / max) * 100;
          row.push(Math.round(p));
          const level = calculateLevel(p);
          row.push(formatGrade(level));

          if (parseFloat(section.weight) !== 0 && secStats[section.id]) {
            secStats[section.id].marks.push(markVal);
            secStats[section.id].levelDist[level]++;
            if (Math.round(p) >= passMark) secStats[section.id].passedCount++;
            else secStats[section.id].failedCount++;

            const categoryName =
              (section.category || "").trim() || "Uncategorized";
            if (categoryName && studentCatTotals[categoryName]) {
              studentCatTotals[categoryName].markSum += markVal;
              studentCatTotals[categoryName].maxSum += max;
              studentCatTotals[categoryName].hasMark = true;
            }
          }
        } else {
          row.push("");
          row.push("");
        }
      });

      categoryDefs.forEach((cat) => {
        const catTotal = studentCatTotals[cat.name];
        if (!catTotal.hasMark || catTotal.maxSum <= 0) {
          row.push("");
          row.push("");
          row.push("");
          return;
        }

        const catPerc = (catTotal.markSum / catTotal.maxSum) * 100;
        const catLevel = calculateLevel(catPerc);

        row.push(Number(catTotal.markSum).toFixed(1));
        row.push(Math.round(catPerc));
        row.push(formatGrade(catLevel));

        if (catStats[cat.name]) {
          catStats[cat.name].percentages.push(catPerc);
          catStats[cat.name].levelDist[catLevel]++;
          if (Math.round(catPerc) >= passMark) catStats[cat.name].passedCount++;
          else catStats[cat.name].failedCount++;
        }
      });

      const rawTotal = student.rawMarkSum;
      const rawMax = student.rawMaxSum;
      const calculatedTotal = isFoundationSubject
        ? student.finalMarkSum
        : rawTotal;
      const calculatedMax = isFoundationSubject ? student.finalMaxSum : rawMax;
      const overallPerc = student.average;

      row.push(calculatedMax > 0 ? Number(calculatedTotal).toFixed(1) : "");
      row.push(overallPerc !== null ? Math.round(overallPerc) : "");
      const overallLevel =
        overallPerc !== null ? calculateLevel(overallPerc) : null;
      row.push(overallLevel ? formatGrade(overallLevel) : "");

      if (overallPerc !== null) {
        overallRawData.push({
          markSum: calculatedTotal,
          maxSum: calculatedMax,
          perc: overallPerc,
        });
        overallLevelDist[overallLevel]++;
        if (Math.round(overallPerc) >= passMark) overallPassedCount++;
        else overallFailedCount++;
      }

      ws1.addRow(row);
    });

    // Info row merges
    if (midCol > 2) ws1.mergeCells(1, 2, 1, midCol - 1);
    if (midCol + 1 < ws1TotalCols)
      ws1.mergeCells(1, midCol + 1, 1, ws1TotalCols);
    if (midCol > 2) ws1.mergeCells(2, 2, 2, midCol - 1);
    if (midCol + 3 <= ws1TotalCols)
      ws1.mergeCells(2, midCol + 3, 2, ws1TotalCols);

    // Column header merges (rows 3-4)
    ws1.mergeCells(3, 1, 4, 1);
    ws1.mergeCells(3, 2, 4, 2);
    ws1.mergeCells(3, 3, 4, 3);
    sections.forEach((s, i) => {
      const sc = 4 + i * 3;
      ws1.mergeCells(3, sc, 3, sc + 2);
    });
    categoryDefs.forEach((cat, i) => {
      const cc = categoryStartCol + i * 3;
      ws1.mergeCells(3, cc, 3, cc + 2);
    });
    ws1.mergeCells(3, overallStartCol, 3, overallStartCol + 2);

    ws1.columns = [
      { width: 5 },
      { width: 18 },
      { width: 16 },
      ...sections.flatMap(() => [{ width: 10 }, { width: 6 }, { width: 6 }]),
      ...categoryDefs.flatMap(() => [
        { width: 12 },
        { width: 6 },
        { width: 6 },
      ]),
      { width: 12 },
      { width: 6 },
      { width: 6 },
    ];

    const ws1LastRow = ws1.rowCount;

    // Style info rows (1-2)
    styleRange(ws1, 1, 2, 1, ws1TotalCols, {
      fill: fillGrey("FFFAFAFA"),
      border: thinBorder,
      alignment: { horizontal: "left", vertical: "middle" },
    });
    styleRange(ws1, 1, 2, 1, 1, { font: { bold: true } });
    styleRange(ws1, 1, 1, midCol, midCol, { font: { bold: true } });
    styleRange(ws1, 2, 2, midCol, midCol, { font: { bold: true } });
    styleRange(ws1, 2, 2, midCol + 2, midCol + 2, { font: { bold: true } });
    ws1.getRow(1).height = 22;
    ws1.getRow(2).height = 22;

    // Style column header rows (3-4)
    styleRange(ws1, 3, 3, 1, ws1TotalCols, {
      font: { bold: true },
      fill: fillGrey("FFD9D9D9"),
      border: thinBorder,
      alignment: { horizontal: "center", vertical: "middle" },
    });

    styleRange(ws1, 4, 4, 1, ws1TotalCols, {
      font: { bold: true },
      fill: fillGrey("FFEFEFEF"),
      border: thinBorder,
      alignment: { horizontal: "center", vertical: "middle" },
    });

    if (ws1LastRow >= 5) {
      styleRange(ws1, 5, ws1LastRow, 1, ws1TotalCols, {
        border: thinBorder,
        alignment: { horizontal: "center", vertical: "middle" },
      });
      styleRange(ws1, 5, ws1LastRow, 2, 3, {
        border: thinBorder,
        alignment: { horizontal: "left", vertical: "middle" },
      });
      styleRange(ws1, 3, ws1LastRow, overallStartCol, ws1TotalCols, {
        fill: fillGrey("FFF5F5F5"),
        border: thinBorder,
        alignment: { horizontal: "center", vertical: "middle" },
      });
    }

    // ==================== SHEET 2: COMBINED STATS ====================
    const ws2 = workbook.addWorksheet(`Term ${currentTerm} Statistics`);

    const analysisLabel = activeSections.map((s) => s.section_name).join(", ");
    ws2.addRow(["STATISTIC AND DIAGNOSTIC ANALYSIS OF MARKS"]);
    ws2.addRow([]);
    ws2.addRow(["SUBJECT:", subjectname, "", "", "MARK:", totalMaxMark]);
    ws2.addRow(["EXAMINER:", "", "", "", "GRADE:", subjectGrade]);
    ws2.addRow(["DATE:", "", "", "", "TERM:", currentTerm]);
    ws2.addRow(["ANALYSIS:", analysisLabel]);
    ws2.addRow([]);

    ws2.addRow([
      "Section",
      "Total in class",
      "Total written",
      "Class average",
      "Highest mark",
      "Lowest mark",
      "Total failed",
      "% Failures",
      "Total that passed",
      "% Passed",
      "0 - 29",
      "30 - 39",
      "40 - 49",
      "50 - 59",
      "60 - 69",
      "70 - 79",
      "80 - 100",
      "Total (1-7 = Total/Total written)",
    ]);

    const sectionsByCategory = {};
    const categoryOrder = [];
    activeSections.forEach((section) => {
      const category = (section.category || "").trim() || "Uncategorized";
      if (!sectionsByCategory[category]) {
        sectionsByCategory[category] = [];
        categoryOrder.push(category);
      }
      sectionsByCategory[category].push(section);
    });

    const categoryTotalRows = [];

    categoryOrder.forEach((category) => {
      const groupSections = sectionsByCategory[category];

      groupSections.forEach((section) => {
        const stats = secStats[section.id];
        const max = parseFloat(section.max_mark) || 100;
        const tw = stats.marks.length;
        const avgPerc =
          tw > 0
            ? stats.marks.reduce((a, b) => a + (b / max) * 100, 0) / tw
            : null;
        const highest = tw > 0 ? Math.max(...stats.marks) : "";
        const lowest = tw > 0 ? Math.min(...stats.marks) : "";
        const total17 = Object.values(stats.levelDist).reduce(
          (a, b) => a + b,
          0,
        );
        const failP =
          tw > 0 ? Math.round((stats.failedCount / tw) * 100) + "%" : "0%";
        const passP =
          tw > 0 ? Math.round((stats.passedCount / tw) * 100) + "%" : "0%";

        ws2.addRow([
          `${section.section_name} (${max})`,
          students.length,
          tw,
          avgPerc !== null ? avgPerc.toFixed(1) + "%" : "",
          highest,
          lowest,
          stats.failedCount,
          failP,
          stats.passedCount,
          passP,
          stats.levelDist[1],
          stats.levelDist[2],
          stats.levelDist[3],
          stats.levelDist[4],
          stats.levelDist[5],
          stats.levelDist[6],
          stats.levelDist[7],
          tw > 0 ? `${total17}/${tw}` : "",
        ]);
      });

      const mappedName =
        category === "Uncategorized" ? "Uncategorized" : category;
      const currentCatStats = catStats[mappedName] || {
        percentages: [],
        levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
        passedCount: 0,
        failedCount: 0,
      };
      const categoryTotalWritten = currentCatStats.percentages.length;
      const categoryAvgPerc =
        categoryTotalWritten > 0
          ? currentCatStats.percentages.reduce((a, b) => a + b, 0) /
            categoryTotalWritten
          : null;
      const categoryHighestPerc =
        categoryTotalWritten > 0
          ? Math.round(Math.max(...currentCatStats.percentages)) + "%"
          : "";
      const categoryLowestPerc =
        categoryTotalWritten > 0
          ? Math.round(Math.min(...currentCatStats.percentages)) + "%"
          : "";
      const categoryTotal17 = Object.values(currentCatStats.levelDist).reduce(
        (a, b) => a + b,
        0,
      );
      const categoryFailP =
        categoryTotalWritten > 0
          ? Math.round(
              (currentCatStats.failedCount / categoryTotalWritten) * 100,
            ) + "%"
          : "0%";
      const categoryPassP =
        categoryTotalWritten > 0
          ? Math.round(
              (currentCatStats.passedCount / categoryTotalWritten) * 100,
            ) + "%"
          : "0%";

      ws2.addRow([
        `TOTAL ${category.toUpperCase()}`,
        students.length,
        categoryTotalWritten,
        categoryAvgPerc !== null ? categoryAvgPerc.toFixed(1) + "%" : "",
        categoryHighestPerc,
        categoryLowestPerc,
        currentCatStats.failedCount,
        categoryFailP,
        currentCatStats.passedCount,
        categoryPassP,
        currentCatStats.levelDist[1],
        currentCatStats.levelDist[2],
        currentCatStats.levelDist[3],
        currentCatStats.levelDist[4],
        currentCatStats.levelDist[5],
        currentCatStats.levelDist[6],
        currentCatStats.levelDist[7],
        categoryTotalWritten > 0
          ? `${categoryTotal17}/${categoryTotalWritten}`
          : "",
      ]);
      categoryTotalRows.push(ws2.rowCount);
    });

    const totalWrittenOverall = overallRawData.length;
    const overallClassAvgPerc =
      totalWrittenOverall > 0
        ? overallRawData.reduce((a, b) => a + b.perc, 0) / totalWrittenOverall
        : null;
    const allRawSums = overallRawData.map((r) => r.markSum);
    const highestOverall = allRawSums.length > 0 ? Math.max(...allRawSums) : "";
    const lowestOverall = allRawSums.length > 0 ? Math.min(...allRawSums) : "";
    const overallTotal17 = Object.values(overallLevelDist).reduce(
      (a, b) => a + b,
      0,
    );
    const overallFailP =
      totalWrittenOverall > 0
        ? Math.round((overallFailedCount / totalWrittenOverall) * 100) + "%"
        : "0%";
    const overallPassP =
      totalWrittenOverall > 0
        ? Math.round((overallPassedCount / totalWrittenOverall) * 100) + "%"
        : "0%";

    ws2.addRow([
      `OVERALL TOTAL (${totalMaxMark})`,
      students.length,
      totalWrittenOverall,
      overallClassAvgPerc !== null ? overallClassAvgPerc.toFixed(1) + "%" : "",
      highestOverall,
      lowestOverall,
      overallFailedCount,
      overallFailP,
      overallPassedCount,
      overallPassP,
      overallLevelDist[1],
      overallLevelDist[2],
      overallLevelDist[3],
      overallLevelDist[4],
      overallLevelDist[5],
      overallLevelDist[6],
      overallLevelDist[7],
      totalWrittenOverall > 0 ? `${overallTotal17}/${totalWrittenOverall}` : "",
    ]);
    const overallTotalRow = ws2.rowCount;

    ws2.addRow([]);
    ws2.addRow([
      "DIAGNOSTIC ANALYSIS: (High frequency errors that were made in questions)",
    ]);
    ws2.addRow([]);
    ws2.addRow([
      "Problem area",
      "",
      "",
      "",
      "Intervention",
      "",
      "",
      "",
      "",
      "",
      "Time",
      "",
    ]);
    for (let i = 0; i < 4; i++) {
      ws2.addRow(["", "", "", "", "", "", "", "", "", "", "", ""]);
    }

    ws2.addRow([]);
    ws2.addRow([
      "Signature of Teacher:",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Date:",
      "",
      "",
      "",
      "",
    ]);
    ws2.addRow([
      "Signature of Departmental Head:",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Date:",
      "",
      "",
      "",
      "",
    ]);

    ws2.mergeCells(1, 1, 1, 18);
    ws2.mergeCells(6, 2, 6, 18);
    ws2.columns = [
      { width: 24 },
      { width: 12 },
      { width: 12 },
      { width: 13 },
      { width: 12 },
      { width: 12 },
      { width: 11 },
      { width: 11 },
      { width: 13 },
      { width: 10 },
      { width: 8 },
      { width: 8 },
      { width: 8 },
      { width: 8 },
      { width: 8 },
      { width: 8 },
      { width: 8 },
      { width: 18 },
    ];

    const tableHeaderRow = 8;
    const tableFirstDataRow = 9;
    const tableLastDataRow = overallTotalRow;
    const diagTitleRow = tableLastDataRow + 2;
    const diagHeaderRow = tableLastDataRow + 4;
    const diagLastRow = diagHeaderRow + 4;
    const sigRow1 = diagLastRow + 2;
    const sigRow2 = diagLastRow + 3;

    // Diagnostic section merges
    ws2.mergeCells(diagTitleRow, 1, diagTitleRow, 18);
    ws2.mergeCells(diagHeaderRow, 1, diagHeaderRow, 4);
    ws2.mergeCells(diagHeaderRow, 5, diagHeaderRow, 10);
    ws2.mergeCells(diagHeaderRow, 11, diagHeaderRow, 12);
    for (let di = 1; di <= 4; di++) {
      ws2.mergeCells(diagHeaderRow + di, 1, diagHeaderRow + di, 4);
      ws2.mergeCells(diagHeaderRow + di, 5, diagHeaderRow + di, 10);
      ws2.mergeCells(diagHeaderRow + di, 11, diagHeaderRow + di, 12);
      ws2.getRow(diagHeaderRow + di).height = 45;
    }

    // Signature row merges
    [sigRow1, sigRow2].forEach((r) => {
      ws2.mergeCells(r, 1, r, 2);
      ws2.mergeCells(r, 3, r, 8);
      ws2.mergeCells(r, 11, r, 14);
    });

    styleRange(ws2, 1, 1, 1, 18, {
      font: { bold: true },
      fill: fillGrey("FFD9D9D9"),
      border: thinBorder,
      alignment: { horizontal: "center", vertical: "middle" },
    });

    styleRange(ws2, 3, 6, 1, 1, {
      font: { bold: true },
      fill: fillGrey("FFEFEFEF"),
      border: thinBorder,
      alignment: { horizontal: "left", vertical: "middle" },
    });
    styleRange(ws2, 3, 6, 2, 18, {
      border: thinBorder,
      alignment: { horizontal: "left", vertical: "middle" },
    });

    styleRange(ws2, tableHeaderRow, tableHeaderRow, 1, 18, {
      font: { bold: true },
      fill: fillGrey("FFE5E5E5"),
      border: thinBorder,
      alignment: { horizontal: "center", vertical: "middle" },
    });

    if (tableLastDataRow >= tableFirstDataRow) {
      styleRange(ws2, tableFirstDataRow, tableLastDataRow, 1, 18, {
        border: thinBorder,
        alignment: { horizontal: "center", vertical: "middle" },
      });
      styleRange(ws2, tableFirstDataRow, tableLastDataRow, 1, 1, {
        border: thinBorder,
        alignment: { horizontal: "left", vertical: "middle" },
      });
    }

    categoryTotalRows.forEach((r) => {
      styleRange(ws2, r, r, 1, 18, {
        font: { bold: true },
        fill: fillGrey("FFF9F9F9"),
        border: thinBorder,
        alignment: { horizontal: "center", vertical: "middle" },
      });
      styleRange(ws2, r, r, 1, 1, {
        font: { bold: true },
        fill: fillGrey("FFF9F9F9"),
        border: thinBorder,
        alignment: { horizontal: "left", vertical: "middle" },
      });
    });

    styleRange(ws2, overallTotalRow, overallTotalRow, 1, 18, {
      font: { bold: true },
      fill: fillGrey("FFF5F5F5"),
      border: thinBorder,
      alignment: { horizontal: "center", vertical: "middle" },
    });

    styleRange(ws2, diagTitleRow, diagTitleRow, 1, 18, {
      font: { bold: true },
      fill: fillGrey("FFEFEFEF"),
      border: thinBorder,
      alignment: { horizontal: "left", vertical: "middle" },
    });

    styleRange(ws2, diagHeaderRow, diagHeaderRow, 1, 12, {
      font: { bold: true },
      fill: fillGrey("FFE5E5E5"),
      border: thinBorder,
      alignment: { horizontal: "center", vertical: "middle" },
    });

    styleRange(ws2, diagHeaderRow + 1, diagLastRow, 1, 12, {
      border: thinBorder,
      alignment: { horizontal: "left", vertical: "top" },
    });

    // Signature rows styling
    [sigRow1, sigRow2].forEach((r) => {
      ws2.getRow(r).height = 32;
      ws2.getCell(r, 1).alignment = { horizontal: "left", vertical: "bottom" };
      ws2.getCell(r, 3).border = {
        bottom: { style: "thin", color: { argb: "FF000000" } },
      };
      ws2.getCell(r, 10).font = { bold: false };
      ws2.getCell(r, 10).alignment = { horizontal: "left", vertical: "bottom" };
      ws2.getCell(r, 11).border = {
        bottom: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    const filename = `${(subjectname || "Subject").replace(/\s+/g, "_")}_${(subjectClass || "Class").replace(/\s+/g, "_")}_Term_${currentTerm}_Report.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename,
    );
  }, [
    studentsWithComputed,
    sections,
    sectionMarks,
    calculateLevel,
    formatGrade,
    currentTerm,
    subjectname,
    subjectClass,
    subjectGrade,
    students.length,
    foundationSubjectDefinition,
    foundationTemplateCode,
    isFoundationSubject,
  ]);

  const handleDownloadTemplate = async () => {
    if (!sections.length && !students.length) {
      alert("Add sections or students first.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Template");
    const usesGroupedHeaders = isFoundationSubject || hasGroupedColumnHeaders;

    if (usesGroupedHeaders) {
      worksheet.addRow([
        "First Name",
        "Last Name",
        ...sections.map((section) =>
          normalizeGroupText(
            sectionHeaderMeta[section.id]?.groupLabel ||
              getAssessmentGroupLabel(section),
          ),
        ),
      ]);
      worksheet.addRow([
        "",
        "",
        ...sections.map((section) =>
          normalizeGroupText(section.section_name || "Assessment"),
        ),
      ]);
      worksheet.mergeCells(1, 1, 2, 1);
      worksheet.mergeCells(1, 2, 2, 2);
    } else {
      worksheet.addRow([
        "First Name",
        "Last Name",
        ...sections.map((s) => s.section_name),
      ]);
    }

    students.forEach((s) => {
      const row = [s.first_name, s.last_name];
      sections.forEach(() => row.push(""));
      worksheet.addRow(row);
    });

    const headerRows = usesGroupedHeaders
      ? [worksheet.getRow(1), worksheet.getRow(2)]
      : [worksheet.getRow(1)];
    headerRows.forEach((headerRow, rowIndex) => {
      headerRow.font = { bold: true };
      headerRow.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      headerRow.height = rowIndex === 0 && usesGroupedHeaders ? 24 : 22;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: rowIndex === 0 ? "FFE8F1EA" : "FFF7FAF7" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FFD9D9D9" } },
          left: { style: "thin", color: { argb: "FFD9D9D9" } },
          bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
          right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
      });
    });

    worksheet.views = [{ state: "frozen", ySplit: usesGroupedHeaders ? 2 : 1 }];

    worksheet.columns = [
      { width: 18 },
      { width: 18 },
      ...sections.map((section) => ({
        width:
          normalizeGroupText(sectionHeaderMeta[section.id]?.groupLabel || "")
            .length > 0
            ? 18
            : 14,
      })),
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      blob,
      `Template_${(subjectname || "Subject").replace(/\s+/g, "_")}_Term${currentTerm}.xlsx`,
    );
  };

  // --- Section Management Handlers ---
  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!newSection.section_name.trim()) return alert("Name required");
    setAddingSection(true);
    try {
      const normalizedGroup = normalizeGroupText(
        isFoundationSubject
          ? newSection.assessment_group ||
              foundationAssessmentGroupOptions[0] ||
              ""
          : newSection.assessment_group,
      );
      const matchingGroup = matchingAssessmentGroup;
      await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
        subject_id: id,
        term: currentTerm,
        ...newSection,
        weight: isFoundationSubject
          ? 1
          : matchingGroup
            ? matchingGroup.currentWeight
            : parsePositiveNumber(newSection.weight, 1),
        category: isFoundationSubject ? normalizedGroup : newSection.category,
        assessment_group: normalizedGroup,
      });
      setNewSection((prev) => ({
        ...prev,
        section_name: "",
        max_mark: 100,
        weight: isFoundationSubject
          ? 1
          : matchingGroup
            ? matchingGroup.currentWeight
            : parsePositiveNumber(prev.weight, 1),
        category: isFoundationSubject ? normalizedGroup : prev.category,
        assessment_group: normalizedGroup,
      }));
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`,
      );
      setSections(res.data.sections || []);
    } catch (e) {
      alert("Failed");
    } finally {
      setAddingSection(false);
    }
  };

  // Guided set-up: add several sections of one assessment at once.
  const handleCreateSections = async (rows, { assessment_group, weight, category }) => {
    const group = normalizeGroupText(assessment_group);
    for (const row of rows) {
      await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
        subject_id: id,
        term: currentTerm,
        section_name: row.name,
        max_mark: row.max,
        weight: parsePositiveNumber(weight, 1),
        category,
        assessment_group: group,
      });
    }
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`,
    );
    setSections(res.data.sections || []);
  };

  const handleUpdateSection = (id, field, val) => {
    if (field === "max_mark") setSectionMaxMarks((p) => ({ ...p, [id]: val }));

    const timerRef = maxMarkTimersRef;
    if (timerRef.current[id]) clearTimeout(timerRef.current[id]);

    timerRef.current[id] = setTimeout(async () => {
      try {
        const section = sections.find((s) => s.id === id);
        if (!section) return;

        await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
          id,
          section_name: section.section_name,
          weight: section.weight,
          max_mark: field === "max_mark" ? val : section.max_mark,
          category: section.category || "",
          assessment_group: section.assessment_group || "",
        });

        setSections((prev) =>
          prev.map((s) =>
            s.id === id ? { ...s, [field]: parseFloat(val) } : s,
          ),
        );
        if (field === "max_mark") {
          setSectionMaxMarks((prev) => {
            const u = { ...prev };
            delete u[id];
            return u;
          });
        }
      } catch (e) {
        alert("Update failed. Check your connection.");
      }
    }, 1500);
  };

  const SECTION_CATEGORIES = [
    "",
    "Assignment 1",
    "Assignment 2",
    "Essay",
    "Exam",
    "Investigation 1",
    "Investigation 2",
    "Oral 1",
    "Oral 2",
    "Other",
    "PAT",
    "Practical 1",
    "Practical 2",
    "Presentation",
    "Project 1",
    "Project 2",
    "SBA",
    "Test 1",
    "Test 2",
    "Test 3",
    "Transactional Writing",
  ];

  const handleUpdateSectionCategory = async (sectionId, val) => {
    try {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
        id: sectionId,
        section_name: section.section_name,
        weight: section.weight,
        max_mark: section.max_mark,
        category: val,
        assessment_group: section.assessment_group || "",
      });
      setSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, category: val } : s)),
      );
    } catch (e) {
      alert("Update failed. Check your connection.");
    }
  };

  const handleUpdateSectionName = async (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    const nextName = normalizeGroupText(
      sectionNameDrafts[sectionId] ?? section.section_name,
    );
    if (!nextName || nextName === normalizeGroupText(section.section_name)) {
      setSectionNameDrafts((prev) => {
        const updated = { ...prev };
        delete updated[sectionId];
        return updated;
      });
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
        id: sectionId,
        section_name: nextName,
        weight: section.weight,
        max_mark: section.max_mark,
        category: section.category || "",
        assessment_group: section.assessment_group || "",
      });
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId ? { ...s, section_name: nextName } : s,
        ),
      );
      setSectionNameDrafts((prev) => {
        const updated = { ...prev };
        delete updated[sectionId];
        return updated;
      });
    } catch (e) {
      alert("Section name update failed. Check your connection.");
    }
  };

  const handleUpdateSectionGroup = async (sectionId, nextGroup) => {
    try {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
        id: sectionId,
        section_name: section.section_name,
        weight: section.weight,
        max_mark: section.max_mark,
        category: nextGroup,
        assessment_group: nextGroup,
      });
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId
            ? { ...s, category: nextGroup, assessment_group: nextGroup }
            : s,
        ),
      );
    } catch (e) {
      alert("CAPS area update failed. Check your connection.");
    }
  };

  const handleUpdateAssessmentGroupName = async (group) => {
    const nextGroup = normalizeGroupText(
      assessmentNameDrafts[group.key] ?? group.primaryGroupName,
    );
    const currentGroupName = normalizeGroupText(group.primaryGroupName);

    if (nextGroup === currentGroupName) {
      setAssessmentNameDrafts((prev) => {
        const updated = { ...prev };
        delete updated[group.key];
        return updated;
      });
      return;
    }

    setSavingAssessmentNameKey(group.key);
    try {
      await Promise.all(
        group.sections.map((section) =>
          axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
            id: section.id,
            section_name: section.section_name,
            weight: section.weight,
            max_mark: section.max_mark,
            category: section.category || "",
            assessment_group: nextGroup,
          }),
        ),
      );

      const sectionIds = new Set(group.sections.map((section) => section.id));
      setSections((prev) =>
        prev.map((section) =>
          sectionIds.has(section.id)
            ? { ...section, assessment_group: nextGroup }
            : section,
        ),
      );
      setAssessmentNameDrafts((prev) => {
        const updated = { ...prev };
        delete updated[group.key];
        return updated;
      });
    } catch (e) {
      alert("Assessment name update failed. Check your connection.");
    } finally {
      setSavingAssessmentNameKey("");
    }
  };

  const handleUpdateAssessmentGroupWeight = async (group) => {
    const draftValue = assessmentWeightDrafts[group.key];
    const nextWeight = parsePositiveNumber(draftValue, group.currentWeight);
    const currentWeight = parsePositiveNumber(group.currentWeight, 1);

    if (
      Math.abs(nextWeight - currentWeight) < 0.0001 &&
      !group.hasMixedWeights
    ) {
      setAssessmentWeightDrafts((prev) => {
        const updated = { ...prev };
        delete updated[group.key];
        return updated;
      });
      return;
    }

    setSavingAssessmentGroupKey(group.key);
    try {
      await Promise.all(
        group.sections.map((section) =>
          axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
            id: section.id,
            section_name: section.section_name,
            weight: nextWeight,
            max_mark: section.max_mark,
            category: section.category || "",
            assessment_group: section.assessment_group || "",
          }),
        ),
      );

      const sectionIds = new Set(group.sections.map((section) => section.id));
      setSections((prev) =>
        prev.map((section) =>
          sectionIds.has(section.id)
            ? { ...section, weight: nextWeight }
            : section,
        ),
      );
      setAssessmentWeightDrafts((prev) => {
        const updated = { ...prev };
        delete updated[group.key];
        return updated;
      });
    } catch (e) {
      alert("Weight update failed. Check your connection.");
    } finally {
      setSavingAssessmentGroupKey("");
    }
  };

  const handleDeleteSection = async (sid, name) => {
    const markedCount = markedRuns.filter((run) =>
      run.section_ids.includes(sid),
    ).length;
    const warning = markedCount
      ? `\n\n${markedCount} marked test${markedCount === 1 ? "" : "s"} saved for this section, including stored scripts, will be permanently deleted.`
      : "";
    if (!window.confirm(`Delete ${name}?${warning}`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/task_sections.php?id=${sid}`,
      );
      setSections((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) {
      alert("Error deleting");
    }
  };

  // --- Mark Handlers (Optimized) ---
  const handleSectionMarkChange = useCallback(
    (studentId, sectionId, value) => {
      const key = `${studentId}-${sectionId}`;
      let sanitized = value.replace(/[^0-9.]/g, "");

      // Allow empty string (for deletion)
      if (sanitized === "") {
        setMarksBeingEdited((prev) => ({ ...prev, [key]: "" }));
        setSectionMarks((prev) => {
          const idx = prev.findIndex(
            (m) =>
              parseInt(m.student_id) === parseInt(studentId) &&
              parseInt(m.section_id) === parseInt(sectionId),
          );
          if (idx !== -1) {
            const newMarks = [...prev];
            newMarks[idx] = { ...newMarks[idx], mark: "" };
            return newMarks;
          }
          return prev;
        });
        return;
      }

      // Validate and cap the value
      if (!isNaN(sanitized)) {
        let num = parseFloat(sanitized);
        const section = sections.find((s) => s.id === sectionId);
        const max = section?.max_mark || 100;
        if (num > max) {
          setMarkLimitNotice(
            `${section?.section_name || "This assessment"} has a maximum of ${max}. The mark was limited to ${max}.`,
          );
          num = max;
        } else if (num >= 0) {
          setMarkLimitNotice("");
        }
        sanitized = String(num);
      }

      setMarksBeingEdited((prev) => ({ ...prev, [key]: sanitized }));

      setSectionMarks((prev) => {
        const idx = prev.findIndex(
          (m) =>
            parseInt(m.student_id) === parseInt(studentId) &&
            parseInt(m.section_id) === parseInt(sectionId),
        );
        if (idx !== -1 && prev[idx].mark === sanitized) return prev; // Prevent unnecessary updates

        const newMarks = [...prev];
        if (idx !== -1) {
          newMarks[idx] = { ...newMarks[idx], mark: sanitized };
        } else {
          newMarks.push({
            student_id: parseInt(studentId),
            section_id: parseInt(sectionId),
            mark: sanitized,
          });
        }
        return newMarks;
      });
    },
    [sections],
  );

  const handleMarkBlur = useCallback(
    async (studentId, sectionId) => {
      const key = `${studentId}-${sectionId}`;
      const val = marksBeingEdited[key];
      if (val !== undefined) {
        if (val !== "" && val !== null && !confirmTermForMarkEntry()) return;
        try {
          // Find the existing mark record
          const markObj = sectionMarks.find(
            (m) =>
              parseInt(m.student_id) === parseInt(studentId) &&
              parseInt(m.section_id) === parseInt(sectionId),
          );

          if (val === "" || val === null) {
            // Empty value - DELETE the mark if it exists
            if (markObj && markObj.id) {
              await axios.delete(
                `${process.env.REACT_APP_API_URL}/section_marks.php?id=${markObj.id}`,
              );
              // Remove from state
              setSectionMarks((prev) =>
                prev.filter((m) => m.id !== markObj.id),
              );
            }
          } else {
            // Non-empty value - POST or PUT
            if (markObj && markObj.id) {
              // Update existing mark
              await axios.put(
                `${process.env.REACT_APP_API_URL}/section_marks.php`,
                {
                  id: markObj.id,
                  mark: val,
                },
              );
            } else {
              // Create new mark
              const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/section_marks.php`,
                {
                  student_id: studentId,
                  section_id: sectionId,
                  mark: val,
                },
              );
              // Update the mark in state with the new ID from response
              if (response.data.id) {
                setSectionMarks((prev) => {
                  const idx = prev.findIndex(
                    (m) =>
                      parseInt(m.student_id) === parseInt(studentId) &&
                      parseInt(m.section_id) === parseInt(sectionId),
                  );
                  if (idx !== -1) {
                    const newMarks = [...prev];
                    newMarks[idx] = { ...newMarks[idx], id: response.data.id };
                    return newMarks;
                  }
                  return prev;
                });
              }
            }
          }

          setMarksBeingEdited((prev) => {
            const u = { ...prev };
            delete u[key];
            return u;
          });
        } catch (e) {
          console.error("Mark save error:", e);
          alert("Error saving mark: " + (e.response?.data?.error || e.message));
        }
      }
    },
    [marksBeingEdited, sectionMarks, confirmTermForMarkEntry],
  );

  const handleMarkInputFocus = useCallback(
    (event) => {
      if (!confirmTermForMarkEntry()) {
        event.currentTarget.blur();
      }
    },
    [confirmTermForMarkEntry],
  );

  // --- Student Handlers ---
  const closeRosterScan = (force = false) => {
    if (!force && (rosterScanning || rosterImporting)) return;
    setShowRosterScan(false);
    setRosterImage(null);
    setRosterPreview("");
    setRosterRows([]);
    setRosterError("");
  };

  const selectRosterImage = (file) => {
    if (!file) return;
    const accepted = ["image/png", "image/jpeg", "image/webp"];
    if (!accepted.includes(file.type) || file.size > 10 * 1024 * 1024) {
      setRosterError("Choose a PNG, JPEG, or WEBP image no larger than 10 MB.");
      return;
    }
    setRosterImage(file);
    setRosterPreview(URL.createObjectURL(file));
    setRosterRows([]);
    setRosterError("");
  };

  const handleRosterScan = async () => {
    if (!rosterImage) return;
    setRosterScanning(true);
    setRosterError("");
    try {
      const uploadImage = await prepareRosterImageForUpload(rosterImage);
      const formData = new FormData();
      formData.append("subject_id", id);
      formData.append("image", uploadImage);
      const token = localStorage.getItem("auth_token");
      const result = await axios.post(
        `${process.env.REACT_APP_API_URL}/scan_roster.php`,
        formData,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
      const scannedRows = (result.data?.students || []).map(
        (student, index) => ({
          key: `${Date.now()}-${index}`,
          selected: true,
          first_name: student.first_name || "",
          last_name: student.last_name || "",
          raw_name: student.raw_name || "",
          marks_text: formatScannedMarks(student.marks),
          needs_review: Boolean(student.needs_review),
        }),
      );
      if (!scannedRows.length) {
        setRosterError(
          "No names were found. Try a clearer, well-lit photo or add learners manually.",
        );
      }
      setRosterRows(scannedRows);
    } catch (error) {
      setRosterError(
        error.response?.data?.error ||
          "Scanning failed. Please try another image.",
      );
    } finally {
      setRosterScanning(false);
    }
  };

  const updateRosterRow = (key, field, value) => {
    setRosterRows((current) =>
      current.map((row) =>
        row.key === key ? { ...row, [field]: value } : row,
      ),
    );
  };

  const addRosterRow = () =>
    setRosterRows((current) => [
      ...current,
      {
        key: `${Date.now()}-${current.length}`,
        selected: true,
        first_name: "",
        last_name: "",
        raw_name: "",
        marks_text: "",
        needs_review: false,
      },
    ]);

  const importRosterRows = async () => {
    const selected = rosterRows
      .filter((row) => row.selected)
      .map(({ first_name, last_name, marks_text }) => ({
        first_name,
        last_name,
        marks: parseScannedMarks(marks_text),
      }));
    if (!selected.length)
      return setRosterError("Select at least one learner to add.");
    if (selected.some((row) => !row.first_name.trim() || !row.last_name.trim()))
      return setRosterError(
        "Every selected learner needs both a first name and surname.",
      );
    const existingAssessmentNames = new Set(
      sections.map((section) => normalizeAssessmentName(section.section_name)),
    );
    const missingAssessments = [
      ...new Map(
        selected
          .flatMap((row) => row.marks || [])
          .filter(
            (mark) =>
              !existingAssessmentNames.has(
                normalizeAssessmentName(mark.section_name),
              ),
          )
          .map((mark) => [
            normalizeAssessmentName(mark.section_name),
            mark.section_name,
          ]),
      ).values(),
    ];
    const createSections =
      missingAssessments.length &&
      window.confirm(
        `The scanned marks include ${missingAssessments.join(", ")}, but ${missingAssessments.length === 1 ? "this assessment does" : "these assessments do"} not exist for Term ${currentTerm}. Add ${missingAssessments.length === 1 ? "it" : "them"} before importing the marks?`,
      )
        ? missingAssessments
        : [];
    setRosterImporting(true);
    setRosterError("");
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_API_URL}/batch_add_students.php`,
        {
          subject_id: id,
          term: currentTerm,
          create_sections: createSections,
          students: selected,
        },
      );
      await loadSubjectData({ showLoader: false });
      setRosterImporting(false);
      closeRosterScan(true);
      const count = Number(result.data?.linked || 0);
      setSuccessMessage({
        title: "Class list imported",
        message: `${count} learner${count === 1 ? "" : "s"} added to this class; ${Number(result.data?.marks_imported || 0)} mark${Number(result.data?.marks_imported || 0) === 1 ? "" : "s"} imported.${result.data?.marks_skipped ? ` ${result.data.marks_skipped} mark${result.data.marks_skipped === 1 ? "" : "s"} could not be matched to an assessment.` : ""}`,
      });
    } catch (error) {
      setRosterError(
        error.response?.data?.error || "Could not add the selected learners.",
      );
    } finally {
      setRosterImporting(false);
    }
  };

  const handleAddStudent = async () => {
    if (!newStudent.firstName) return alert("Name required");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/add_student_with_marks.php`,
        {
          subject_id: id,
          first_name: newStudent.firstName,
          last_name: newStudent.lastName,
        },
      );
      if (res.data.success) {
        const newStudentData = {
          student_id: res.data.student_id,
          first_name: newStudent.firstName,
          last_name: newStudent.lastName,
          level: null,
          average: null,
        };
        setStudents((prev) => [...prev, newStudentData]);
        setNewStudent({ firstName: "", lastName: "" });
        setAddStudentCollapsed(true);
        setSuccessMessage({
          title: "Learner added",
          message: `${newStudent.firstName}${newStudent.lastName ? ` ${newStudent.lastName}` : ""} has been added to this subject.`,
        });
      } else {
        alert("Failed to add student");
      }
    } catch (e) {
      alert("Error adding student: " + e.message);
    }
  };

  const handleDeleteStudent = async (sid, name) => {
    if (!window.confirm(`Remove student ${name || ""}?`)) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/delete_student.php`, {
        student_id: sid,
        subject_id: id,
      });
      setStudents((prev) => prev.filter((s) => s.student_id !== sid));
    } catch (e) {
      alert("Error");
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL DATA?")) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/delete_all_students.php`,
        { subject_id: id },
      );
      setStudents([]);
      setSectionMarks([]);
      alert("All students and marks deleted successfully!");
    } catch (e) {
      alert("Error deleting: " + e.message);
    }
  };

  const loadSubjectData = useCallback(
    async ({ showLoader = true } = {}) => {
      if (showLoader) {
        setLoading(true);
      }

      try {
        const [classRes, secRes, marksRes, notesRes, markedRes] = await Promise.allSettled(
          [
            axios.get(
              `${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}&term=${currentTerm}`,
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`,
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${currentTerm}`,
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL}/subject_notes.php?subject_id=${id}`,
            ),
            axios.get(
              `${process.env.REACT_APP_API_URL}/marked_scripts.php?subject_id=${id}&term=${currentTerm}`,
            ),
          ],
        );

        // Set students data
        const classData =
          classRes.status === "fulfilled" ? classRes.value.data : {};
        const studentsData = (classData.students || []).map((s) => ({
          ...s,
          level: s.average ? calculateLevel(s.average) : null,
        }));
        setStudents(studentsData);
        setSubjectName(classData.subject_name || "Subject");
        setSubjectGrade(classData.grade || "");
        setSubjectClass(classData.class_name || "");
        setSubjectGradingMode(classData.grading_mode || "standard");
        setFoundationTemplateCode(classData.foundation_template_code || "");
        setFoundationTemplateDefinition(classData.foundation_template || null);
        setSubjectPassRequirements(classData.pass_requirements || null);

        // Set sections and max marks
        const secData =
          secRes.status === "fulfilled" ? secRes.value.data : { sections: [] };
        const sectionsData = secData.sections || [];
        setSections(sectionsData);

        const maxes = {};
        sectionsData.forEach((s) => {
          maxes[s.id] = s.max_mark;
        });
        setSectionMaxMarks(maxes);

        // Set marks
        const marksData =
          marksRes.status === "fulfilled" ? marksRes.value.data : { marks: [] };
        setSectionMarks(marksData.marks || []);

        // Set notes (gracefully handle failure)
        const notesData =
          notesRes.status === "fulfilled" ? notesRes.value.data : { notes: [] };
        setNotes(notesData.notes || []);
        setMarkedRuns(
          markedRes.status === "fulfilled" ? markedRes.value.data?.runs || [] : [],
        );
        setMarksBeingEdited({});
      } catch (e) {
        console.error("Load error:", e);
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [id, currentTerm, calculateLevel],
  );

  const handlePassRequirementFieldChange = useCallback(
    (field, value) => {
      setSubjectPassRequirements((current) => ({
        ...(current || {
          default_pass_mark: getDefaultSubjectPassMark(subjectname),
          default_support_threshold: getDefaultSupportThreshold(),
          default_distinction_threshold: getDefaultDistinctionThreshold(),
          pass_mark_override: null,
          support_threshold_override: null,
          distinction_threshold_override: null,
          pass_mark: getDefaultSubjectPassMark(subjectname),
          support_threshold: getDefaultSupportThreshold(),
          distinction_threshold: getDefaultDistinctionThreshold(),
          has_override: false,
        }),
        [field]: value,
      }));
    },
    [subjectname],
  );

  const handleSavePassRequirements = useCallback(async () => {
    if (!id) {
      return;
    }

    setSavingPassRequirements(true);
    try {
      const payload = {
        subject_id: Number(id),
        pass_mark_override:
          subjectPassRequirements?.pass_mark_override === ""
            ? null
            : subjectPassRequirements?.pass_mark_override,
        support_threshold_override:
          subjectPassRequirements?.support_threshold_override === ""
            ? null
            : subjectPassRequirements?.support_threshold_override,
        distinction_threshold_override:
          subjectPassRequirements?.distinction_threshold_override === ""
            ? null
            : subjectPassRequirements?.distinction_threshold_override,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/update_subject_pass_requirements.php`,
        payload,
      );
      if (response.data?.success) {
        setSubjectPassRequirements(response.data.pass_requirements || null);
        setPassRequirementsCollapsed(true);
        setSuccessMessage({
          title: "Pass requirements updated",
          message:
            "The pass mark, support threshold and distinction threshold are now active for this subject.",
        });
      } else {
        alert(response.data?.error || "Failed to save pass requirements");
      }
    } catch (error) {
      alert(error.response?.data?.error || "Failed to save pass requirements");
    } finally {
      setSavingPassRequirements(false);
    }
  }, [id, subjectPassRequirements]);

  const handleResetPassRequirements = useCallback(() => {
    setSubjectPassRequirements((current) => ({
      ...(current || {}),
      pass_mark_override: "",
      support_threshold_override: "",
      distinction_threshold_override: "",
      pass_mark:
        current?.default_pass_mark ?? getDefaultSubjectPassMark(subjectname),
      support_threshold:
        current?.default_support_threshold ?? getDefaultSupportThreshold(),
      distinction_threshold:
        current?.default_distinction_threshold ??
        getDefaultDistinctionThreshold(),
      has_override: false,
    }));
  }, [subjectname]);

  useEffect(() => {
    const storedTerm = requestedTerm || getStoredSubjectTerm(id);
    setCurrentTerm(storedTerm);
    setTermReadySubjectId(id || "");
    setMarksBeingEdited({});
    focusedPendingTarget.current = null;
  }, [id, requestedTerm, linkedStudent, linkedSection]);

  useEffect(() => {
    if (typeof window === "undefined" || !id || termReadySubjectId !== id) {
      return;
    }

    window.localStorage.setItem(
      getSubjectTermStorageKey(id),
      String(currentTerm),
    );
  }, [id, currentTerm, termReadySubjectId]);

  // --- Initial Load (Optimized) ---
  useEffect(() => {
    if (!id || termReadySubjectId !== id) {
      return;
    }

    const init = async () => {
      await loadSubjectData();
    };
    init();
  }, [id, termReadySubjectId, loadSubjectData]);

  useEffect(() => {
    if (
      loading ||
      !linkedStudent ||
      currentTerm !== requestedTerm ||
      termReadySubjectId !== id
    )
      return;
    const targetKey = `${id}-${currentTerm}-${linkedStudent}-${linkedSection}`;
    if (focusedPendingTarget.current === targetKey) return;
    const student = students.find(
      (item) => String(item.student_id) === linkedStudent,
    );
    if (!student) return;
    const missingSection = sections.find((section) => {
      const mark = sectionMarks.find(
        (item) =>
          String(item.student_id) === linkedStudent &&
          String(item.section_id) === String(section.id),
      );
      return !mark || mark.mark == null || mark.mark === "";
    });
    const targetSection =
      missingSection ||
      sections.find((section) => String(section.id) === linkedSection);
    const input =
      targetSection &&
      getMarkInputRef(targetSection.id, student.student_id).current;
    if (!input) return;
    focusedPendingTarget.current = targetKey;
    input.scrollIntoView({ block: "center", inline: "center" });
    input.focus({ preventScroll: true });
  }, [
    loading,
    linkedStudent,
    linkedSection,
    currentTerm,
    requestedTerm,
    termReadySubjectId,
    id,
    students,
    sections,
    sectionMarks,
    getMarkInputRef,
  ]);

  return (
    <div className="subject-detail-wrapper">
      <style>{`
                .modern-table tr.pending-target-row > td:first-child { background: #e5f3e9; box-shadow: inset 4px 0 #2f8f53; }
                .modern-table tr.pending-target-row > td:nth-child(2) { background: #e5f3e9; }
                .modern-table .pending-target-missing { background: #fff3cd; box-shadow: inset 0 0 0 2px #dfb653; }
                .pending-target-note { color: #21683c; font-size: 0.9rem; margin-top: 8px; }
                :root {
                    --primary-green: #4CAF50;
                    --dark-green: #388E3C;
                    --light-green: #E8F5E9;
                    --text-dark: #2c3e50;
                    --text-light: #7f8c8d;
                    --border-color: #ecf0f1;
                    --card-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                }

                body { background-color: #f8f9fa; font-family: 'Inter', -apple-system, sans-serif; color: var(--text-dark); }
                
                 .subject-detail-wrapper {
                     padding: calc(2rem + var(--shell-top-offset)) 2rem 2rem;
                     margin-left: var(--sidebar-layout-width); 
                     width: calc(100% - var(--sidebar-layout-width));
                 }

                .page-header {
                  
                    padding: 1.75rem 2rem;
                    border-radius: 12px;
                    box-shadow: var(--card-shadow);
                    display: grid;
                    grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
                    gap: 1.5rem;
                    align-items: start;
                    margin-bottom: 2rem;
                }
                .subject-hero { display: grid; gap: 0.8rem; }
                .page-kicker {
                    margin: 0;
                    font-size: 0.76rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.14em;
                    color: #2f7a4b;
                }
                .page-header h1 { margin: 0; font-size: 1.5rem; font-weight: 700; color: white; }
                .page-summary { margin: 0; color: #5f6f66; line-height: 1.5; max-width: 720px; }
                .header-meta { display: flex; flex-wrap: wrap; gap: 0.65rem; }
                .meta-chip, .badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    background: rgba(232, 245, 233, 0.95);
                    color: var(--dark-green);
                    padding: 0.4rem 0.85rem;
                    border-radius: 999px;
                    font-size: 0.82rem;
                    font-weight: 600;
                    border: 1px solid rgba(76, 175, 80, 0.14);
                }
                .badge { margin-left: 10px; }
                .header-side { display: grid; gap: 1rem; justify-items: end; }
                .page-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 0.75rem; }
                .workspace-summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }
                .summary-card {
                    background: white;
                    border: 1px solid var(--border-color);
                    border-radius: 14px;
                    padding: 1rem 1.1rem;
                    box-shadow: var(--card-shadow);
                }
                .summary-label {
                    margin: 0 0 0.35rem;
                    color: var(--text-light);
                    font-size: 0.78rem;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    font-weight: 700;
                }
                .summary-value {
                    margin: 0 0 0.2rem;
                    font-size: 1.55rem;
                    line-height: 1;
                    color: var(--text-dark);
                    font-weight: 800;
                }
                .summary-note { margin: 0; color: #66756c; font-size: 0.88rem; }
                .section-block { margin-bottom: 1.75rem; }
                .section-heading {
                    display: flex;
                    justify-content: space-between;
                    align-items: end;
                    gap: 1rem;
                    margin-bottom: 1rem;
                    flex-wrap: wrap;
                }
                .section-heading h2 {
                    margin: 0;
                    color: var(--text-dark);
                    font-size: 1.28rem;
                    font-weight: 700;
                }
                .section-kicker {
                    margin: 0 0 0.3rem;
                    color: #2f7a4b;
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.12em;
                }
                .section-copy { margin: 0.35rem 0 0; color: #66756c; line-height: 1.5; max-width: 760px; }
                .section-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }
                .helper-text { margin: 0; color: #738379; font-size: 0.83rem; line-height: 1.45; }

                .term-tabs {
                    display: flex;
                    gap: 10px;
                    background: #f1f3f5;
                    padding: 5px;
                    border-radius: 25px;
                }
                .term-tab {
                    padding: 8px 24px;
                    border: none;
                    border-radius: 20px;
                    font-weight: 600;
                    color: #7f8c8d;
                    background: transparent;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                }
                .term-tab.active {
                    background: white;
                    color: var(--primary-green);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .term-tab:hover:not(.active) {
                    color: var(--text-dark);
                }

                .management-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(min(100%, 290px), 1fr));
                    gap: 1.5rem;
                }
                .modern-card { min-width: 0; background: white; border-radius: 12px; padding: 1.5rem; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); }
                .card-title { font-size: 1.1rem; font-weight: 600; margin-top: 0; margin-bottom: 1rem; color: var(--text-dark); border-bottom: 2px solid var(--light-green); padding-bottom: 0.5rem; display: inline-block; }
                .card-subtitle { margin: 0.75rem 0 0; color: #66756c; font-size: 0.92rem; line-height: 1.45; }
                /* Quick actions: tidy headers, responsive controls, and no horizontal spill. */
                .management-grid > .modern-card:not(.setup-card) {
                    position: relative;
                    overflow: hidden;
                    border-color: #dce8df;
                    box-shadow: 0 10px 24px rgba(24, 68, 42, 0.07);
                }
                .management-grid > .modern-card:nth-child(1)::before,
                .management-grid > .modern-card:nth-child(2)::before,
                .management-grid > .modern-card:nth-child(3)::before {
                    content: '';
                    position: absolute;
                    inset: 0 auto 0 0;
                    width: 4px;
                   
                }
                
                .management-grid > .modern-card:nth-child(1) > div:first-child,
                .management-grid > .modern-card:nth-child(2) > div:first-child {
                    display: grid !important;
                    grid-template-columns: minmax(0, 1fr) 38px;
                    align-items: start !important;
                    gap: 12px;
                }
                .management-grid > .modern-card:nth-child(3) > div:first-child {
                    display: grid !important;
                    grid-template-columns: minmax(0, 1fr) auto 38px;
                    align-items: start !important;
                    gap: 10px !important;
                }
                .management-grid > .modern-card:not(.setup-card) .card-title {
                    display: block;
                    margin: 0 !important;
                    padding: 0 0 0.45rem !important;
                    border-bottom: 1px solid #e4ece6;
                    line-height: 1.3;
                    overflow-wrap: anywhere;
                }
                .management-grid > .modern-card:not(.setup-card) .card-subtitle {
                    overflow-wrap: anywhere;
                }
                .management-grid > .modern-card:not(.setup-card) .btn.btn-outline[aria-label] {
                    width: 38px !important;
                    min-width: 38px;
                    height: 38px !important;
                    min-height: 38px;
                    margin: 0 !important;
                    padding: 0 !important;
                    border-radius: 10px;
                    background: #eff7f0;
                    border-color: #cfe2d2;
                    color: #286542;
                    font-size: 1.25rem !important;
                    line-height: 1;
                }
                .management-grid > .modern-card:not(.setup-card) .btn.btn-outline[aria-label]:hover {
                    background: #dcefe0;
                }
                .management-grid > .modern-card:nth-child(1) > div:nth-of-type(2) {
                    display: grid !important;
                    gap: 12px !important;
                    margin-top: 18px !important;
                }
                .management-grid > .modern-card:nth-child(1) input[type="file"] {
                    max-width: 100%;
                    min-width: 0;
                    font-size: 0.82rem;
                }
                .management-grid > .modern-card:nth-child(1) a {
                    overflow-wrap: anywhere;
                }
                .management-grid > .add-student-card > div:nth-of-type(2) {
                    display: grid !important;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 10px !important;
                    margin-top: 18px !important;
                }
                .management-grid > .add-student-card > div:nth-of-type(2) button {
                    grid-column: 1 / -1;
                }
                .management-grid > .modern-card:nth-child(3) .meta-chip {
                    max-width: 100%;
                    white-space: normal !important;
                    text-align: center;
                    justify-content: center;
                    line-height: 1.2;
                }
                .management-grid > .modern-card:nth-child(3) > div:nth-of-type(2) {
                    grid-template-columns: repeat(auto-fit, minmax(min(100%, 145px), 1fr)) !important;
                }
                .management-grid > .modern-card:nth-child(3) .form-actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                .management-grid > .modern-card:nth-child(3) .form-actions .btn {
                    width: auto;
                    flex: 1 1 150px;
                }
                /* --- SETUP CARD (ASSESSMENT SECTIONS) --- */
                .setup-card {
                    order: -1;
                    background: #ffffff;
                    border: 1.5px solid #dce8df;
                    border-radius: 20px;
                    padding: 24px;
                    box-shadow: 0 4px 24px rgba(18, 50, 35, 0.05);
                    margin-bottom: 24px;
                }

                .setup-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #e7eee9;
                    flex-wrap: wrap;
                }

                .setup-card-title-group {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .setup-card-title-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .setup-card-title {
                    margin: 0;
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #12301f;
                    letter-spacing: -0.01em;
                }

                .setup-term-badge {
                    background: linear-gradient(135deg, #1f7455 0%, #154c37 100%);
                    color: #ffffff;
                    font-size: 0.8rem;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 8px;
                    box-shadow: 0 2px 6px rgba(31, 116, 85, 0.22);
                }

                .setup-sections-count {
                    background: #eef5f0;
                    color: #27523c;
                    font-size: 0.78rem;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 8px;
                    border: 1px solid rgba(39, 82, 60, 0.12);
                }

                .setup-card-subtitle {
                    margin: 0;
                    color: #556c5e;
                    font-size: 0.86rem;
                    line-height: 1.4;
                }

                .setup-card-header-actions {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .setup-copy-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 8px 14px;
                    border-radius: 10px;
                    background: #eef8f2;
                    border: 1.5px solid #b8ddc4;
                    color: #1a5639;
                    font-size: 0.82rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.18s ease;
                }

                .setup-copy-btn:hover {
                    background: #1f7455;
                    border-color: #1f7455;
                    color: #ffffff;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(31, 116, 85, 0.25);
                }

                .setup-collapse-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    background: #f1f6f3;
                    border: 1px solid #d5e2d8;
                    color: #264a35;
                    cursor: pointer;
                    transition: all 0.18s ease;
                }

                .setup-collapse-btn:hover {
                    background: #e2ede5;
                    color: #1f7455;
                }

                .setup-chevron {
                    transition: transform 0.2s ease;
                }

                .setup-chevron.collapsed {
                    transform: rotate(-90deg);
                }

                /* Stepper Guidance */
                .assessment-stepper-guidance {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    background: #f7faf8;
                    border: 1px solid #e1ece4;
                    padding: 14px 20px;
                    border-radius: 14px;
                    margin: 18px 0;
                }

                .step-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex: 1;
                }

                .step-circle {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    background: linear-gradient(135deg, #1f7455 0%, #154c37 100%);
                    color: #ffffff;
                    border-radius: 50%;
                    font-size: 0.82rem;
                    font-weight: 800;
                    flex-shrink: 0;
                    box-shadow: 0 2px 6px rgba(31, 116, 85, 0.22);
                }

                .step-content {
                    display: flex;
                    flex-direction: column;
                    line-height: 1.15;
                }

                .step-label {
                    font-size: 0.85rem;
                    font-weight: 800;
                    color: #132e20;
                }

                .step-desc {
                    font-size: 0.74rem;
                    color: #617b6d;
                    font-weight: 500;
                }

                .step-connector {
                    height: 2px;
                    flex: 0 0 40px;
                    background: #d4e2d8;
                    border-radius: 2px;
                }

                /* Creation Card */
                .assessment-creation-card {
                    background: #f8faf8;
                    border: 1.5px solid #dce8df;
                    border-radius: 16px;
                    padding: 20px 22px;
                    margin-bottom: 24px;
                    box-shadow: 0 2px 12px rgba(18, 50, 35, 0.03);
                }

                .creation-card-header {
                    margin-bottom: 16px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #e4eee6;
                }

                .creation-card-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .creation-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    background: #eef6f1;
                    color: #1f7455;
                    display: grid;
                    place-items: center;
                    flex-shrink: 0;
                }

                .creation-card-title strong {
                    font-size: 0.96rem;
                    color: #132e20;
                    font-weight: 800;
                }

                .creation-card-title p {
                    margin: 2px 0 0;
                    font-size: 0.8rem;
                    color: #5c7466;
                }

                .creation-form-grid {
                    display: grid;
                    grid-template-columns: repeat(12, minmax(0, 1fr));
                    gap: 14px;
                    align-items: start;
                }

                .field-assessment-name {
                    grid-column: span 6;
                }

                .field-section-name {
                    grid-column: span 6;
                }

                .field-max-mark {
                    grid-column: span 3;
                }

                .field-weight {
                    grid-column: span 3;
                }

                .field-category {
                    grid-column: span 6;
                }

                .field-category.full-span {
                    grid-column: span 6;
                }

                /* Phones: names and category full width, max mark and weight side by side. */
                @media (max-width: 640px) {
                    .field-assessment-name,
                    .field-section-name,
                    .field-category,
                    .field-category.full-span {
                        grid-column: span 12;
                    }
                    .field-max-mark,
                    .field-weight {
                        grid-column: span 6;
                    }
                    .creation-form-grid .stacked-field label {
                        white-space: nowrap;
                        letter-spacing: 0.03em;
                    }
                }

                .creation-form-grid .stacked-field label,
                .stacked-field label,
                .stacked-field span {
                    margin: 0;
                    font-size: 0.74rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #4a6354;
                    margin-bottom: 3px;
                }

                .stacked-field {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    min-width: 0;
                }

                .stacked-field .field-label-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .stacked-field .field-label-row label {
                    margin-bottom: 0;
                }

                /* Every label row the same height, with or without a "?" tip, so the inputs line up. */
                .creation-form-grid .stacked-field > label,
                .creation-form-grid .stacked-field > .field-label-row {
                    display: flex;
                    align-items: center;
                    min-height: 22px;
                    margin-bottom: 4px;
                }

                .creation-form-grid .help-tip { margin-left: 0; }
                .creation-form-grid .help-tip-trigger { width: 18px; height: 18px; font-size: 0.7rem; }

                .creation-form-grid .modern-input,
                .modern-input {
                    width: 100%;
                    height: 42px;
                    padding: 0 12px;
                    border: 1.5px solid #d2ded5;
                    border-radius: 9px;
                    background: #ffffff;
                    color: #132e20;
                    font-size: 0.88rem;
                    box-sizing: border-box;
                    transition: all 0.18s ease;
                }

                .creation-form-grid .modern-input:focus,
                .modern-input:focus {
                    outline: none;
                    border-color: #1f7455;
                    box-shadow: 0 0 0 3px rgba(31, 116, 85, 0.14);
                }

                .field-hint {
                    margin-top: 3px;
                    font-size: 0.72rem;
                    color: #6d8477;
                }

                .creation-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    margin-top: 18px;
                    padding-top: 14px;
                    border-top: 1px solid #e4eee6;
                    flex-wrap: wrap;
                }

                .creation-tip {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #486856;
                    font-size: 0.82rem;
                    flex: 1;
                    min-width: 260px;
                }

                .creation-tip svg {
                    color: #1f7455;
                    flex-shrink: 0;
                }

                .btn-add-section {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 0 22px;
                    height: 42px;
                    border-radius: 10px;
                    background: linear-gradient(135deg, #1f7455 0%, #154c37 100%);
                    color: #ffffff;
                    font-size: 0.88rem;
                    font-weight: 700;
                    border: none;
                    cursor: pointer;
                    box-shadow: 0 2px 8px rgba(31, 116, 85, 0.28);
                    transition: all 0.18s ease;
                    white-space: nowrap;
                }

                .btn-add-section:hover:not(:disabled) {
                    background: linear-gradient(135deg, #258a66 0%, #1a5e44 100%);
                    box-shadow: 0 4px 14px rgba(31, 116, 85, 0.38);
                    transform: translateY(-1px);
                }

                .btn-add-section:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }

                /* Assessment Group Cards (Existing) */
                .setup-card .assessment-group-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .setup-card .assessment-group-card {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    background: #ffffff;
                    border: 1.5px solid #dce8df;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 3px 14px rgba(18, 50, 35, 0.04);
                    transition: all 0.18s ease;
                }

                .setup-card .assessment-group-card:hover {
                    border-color: #bcdbc6;
                    box-shadow: 0 6px 20px rgba(18, 50, 35, 0.07);
                }

                .setup-card .assessment-group-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 20px;
                    padding-bottom: 14px;
                    border-bottom: 1px solid #e7eee9;
                    flex-wrap: wrap;
                }

                .assessment-group-title {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    min-width: 220px;
                    flex: 1;
                }

                .group-title-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .group-icon-avatar {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    background: #eef6f1;
                    color: #1f7455;
                    display: grid;
                    place-items: center;
                    flex-shrink: 0;
                }

                .assessment-group-title h4 {
                    margin: 0;
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: #12301f;
                    letter-spacing: -0.01em;
                }

                .assessment-group-meta {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .assessment-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 10px;
                    border-radius: 8px;
                    background: #f1f6f3;
                    border: 1px solid rgba(35, 64, 45, 0.08);
                    color: #3b5847;
                    font-size: 0.78rem;
                    font-weight: 600;
                }

                .assessment-chip strong {
                    color: #132e20;
                    font-weight: 800;
                }

                .assessment-chip.weight-chip {
                    background: #eaf5ee;
                    border-color: #badfc4;
                    color: #185235;
                }

                .setup-card .assessment-inline-editors {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .assessment-inline-editor {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 170px;
                }

                .assessment-inline-editor label {
                    font-size: 0.72rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #4a6354;
                }

                .assessment-inline-editor .modern-input {
                    height: 38px;
                    padding: 0 10px;
                    border: 1.5px solid #d2ded5;
                    border-radius: 8px;
                    background: #ffffff;
                    font-size: 0.85rem;
                }

                .assessment-inline-editor .modern-input:focus {
                    border-color: #1f7455;
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(31, 116, 85, 0.12);
                }

                .assessment-inline-editor .helper-text {
                    margin: 0;
                    font-size: 0.72rem;
                    color: #6d8477;
                }

                /* Section Row List */
                .section-row-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .setup-card .section-row {
                    display: grid;
                    grid-template-columns: minmax(200px, 2.2fr) minmax(90px, 0.8fr) minmax(140px, 1.2fr) auto;
                    gap: 12px;
                    align-items: center;
                    padding: 10px 14px;
                    border-radius: 12px;
                    background: #fbfdfc;
                    border: 1px solid #e4ede6;
                    transition: all 0.15s ease;
                }

                .setup-card .section-row:hover {
                    background: #ffffff;
                    border-color: #badfc4;
                    box-shadow: 0 2px 8px rgba(18, 50, 35, 0.04);
                }

                .setup-card .section-row .stacked-field {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                    min-width: 0;
                }

                .row-field-label {
                    font-size: 0.7rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #617b6d;
                }

                .setup-card .section-row .modern-input {
                    height: 36px;
                    padding: 0 10px;
                    border: 1.5px solid #d5e2d8;
                    border-radius: 7px;
                    background: #ffffff;
                    color: #132e20;
                    font-size: 0.85rem;
                    box-sizing: border-box;
                }

                .setup-card .section-row .modern-input:focus {
                    border-color: #1f7455;
                    outline: none;
                }

                .btn-danger-icon {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 0 14px;
                    height: 36px;
                    border-radius: 8px;
                    border: 1px solid #fecaca;
                    background: #fef2f2;
                    color: #dc2626;
                    font-size: 0.8rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    white-space: nowrap;
                    align-self: flex-end;
                }

                .btn-danger-icon:hover {
                    background: #fee2e2;
                    border-color: #f87171;
                    color: #b91c1c;
                    transform: translateY(-1px);
                }

                .btn-danger-icon svg {
                    width: 14px;
                    height: 14px;
                }
                .modern-input { width: 100%; padding: 0.6rem; border: 1px solid #dfe6e9; border-radius: 6px; font-size: 0.9rem; box-sizing: border-box; }
                .compact-notes-card { min-width: 0; box-sizing: border-box; }
                .compact-notes-card .compact-notes-form { display: flex; flex-direction: column; gap: 8px; }
                .compact-notes-card .compact-notes-form > .modern-input { margin-bottom: 4px; }
                .compact-notes-card .compact-datetime { padding: 8px 10px; border: 1px solid #e0e0e0; border-radius: 6px; font-size: 0.9rem; }
                .compact-notes-card .compact-add-btn { width: 120px; flex: none; }
                .compact-notes-card .compact-notes-form, .compact-notes-card .compact-notes-form * { min-width: 0; box-sizing: border-box; }
                .compact-notes-card input[type="datetime-local"] { min-width: 0; flex: 1 1 auto; }
                .modern-input:focus { outline: none; border-color: var(--primary-green); box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1); }
                .btn { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 0.9rem; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
                .btn-primary { background: var(--primary-green); color: white; width: 100%; }
                .btn-outline { background:var(--primary-green); border: 1px solid var(--primary-green); color: white; width: 100%; }
                .at-risk-resource-btn {
                    width: auto;
                    background: #e74c3c !important;
                    border: 1px solid #e74c3c !important;
                    color: #fff !important;
                    font-weight: 600;
                }
                .at-risk-resource-btn:hover { background: #c83d31 !important; border-color: #c83d31 !important; }
                .btn-danger { background: #fee2e2; color: #dc2626; width: auto; padding: 0.4rem 0.8rem; font-size: 0.8rem; }
                
                .table-container { background: white; border-radius: 12px; box-shadow: var(--card-shadow); overflow-x: auto; overflow-y: auto; border: 1px solid var(--border-color); margin-bottom: 30px; padding: 0.7rem; }
                .table-toolbar {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 1rem;
                    margin-bottom: 1rem;
                    border-bottom: 1px solid #edf1ef;
                }
                .table-toolbar-copy { display: grid; gap: 0.25rem; }
                .table-toolbar-title { font-weight: 700; color: #55655d; }
                .table-toolbar-note { font-size: 0.88rem; color: #78867f; line-height: 1.45; }
                .table-toolbar-actions { display: flex; flex-wrap: nowrap; align-items: stretch; justify-content: flex-end; gap: 0.6rem; }
                .table-toolbar-actions .table-toolbar-action { box-sizing: border-box; width: 166px !important; height: 42px; min-height: 42px; flex: 0 0 166px; padding: 0.55rem 0.9rem; justify-content: center; white-space: nowrap; }
                .table-toolbar-action img { width: 16px; height: 16px; filter: invert(1); }
                .table-toolbar-risk-action { background: #e74c3c; color: #fff; }
                .table-toolbar-risk-action:hover { background: #c83d31; }
                .table-toolbar-action.save-complete { background: #2f8f53; }
                .learner-levels-mini { display: grid; grid-template-columns: 1fr 92px; align-items: center; gap: 6px; width: 166px; height: 42px; padding: 4px 7px; border: 1px solid #cfe0d2; border-radius: 6px; background: #f8fcf8; color: #315f42; font: inherit; font-size: .74rem; font-weight: 700; cursor: pointer; text-align: left; }
                .learner-levels-mini:hover { border-color: #70a67d; background: #eff8f0; }
                .learner-levels-mini:disabled { opacity: .58; cursor: not-allowed; }
                .learner-levels-chart { display: flex; align-items: end; justify-content: space-around; gap: 10px; min-height: 230px; padding: 4px 10px 0; border-bottom: 1px solid #e5ece7; }
                .learner-levels-chart__bar { display: flex; flex: 1; flex-direction: column; align-items: center; justify-content: end; gap: 8px; min-width: 34px; height: 220px; color: var(--text-dark); font-size: .8rem; font-weight: 700; }
                .learner-levels-chart__bar strong { min-height: 19px; font-size: .9rem; }
                .learner-levels-chart__track { display: flex; align-items: end; width: min(54px, 100%); height: 160px; overflow: hidden; border-radius: 8px 8px 0 0; background: #edf1ee; }
                .learner-levels-chart__track span { display: block; width: 100%; border-radius: inherit; }
                .learner-levels-chart .level-1 .learner-levels-chart__track span { background: #dc5b50; }.learner-levels-chart .level-2 .learner-levels-chart__track span { background: #e9844b; }.learner-levels-chart .level-3 .learner-levels-chart__track span { background: #d5ae45; }.learner-levels-chart .level-4 .learner-levels-chart__track span { background: #87a95c; }.learner-levels-chart .level-5 .learner-levels-chart__track span { background: #65a67b; }.learner-levels-chart .level-6 .learner-levels-chart__track span { background: #458c70; }.learner-levels-chart .level-7 .learner-levels-chart__track span { background: #286d54; }
                .learner-levels-chart--compact { min-height: 30px; height: 30px; gap: 3px; padding: 0; border: 0; }.learner-levels-chart--compact .learner-levels-chart__bar { min-width: 0; height: 30px; gap: 1px; font-size: .56rem; }.learner-levels-chart--compact .learner-levels-chart__track { width: 8px; height: 22px; border-radius: 3px 3px 0 0; }
                .compact-table-toggle { width: max-content; padding: 0.38rem 0.68rem; border: 1px solid #cfe0d2; border-radius: 999px; background: #f3f8f3; color: #356447; font: inherit; font-size: 0.78rem; font-weight: 700; cursor: pointer; }
                .compact-table-toggle:hover { background: #e5f1e6; }
                .modern-table { width: 100%; border-collapse: collapse; min-width: 520px; table-layout: auto; }
                .modern-table th { background: #f8f9fa; padding: 0.62rem 0.48rem; text-align: center; font-weight: 600; color: var(--text-dark); border-bottom: 2px solid var(--border-color); white-space: normal; position: sticky; top: 0; z-index: 1; font-size: 0.82rem; }
                .modern-table td { padding: 0.32rem; border-bottom: 1px solid var(--border-color); text-align: center; color: var(--text-dark); font-size: 0.86rem; }
                .modern-table th:nth-child(1), .modern-table th:nth-child(2) { width: 112px; }
                .modern-table th:nth-child(n + 3) { min-width: 62px; }
                .cell-input { width: 100%; min-width: 54px; padding: 6px 4px; border: 1px solid transparent; border-radius: 4px; text-align: center; font-weight: 500; background: transparent; }
                .modern-table.results-hidden td:nth-last-child(-n + 4) { display: none; }
                .modern-table.foundation-table.results-hidden td:nth-last-child(-n + 5) { display: none; }
                .cell-input:focus { background: white; border-color: var(--primary-green); outline: none; }
                
                .analytics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
                .stat-card { background: white; border-radius: 12px; padding: 24px; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); border-top: 4px solid var(--primary-green); display: flex; flex-direction: column; justify-content: space-between; min-height: 200px; }
                .stat-grid-inner { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; row-gap: 16px; margin-bottom: 20px; }
                .stat-item { display: flex; flex-direction: column; }
                .stat-label { font-size: 0.75rem; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
                .stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text-dark); }
                .stat-value.green { color: var(--primary-green); }
                .stat-value.red { color: #e74c3c; }
                .submission-count { margin-top: auto; padding-top: 15px; border-top: 1px dashed #eee; font-size: 0.85rem; color: var(--text-light); display: flex; align-items: center; gap: 8px; font-weight: 500; }
                .level-distribution-section { margin-top: 40px; padding: 24px; border: 1px solid var(--border-color); border-radius: 12px; background: white; box-shadow: var(--card-shadow); }
                .level-distribution-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
                .level-distribution-heading .stats-section-title { margin: 0; }
                .level-distribution-heading p { margin: 6px 0 0; color: var(--text-light); font-size: 0.9rem; }
                .level-distribution-heading > span { flex-shrink: 0; padding: 7px 10px; border-radius: 999px; background: #edf7ef; color: var(--dark-green); font-size: 0.78rem; font-weight: 700; }
                .level-distribution-chart { display: flex; align-items: end; justify-content: space-around; gap: 14px; min-height: 230px; padding: 4px 10px 0; border-bottom: 1px solid #e5ece7; }
                .level-distribution-bar { display: flex; flex: 1; flex-direction: column; align-items: center; justify-content: end; gap: 8px; min-width: 34px; height: 220px; color: var(--text-dark); font-size: 0.8rem; font-weight: 700; }
                .level-distribution-bar strong { min-height: 19px; font-size: 0.9rem; }
                .level-distribution-track { display: flex; align-items: end; width: min(54px, 100%); height: 160px; overflow: hidden; border-radius: 8px 8px 0 0; background: #edf1ee; }
                .level-distribution-track span { display: block; width: 100%; min-height: 0; border-radius: inherit; }
                .level-1 .level-distribution-track span { background: #dc5b50; }
                .level-2 .level-distribution-track span { background: #e9844b; }
                .level-3 .level-distribution-track span { background: #d5ae45; }
                .level-4 .level-distribution-track span { background: #87a95c; }
                .level-5 .level-distribution-track span { background: #65a67b; }
                .level-6 .level-distribution-track span { background: #458c70; }
                .level-7 .level-distribution-track span { background: #286d54; }
                .level-distribution-empty { margin: 0; padding: 28px 0; color: var(--text-light); text-align: center; }
                .workflow-modal-backdrop { position: fixed; inset: 0; z-index: 1600; display: grid; place-items: center; padding: 20px; background: rgba(20, 45, 31, 0.42); backdrop-filter: blur(4px); }
                .workflow-modal { width: min(620px, 100%); max-height: min(720px, calc(100vh - 40px)); overflow: auto; border-radius: 18px; background: #fff; box-shadow: 0 24px 60px rgba(15, 44, 27, 0.28); }
                .workflow-modal-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 22px; border-bottom: 1px solid #e4ece6; background: #f8fbf8; }
                .workflow-modal-header h2 { margin: 0; color: var(--text-dark); font-size: 1.2rem; }
                .workflow-modal-close { display: grid; place-items: center; flex: 0 0 34px; width: 34px; height: 34px; border: 0; border-radius: 50%; background: #e7f1e8; color: #315f42; font-size: 1.45rem; line-height: 1; cursor: pointer; }
                .workflow-modal-close:hover { background: #d3e7d6; }
                .workflow-modal-content { padding: 22px; }
                .workflow-modal:has(.roster-scan-modal) { width: min(1180px, 100%); max-height: calc(100vh - 28px); }
                .roster-scan-modal { display: grid; gap: 16px; }
                .roster-scan-intro { margin: 0; color: #5d6e63; line-height: 1.5; }
                .roster-drop-zone { display: grid; gap: 14px; place-items: center; min-height: 220px; padding: 20px; border: 2px dashed #b8d4bf; border-radius: 13px; background: #f7fbf8; color: #587062; text-align: center; }
                .roster-drop-zone img { display: block; max-width: 100%; max-height: 260px; border-radius: 8px; object-fit: contain; box-shadow: 0 3px 12px rgba(20, 65, 35, .13); }
                .roster-upload-actions, .roster-review-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
                .roster-scan-loading { display: grid; justify-items: center; gap: 12px; padding: 56px 12px; color: #3d6650; text-align: center; }
                .roster-scan-loading .spinner { width: 32px; height: 32px; }
                .roster-scan-loading span:last-child { color: #728279; font-size: .87rem; }
                .roster-scan-error { margin: 0; padding: 10px 12px; border-radius: 9px; background: #fff2f0; color: #a43b31; font-size: .9rem; }
                .roster-review-layout { display: grid; grid-template-columns: minmax(180px, .6fr) minmax(0, 1.4fr); gap: 16px; align-items: start; }
                .roster-image-pane { position: sticky; top: 0; max-height: 520px; overflow: auto; padding: 8px; border: 1px solid #dfe9e1; border-radius: 10px; background: #f8fbf8; }
                .roster-image-pane img { display: block; width: 100%; cursor: zoom-in; }
                .roster-grid-wrap { overflow: auto; border: 1px solid #dfe9e1; border-radius: 10px; }
                .roster-review-grid { width: 100%; min-width: 650px; border-collapse: collapse; font-size: .82rem; }
                .roster-review-grid th { padding: 10px 8px; background: #f1f7f2; color: #466451; text-align: left; white-space: nowrap; }
                .roster-review-grid td { padding: 7px; border-top: 1px solid #e5ece7; vertical-align: middle; }
                .roster-review-grid tr.needs-review { background: #fff9ea; }
                .roster-review-grid input[type=text], .roster-review-grid td input:not([type]) { width: 100%; }
                .roster-review-grid td input:not([type]), .roster-review-grid td input[type=text] { min-width: 96px; padding: 7px; border: 1px solid #cedbd1; border-radius: 6px; }
                .roster-review-grid td span { display: grid; gap: 3px; min-width: 105px; color: #42564a; }
                .roster-review-grid small { color: #9b650a; font-weight: 700; }
                .roster-delete { width: 28px; height: 28px; border: 0; border-radius: 50%; background: #fff0ee; color: #b64437; font-size: 1.25rem; cursor: pointer; }
                .roster-review-actions { justify-content: space-between; }
                @media (max-width: 720px) { .roster-review-layout { grid-template-columns: 1fr; } .roster-image-pane { position: static; max-height: 250px; } .workflow-modal:has(.roster-scan-modal) { max-height: calc(100vh - 18px); } }
                .import-result-modal { text-align: center; }
                .success-confirmation-modal { text-align: center; }
                .success-confirmation-modal p { margin: 0; color: #5d6e63; line-height: 1.55; }
                .voice-marking-modal { display: grid; gap: 16px; }
                .voice-marking-modal > p { margin: 0; color: #5d6e63; line-height: 1.5; }
                .voice-listen-button { justify-self: start; min-width: 180px; }
                .voice-listen-button.is-listening { background: #b73d46; animation: voice-pulse 1.2s infinite; }
                .voice-transcript, .voice-status { margin: 0; padding: 11px 12px; border-radius: 9px; background: #f6faf7; color: #486052; font-size: 0.9rem; line-height: 1.45; }
                .voice-status { background: #fff9ec; color: #745721; }
                .voice-candidate { display: grid; gap: 7px; padding: 16px; border: 1px solid #bde0c5; border-radius: 12px; background: #f2fbf4; color: #28633a; }
                .voice-candidate span { font-size: 0.78rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
                .voice-candidate strong { font-size: 1.05rem; }
                .voice-candidate .btn { justify-self: start; margin-top: 4px; }
                .learner-levels-modal { display: grid; gap: 16px; }.learner-levels-modal > p { margin: 0; color: #5d6e63; line-height: 1.5; }.learner-levels-modal__summary { display: flex; align-items: baseline; gap: 7px; padding: 10px 12px; border-radius: 10px; background: #f3f8f3; color: #486052; }.learner-levels-modal__summary strong { color: #24683a; font-size: 1.25rem; }.learner-levels-export { justify-self: start; width: auto; }
                @keyframes voice-pulse { 50% { box-shadow: 0 0 0 7px rgba(183,61,70,.15); } }
                .completion-celebration { position: fixed; inset: 0; z-index: 1700; pointer-events: none; overflow: hidden; }
                .completion-celebration-message { position: absolute; top: 18%; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 9px; padding: 12px 18px; border: 1px solid #c8e5cf; border-radius: 999px; background: rgba(255,255,255,0.96); box-shadow: 0 10px 28px rgba(30, 103, 57, 0.22); color: #22673a; font-weight: 800; white-space: nowrap; animation: celebration-message 4.2s ease both; }
                .completion-confetti { position: absolute; top: -16px; left: var(--left); width: 10px; height: 16px; border-radius: 2px; background: var(--colour); transform: rotate(var(--rotate)); animation: confetti-fall 3.5s cubic-bezier(.2,.7,.3,1) var(--delay) both; }
                @keyframes confetti-fall { 0% { opacity: 1; transform: translate(0, -20px) rotate(var(--rotate)); } 100% { opacity: 0; transform: translate(var(--drift), 110vh) rotate(calc(var(--rotate) + 720deg)); } }
                @keyframes celebration-message { 0%, 100% { opacity: 0; transform: translate(-50%, -12px) scale(.94); } 12%, 82% { opacity: 1; transform: translate(-50%, 0) scale(1); } }
                .import-result-icon { display: grid; place-items: center; width: 48px; height: 48px; margin: 0 auto 12px; border-radius: 50%; background: #e7f6ea; color: #287a42; }
                .import-result-summary { margin: 0 auto 20px; max-width: 440px; color: #5d6e63; line-height: 1.5; }
                .import-result-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; text-align: left; }
                .import-result-grid > div { display: grid; gap: 2px; padding: 12px; border: 1px solid #e2ece4; border-radius: 10px; background: #f9fcf9; }
                .import-result-grid strong { color: #24683a; font-size: 1.25rem; }
                .import-result-grid span { color: #64746a; font-size: 0.78rem; font-weight: 600; }
                .import-result-grid .import-result-warning { border-color: #f1c982; background: #fff9ec; }
                .import-result-grid .import-result-warning strong { color: #a65d00; }
                .import-result-note { margin: 14px 0 0; padding: 10px 12px; border-radius: 9px; background: #fff7e7; color: #875a13; font-size: 0.86rem; line-height: 1.45; }
                .import-result-actions { justify-content: center; margin-top: 20px; }
                @media (max-width: 420px) { .import-result-grid { grid-template-columns: 1fr; } }
                .management-grid .workflow-open-btn {
                    width: auto !important;
                    min-width: 76px;
                    height: 36px !important;
                    min-height: 36px;
                    flex: 0 0 auto;
                    align-self: center;
                    margin-left: 12px !important;
                    padding: 0.4rem 0.72rem !important;
                    border-radius: 999px;
                    background: #eff7f0;
                    border-color: #c7dec9;
                    color: #286542;
                    font-size: 0.78rem !important;
                    font-weight: 700;
                    letter-spacing: 0.01em;
                    white-space: nowrap;
                }
                .management-grid .workflow-open-btn:hover { background: #dcefe0; border-color: #a9ceaf; }
                .management-grid {
                    align-items: stretch;
                    gap: 24px;
                    margin-top: 8px;
                }
                .management-grid > .modern-card:nth-child(-n + 3) {
                    display: flex;
                    flex-direction: column;
                    min-height: 188px;
                    padding: 24px 26px;
                }
                .management-grid > .modern-card:nth-child(-n + 3) > div:first-child {
                    margin-bottom: 14px;
                }
                .management-grid > .modern-card:nth-child(1) > div:first-child,
                .management-grid > .modern-card:nth-child(2) > div:first-child {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }
                .management-grid > .modern-card:nth-child(3) > div:first-child {
                    grid-template-columns: minmax(0, 1fr) auto auto !important;
                }
                .management-grid > .modern-card:nth-child(-n + 3) .card-subtitle {
                    margin: 0;
                    max-width: 58ch;
                    line-height: 1.55;
                }
                .management-grid > .modern-card:nth-child(1) .card-subtitle strong {
                    display: block;
                    margin: 8px 0 5px;
                    color: #315f42;
                    font-size: 0.86rem;
                }
                .management-grid > .modern-card:nth-child(-n + 3) .workflow-open-btn {
                    width: auto !important;
                    min-width: 84px;
                    height: 36px !important;
                    min-height: 36px;
                    flex: 0 0 auto;
                    align-self: center;
                    margin: 0 !important;
                    padding: 0.4rem 0.78rem !important;
                    border-radius: 999px;
                    background: #eff7f0;
                    border-color: #c7dec9;
                    color: #286542;
                    font-size: 0.78rem !important;
                    font-weight: 700;
                    letter-spacing: 0.01em;
                    line-height: 1;
                    white-space: nowrap;
                }
                .management-grid > .modern-card:nth-child(-n + 3) .workflow-open-btn:hover { background: #dcefe0; border-color: #a9ceaf; }
                /* Compact launcher cards and keep modal layout independent from card rules. */
                .management-grid { gap: 16px; margin-top: 0; }
                .management-grid > .modern-card:nth-child(-n + 3) { min-height: 0; padding: 17px 18px; }
                .management-grid > .modern-card:nth-child(-n + 3) > div:first-child { margin-bottom: 9px; }
                .management-grid > .modern-card:nth-child(-n + 3) .card-title { padding-bottom: 0.3rem !important; font-size: 1rem; }
                .management-grid > .modern-card:nth-child(-n + 3) .card-subtitle { font-size: 0.84rem; line-height: 1.42; }
                .management-grid > .modern-card:nth-child(1) .card-subtitle strong { margin: 5px 0 3px; font-size: 0.8rem; }
                .management-grid > .modern-card:not(.setup-card) .btn.btn-outline.workflow-open-btn[aria-label] {
                    width: auto !important;
                    min-width: 78px !important;
                    height: 32px !important;
                    min-height: 32px !important;
                    margin: 0 !important;
                    padding: 0.34rem 0.62rem !important;
                    border-radius: 999px;
                    background: #eff7f0 !important;
                    border-color: #c7dec9 !important;
                    color: #286542 !important;
                    font-size: 0.74rem !important;
                    line-height: 1 !important;
                }
                .workflow-modal-backdrop {
                    position: fixed !important;
                    inset: 0 !important;
                    display: grid !important;
                    grid-template-columns: none !important;
                    place-items: center !important;
                    margin: 0 !important;
                    padding: 20px !important;
                    z-index: 2000 !important;
                }
                /* Keep the three launch cards simple: title and status above, action in one predictable place. */
                .management-grid > .modern-card:nth-child(-n + 3) {
                    min-height: 164px;
                    padding: 18px 20px 58px;
                }
                .management-grid > .modern-card:nth-child(1) > div:first-child,
                .management-grid > .modern-card:nth-child(2) > div:first-child {
                    grid-template-columns: minmax(0, 1fr) !important;
                }
                .management-grid > .modern-card:nth-child(3) > div:first-child {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                    padding-right: 0;
                }
                .management-grid > .modern-card:nth-child(-n + 3) .workflow-open-btn {
                    position: absolute;
                    right: 20px;
                    bottom: 18px;
                    min-width: 82px !important;
                    z-index: 1;
                }
                .management-grid > .modern-card:nth-child(-n + 3) .card-subtitle {
                    max-width: none;
                    font-size: 0.86rem;
                    line-height: 1.45;
                }
                .management-grid > .modern-card:nth-child(1) .card-subtitle strong {
                    display: inline;
                    margin: 0;
                    font-size: inherit;
                }
                /* The roster scanner added a fourth quick-action card. Keep
                   the full launcher row fixed and prevent the pass-requirement
                   header from collapsing into a one-character column. */
                .management-grid {
                    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
                    grid-auto-rows: auto;
                    align-items: stretch;
                }
                .management-grid > .modern-card:nth-child(-n + 4) {
                    box-sizing: border-box;
                    min-height: 138px;
                    height: auto;
                    padding: 14px 16px 48px;
                }
                .management-grid > .modern-card:nth-child(4) > div:first-child {
                    display: grid !important;
                    grid-template-columns: minmax(0, 1fr) auto !important;
                    align-items: start !important;
                    gap: 10px !important;
                }
                .management-grid > .modern-card:nth-child(4) > div:first-child > div {
                    min-width: 0;
                    grid-column: 1 / -1;
                }
                .management-grid > .modern-card:nth-child(4) .card-title {
                    overflow-wrap: normal;
                    word-break: normal;
                }
                .management-grid > .modern-card:nth-child(4) .workflow-open-btn {
                    position: static;
                    min-width: 82px !important;
                }
                
                .loading-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: var(--text-light); }
                .spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid var(--primary-green); border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @media print { .no-print { display: none !important; } .subject-detail-wrapper { margin: 0; padding: 0; } }

                /* --- MOBILE RESPONSIVE FIXES --- */
                @media screen and (max-width: 768px) {
                     .subject-detail-wrapper {
                          margin-left: 0;
                          padding: var(--shell-top-offset-mobile) 10px 10px;
                          width: 100%;
                      }
                    .page-header {
                        grid-template-columns: 1fr;
                        gap: 1rem;
                        padding: 1rem;
                    }
                    .page-header h1 {
                        font-size: 1.2rem;
                        display: flex;
                        flex-direction: column;
                        gap: 5px;
                    }
                    .page-header > div { width: 100%; }
                    .badge { margin-left: 0; align-self: flex-start; }
                    .header-side { justify-items: stretch; }
                    .page-actions { justify-content: stretch; }
                    .page-actions > button { width: 100%; }
                    .term-tabs {
                        width: 100%;
                        overflow-x: auto;
                        padding-bottom: 5px;
                        justify-content: flex-start;
                    }
                    .term-tab {
                        white-space: nowrap;
                        flex-shrink: 0;
                        padding: 6px 16px;
                    }
                    .management-grid {
                        grid-template-columns: 1fr;
                    }
                    .management-grid > .modern-card:nth-child(3) > div:first-child {
                        grid-template-columns: minmax(0, 1fr) 38px;
                    }
                    .management-grid > .modern-card:nth-child(3) .meta-chip {
                        grid-column: 1;
                        grid-row: 2;
                        justify-self: start;
                    }
                    .management-grid > .add-student-card > div:nth-of-type(2) {
                        grid-template-columns: 1fr;
                    }
                    .section-form-grid {
                        grid-template-columns: 1fr;
                    }
                    .section-form-grid .stacked-field,
                    .section-form-grid .btn {
                        grid-column: auto;
                    }
                    .setup-card .assessment-group-header,
                    .table-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .setup-card .assessment-group-header,
                    .setup-card .assessment-inline-editors,
                    .setup-card .section-row {
                        grid-template-columns: 1fr;
                    }
                    .table-toolbar-actions { width: max-content; justify-content: flex-start; }
                    .section-row {
                        grid-template-columns: 1fr;
                    }
                   
                    .section-row .btn-danger {
                        justify-self: stretch;
                    }
                    .level-distribution-section { padding: 18px; }
                    .level-distribution-heading { flex-direction: column; gap: 8px; }
                    .level-distribution-chart { gap: 7px; padding: 4px 0 0; min-height: 200px; }
                    .level-distribution-bar { min-width: 26px; height: 190px; font-size: 0.68rem; }
                    .level-distribution-track { height: 130px; }
                    .page-header button {
                        width: 100%;
                        margin-bottom: 5px;
                    }
                    form[style*="display: flex"] {
                        flex-direction: column;
                        align-items: stretch !important;
                    }
                    form[style*="display: flex"] input,
                    form[style*="display: flex"] button {
                        width: 100% !important;
                        flex: none !important;
                        margin-bottom: 5px;
                    }
                    .table-container {
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin-bottom: 1rem;
                    }
                    .modern-table {
                        min-width: 600px;
                    }
                    .analytics-grid {
                        grid-template-columns: 1fr;
                    }
                    /* Ensure compact notes card is full width on mobile */
                    .compact-notes-card { width: 100%; }
                    .compact-notes-card .compact-notes-form { flex-direction: column; }
                    
                    .compact-notes-card .compact-notes-form, .compact-notes-card .compact-notes-form * { min-width: 0; }
                    .compact-notes-card input[type="datetime-local"] { -webkit-appearance: textfield; }
                    /* smaller breakpoint adjustments */
                    @media screen and (max-width: 480px) {
                        .compact-notes-card { padding: 12px; }
                        .compact-notes-card .compact-add-btn { padding: 10px 12px; }
                        .compact-notes-card .compact-datetime { font-size: 0.9rem; }
                    }
                }
            `}</style>

      {/* Header */}
      <div className="page-header">
        <div className="subject-hero">
          <h1>
            {subjectname}{" "}
            <span className="badge">
              {subjectGrade} {subjectClass}
            </span>
          </h1>

          <div className="header-meta">
            <span className="meta-chip">Term {currentTerm}</span>
            <span className="meta-chip">
              {students.length} Learner{students.length === 1 ? "" : "s"}
            </span>
            <span className="meta-chip">
              {sections.length} Section{sections.length === 1 ? "" : "s"}
            </span>
            <span className="meta-chip">
              {assessmentGroups.length} Assessment
              {assessmentGroups.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <div className="header-side">
          <div className="term-tabs no-print">
            {[1, 2, 3, 4].map((term) => (
              <button
                key={term}
                className={`term-tab ${currentTerm === term ? "active" : ""}`}
                onClick={() => setCurrentTerm(term)}
              >
                Term {term}
              </button>
            ))}
          </div>

          <div className="page-actions">
            <TourButton onClick={subjectTour.start} />
            <button
              className="btn btn-danger no-print"
              onClick={handleDeleteAll}
            >
              <Icons.Trash /> Reset All Marks to 0
            </button>
          </div>
        </div>
      </div>

      <div className="section-block no-print">
        <div className="management-grid">
          {/* CARD 1: File Operations (Collapsible) */}
          <div className="modern-card" data-tour="import">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 className="card-title" style={{ margin: 0 }}>
                Import Marks (Bulk) (Term {currentTerm})
              </h3>
              <button
                type="button"
                className="btn btn-outline workflow-open-btn"
                style={{
                  fontSize: "1.1rem",
                  padding: "2px 10px",
                  width: 40,
                  height: "32px",
                  marginLeft: 8,
                }}
                onClick={() => setImportExportCollapsed(false)}
                aria-label="Open Import Marks"
              >
                Open →
              </button>
            </div>
            <p className="card-subtitle">
              Import an Excel file with{" "}
              <strong>name, surname and assessment</strong> columns. MarkSure
              calculates totals automatically; use the template as your guide.
            </p>
            {!importExportCollapsed && (
              <WorkflowModal
                title={`Import marks — Term ${currentTerm}`}
                onClose={() => setImportExportCollapsed(true)}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    marginTop: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "10px",
                    }}
                  >
                    <button
                      className="btn btn-outline"
                      onClick={handleDownloadTemplate}
                    >
                      Download Excel Template
                    </button>
                  </div>
                  <div
                    style={{
                      background: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px dashed #ced4da",
                      textAlign: "center",
                    }}
                  >
                    {csvUploading ? (
                      <div
                        style={{
                          color: "var(--primary-green)",
                          fontWeight: "600",
                        }}
                      >
                        <div
                          className="spinner"
                          style={{
                            width: "20px",
                            height: "20px",
                            display: "inline-block",
                            verticalAlign: "middle",
                            marginRight: "8px",
                            marginBottom: 0,
                          }}
                        ></div>
                        {csvProgress}
                      </div>
                    ) : (
                      <>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "0.85rem",
                            color: "#666",
                          }}
                        >
                          Upload Data for Term {currentTerm}
                        </label>
                        <input
                          type="file"
                          accept=".xlsx, .xls, .csv"
                          onChange={handleExcelUpload}
                          className="modern-input"
                        />
                        <a
                          href="/assets/marksure-add-assessment-classes.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-block",
                            marginTop: "10px",
                            fontSize: "0.85rem",
                            color: "var(--dark-green)",
                            fontWeight: "600",
                            textDecoration: "none",
                          }}
                        >
                          Need help? Open Excel import guide (PDF)
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </WorkflowModal>
            )}
          </div>

          {/* CARD 2: Add Student (Collapsible) */}
          <div className="modern-card add-student-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 className="card-title" style={{ margin: 0 }}>
                Add Student (Manual)
              </h3>
              <button
                type="button"
                className="btn btn-outline workflow-open-btn"
                style={{
                  fontSize: "1.1rem",
                  padding: "2px 10px",
                  width: 40,
                  height: "32px",
                  marginLeft: 8,
                }}
                onClick={() => setAddStudentCollapsed(false)}
                aria-label="Open Add Learner"
              >
                Open →
              </button>
            </div>
            <p className="card-subtitle">
              Add a learner straight into this subject when they join mid-term
              or were missed during import.
            </p>
            {!addStudentCollapsed && (
              <WorkflowModal
                title="Add learner"
                onClose={() => setAddStudentCollapsed(true)}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    marginTop: "20px",
                  }}
                >
                  <input
                    className="modern-input"
                    placeholder="First Name"
                    value={newStudent.firstName}
                    onChange={(e) =>
                      setNewStudent({
                        ...newStudent,
                        firstName: e.target.value,
                      })
                    }
                  />
                  <input
                    className="modern-input"
                    placeholder="Last Name"
                    value={newStudent.lastName}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, lastName: e.target.value })
                    }
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleAddStudent}
                  >
                    + Add Student
                  </button>
                </div>
              </WorkflowModal>
            )}
          </div>

          <div className="modern-card roster-scan-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 className="card-title" style={{ margin: 0 }}>
                Scan / Upload Classlist
              </h3>
              <button
                type="button"
                className="btn btn-outline workflow-open-btn"
                onClick={() => setShowRosterScan(true)}
                aria-label="Open Scan / Upload Classlist"
              >
                Scan list
              </button>
            </div>
            <p className="card-subtitle">
              Photograph a handwritten or printed list, verify the names, then
              add the selected learners in one step.
            </p>
          </div>

          <div className="modern-card" data-tour="pass">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <div>
                <h3 className="card-title" style={{ margin: 0 }}>
                  Pass Requirements
                </h3>
                <p className="card-subtitle" style={{ marginTop: "6px" }}>
                  Keep the current defaults or override them for this subject
                  only.
                </p>
              </div>
              <span className="meta-chip" style={{ whiteSpace: "nowrap" }}>
                {activePassRequirements.has_override
                  ? "Override active"
                  : "Using default"}
              </span>
              <button
                type="button"
                className="btn btn-outline workflow-open-btn"
                style={{
                  fontSize: "1.1rem",
                  padding: "2px 10px",
                  width: 40,
                  height: "32px",
                }}
                onClick={() => setPassRequirementsCollapsed(false)}
                aria-label="Open Pass Requirements"
              >
                Open →
              </button>
            </div>

            {!passRequirementsCollapsed && (
              <WorkflowModal
                title="Pass requirements"
                onClose={() => setPassRequirementsCollapsed(true)}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: "12px",
                    marginTop: "14px",
                  }}
                >
                  <div className="stacked-field">
                    <label htmlFor="subject-pass-mark">Pass Mark %</label>
                    <input
                      id="subject-pass-mark"
                      type="number"
                      min="0"
                      max="100"
                      className="modern-input"
                      value={subjectPassRequirements?.pass_mark_override ?? ""}
                      onChange={(e) =>
                        handlePassRequirementFieldChange(
                          "pass_mark_override",
                          e.target.value,
                        )
                      }
                      placeholder={`${activePassRequirements.default_pass_mark}`}
                    />
                    <small className="field-hint text-muted">
                      Default: {activePassRequirements.default_pass_mark}%
                    </small>
                  </div>

                  <div className="stacked-field">
                    <label htmlFor="subject-support-threshold">
                      Support Threshold %
                    </label>
                    <input
                      id="subject-support-threshold"
                      type="number"
                      min="0"
                      max="100"
                      className="modern-input"
                      value={
                        subjectPassRequirements?.support_threshold_override ??
                        ""
                      }
                      onChange={(e) =>
                        handlePassRequirementFieldChange(
                          "support_threshold_override",
                          e.target.value,
                        )
                      }
                      placeholder={`${activePassRequirements.default_support_threshold}`}
                    />
                    <small className="field-hint text-muted">
                      Default:{" "}
                      {activePassRequirements.default_support_threshold}%
                    </small>
                  </div>

                  <div className="stacked-field">
                    <label htmlFor="subject-distinction-threshold">
                      Distinction Threshold %
                    </label>
                    <input
                      id="subject-distinction-threshold"
                      type="number"
                      min="0"
                      max="100"
                      className="modern-input"
                      value={
                        subjectPassRequirements?.distinction_threshold_override ??
                        ""
                      }
                      onChange={(e) =>
                        handlePassRequirementFieldChange(
                          "distinction_threshold_override",
                          e.target.value,
                        )
                      }
                      placeholder={`${activePassRequirements.default_distinction_threshold}`}
                    />
                    <small className="field-hint text-muted">
                      Default:{" "}
                      {activePassRequirements.default_distinction_threshold}%
                    </small>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    background: "#f8fbf7",
                    border: "1px solid #e0e8dc",
                    color: "#4f6355",
                  }}
                >
                  Active for this subject: pass at{" "}
                  <strong>{activePassRequirements.pass_mark}%</strong>, support
                  below or equal to{" "}
                  <strong>{activePassRequirements.support_threshold}%</strong>,
                  distinction at{" "}
                  <strong>
                    {activePassRequirements.distinction_threshold}%
                  </strong>{" "}
                  and above.
                </div>

                <div className="form-actions mt-3">
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{
                      padding: "7px 11px",
                      fontSize: "0.82rem",
                      minHeight: "auto",
                    }}
                    onClick={handleResetPassRequirements}
                  >
                    Reset To Default
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSavePassRequirements}
                    disabled={savingPassRequirements}
                  >
                    {savingPassRequirements
                      ? "Saving..."
                      : "Save Pass Requirements"}
                  </button>
                </div>
              </WorkflowModal>
            )}
          </div>

          {/* CARD 3: Manage Sections (Dynamic per Term) */}
          <div
            className="modern-card setup-card"
            style={{ gridColumn: "1 / -1" }}
          >
            <div className="setup-card-header">
              <div className="setup-card-title-group">
                <div className="setup-card-title-row">
                  <h3 className="setup-card-title">Assessment Sections</h3>
                  <span className="setup-term-badge">Term {currentTerm}</span>
                  {sections.length > 0 && (
                    <span className="setup-sections-count">
                      {sections.length}{" "}
                      {sections.length === 1 ? "section" : "sections"}
                    </span>
                  )}
                </div>
                <p className="setup-card-subtitle">
                  Define the assessment structure, mark allocations, and weights for this term.
                </p>
              </div>

              <div className="setup-card-header-actions">
                <TourButton
                  onClick={() => {
                    setSectionsCollapsed(false);
                    assessmentTour.start();
                  }}
                />
                {sections.length > 0 && (
                  <button
                    type="button"
                    className="setup-copy-btn"
                    onClick={handleOpenCopyModal}
                    title="Copy sections structure to another term"
                  >
                    <Icons.Copy />
                    <span>Copy to another Term</span>
                  </button>
                )}
                <button
                  type="button"
                  className="setup-collapse-btn"
                  onClick={() => setSectionsCollapsed((v) => !v)}
                  aria-label={
                    sectionsCollapsed ? "Expand Sections" : "Collapse Sections"
                  }
                  title={sectionsCollapsed ? "Expand sections" : "Collapse sections"}
                >
                  <Icons.ChevronDown
                    className={`setup-chevron ${sectionsCollapsed ? "collapsed" : ""}`}
                  />
                </button>
              </div>
            </div>

            {/* Add Section Bar */}
            {!sectionsCollapsed && (
              <>
                <div className="assessment-stepper-guidance">
                  <div className="step-item">
                    <span className="step-circle">1</span>
                    <div className="step-content">
                      <span className="step-label">Name Assessment</span>
                      <small className="step-desc">e.g. Test 1 or Project</small>
                    </div>
                  </div>

                  <div className="step-connector"></div>

                  <div className="step-item">
                    <span className="step-circle">2</span>
                    <div className="step-content">
                      <span className="step-label">Set Weight</span>
                      <small className="step-desc">Term mark weighting</small>
                    </div>
                  </div>

                  <div className="step-connector"></div>

                  <div className="step-item">
                    <span className="step-circle">3</span>
                    <div className="step-content">
                      <span className="step-label">Add Sections</span>
                      <small className="step-desc">Questions & max marks</small>
                    </div>
                  </div>
                </div>

                {isFoundationSubject && (
                  <div
                    className="assessment-form-container"
                    style={{
                      padding: "16px 18px",
                      background: "#f8fbf5",
                      border: "1px solid #dfe7d8",
                      borderRadius: "12px",
                      marginBottom: "18px",
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#3c5a42",
                      }}
                    >
                      Foundation Phase template active
                    </strong>
                    <p style={{ margin: 0, color: "#55655d" }}>
                      This subject started from the Foundation Phase CAPS
                      template. Teachers can now adjust sections for this
                      subject here, while the CAPS component weighting stays
                      controlled separately.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/my-assessment-plan")}
                      style={{
                        marginTop: "10px",
                        padding: "7px 14px",
                        border: "1px solid #c6d6c8",
                        borderRadius: "999px",
                        background: "#ffffff",
                        color: "#24462d",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Change weights and tasks in My Assessment Plan
                    </button>
                  </div>
                )}

                {!isFoundationSubject && (
                  <SectionSetupHelper
                    subjectName={subjectname}
                    grade={subjectGrade}
                    categories={SECTION_CATEGORIES}
                    existingGroups={assessmentGroups.map((group) => ({
                      name: normalizeGroupText(group.primaryGroupName),
                      weight: group.currentWeight,
                    }))}
                    onCreate={handleCreateSections}
                  />
                )}

                <form
                  onSubmit={handleAddSection}
                  className="assessment-creation-card"
                >
                  <div className="creation-card-header">
                    <div className="creation-card-title">
                      <span className="creation-icon" aria-hidden="true">
                        <Icons.Plus />
                      </span>
                      <div>
                        <strong>Add Assessment Section</strong>
                        <p>
                          Specify the assessment name, section details, and maximum mark.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="creation-form-grid">
                    {/* Assessment Name */}
                    <div className="stacked-field field-assessment-name" data-tour="assessment-name">
                      <div className="field-label-row">
                        <label htmlFor="new-assessment-name">
                          {isFoundationSubject
                            ? "CAPS Area"
                            : "Assessment / Task Name"}
                        </label>
                        <HelpTip label="About the assessment name">
                          Sections with the same assessment name and category are grouped
                          together, for example all the questions of Term 1 Test.
                        </HelpTip>
                      </div>
                      {isFoundationSubject ? (
                        <select
                          id="new-assessment-name"
                          className="modern-input"
                          value={newSection.assessment_group}
                          onChange={(e) =>
                            setNewSection({
                              ...newSection,
                              assessment_group: e.target.value,
                              category: e.target.value,
                            })
                          }
                        >
                          {foundationAssessmentGroupOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id="new-assessment-name"
                          className="modern-input"
                          placeholder="e.g. Term 1 Test"
                          value={newSection.assessment_group}
                          onChange={(e) =>
                            setNewSection({
                              ...newSection,
                              assessment_group: e.target.value,
                            })
                          }
                        />
                      )}
                    </div>

                    {/* Section Name */}
                    <div className="stacked-field field-section-name" data-tour="section-name">
                      <label htmlFor="new-section-name">
                        Section / Question Name
                      </label>
                      <input
                        id="new-section-name"
                        className="modern-input"
                        placeholder="e.g. Question 1 or Language Structures"
                        value={newSection.section_name}
                        onChange={(e) =>
                          setNewSection({
                            ...newSection,
                            section_name: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Max Mark */}
                    <div className="stacked-field field-max-mark" data-tour="max-mark">
                      <label htmlFor="new-section-max">Max Mark</label>
                      <input
                        id="new-section-max"
                        type="number"
                        className="modern-input"
                        placeholder="100"
                        value={newSection.max_mark}
                        onChange={(e) =>
                          setNewSection({
                            ...newSection,
                            max_mark: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Overall Weight */}
                    {!isFoundationSubject && (
                      <div className="stacked-field field-weight" data-tour="weight">
                        <div className="field-label-row">
                          <label htmlFor="new-assessment-weight">
                            Overall Weight
                          </label>
                          <HelpTip label="About the overall weight" align="end">
                            How much this assessment counts towards the term mark. Every section
                            of the same assessment shares this weight.
                          </HelpTip>
                        </div>
                        <input
                          id="new-assessment-weight"
                          type="number"
                          className="modern-input"
                          placeholder="1"
                          value={
                            matchingAssessmentGroup
                              ? matchingAssessmentGroup.currentWeight
                              : newSection.weight
                          }
                          disabled={!!matchingAssessmentGroup}
                          onChange={(e) =>
                            setNewSection({
                              ...newSection,
                              weight: e.target.value,
                            })
                          }
                        />
                        {matchingAssessmentGroup && (
                          <small className="field-hint text-muted">
                            Locked to group
                          </small>
                        )}
                      </div>
                    )}

                    {/* Category */}
                    <div
                      className={`stacked-field field-category ${isFoundationSubject ? "full-span" : ""}`}
                      data-tour="category"
                    >
                      <label htmlFor="new-section-category">
                        {isFoundationSubject
                          ? "Linked CAPS Area"
                          : "Category"}
                      </label>
                      {isFoundationSubject ? (
                        <input
                          id="new-section-category"
                          className="modern-input"
                          value={newSection.assessment_group}
                          disabled
                        />
                      ) : (
                        <select
                          id="new-section-category"
                          className="modern-input"
                          value={newSection.category}
                          onChange={(e) =>
                            setNewSection({
                              ...newSection,
                              category: e.target.value,
                            })
                          }
                        >
                          {SECTION_CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                              {c || "Select Category"}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="creation-footer">
                    <div className="creation-tip">
                      <Icons.Info />
                      <span>
                        <strong>Tip:</strong> Sections sharing the same assessment name and category are grouped together (e.g. Test 1).
                      </span>
                    </div>

                    <button
                      type="submit"
                      className="btn-add-section"
                      data-tour="add-section"
                      disabled={addingSection}
                    >
                      <Icons.Plus />
                      <span>{addingSection ? "Adding Section..." : "Add Section"}</span>
                    </button>
                  </div>
                </form>
                <FeatureTour steps={ASSESSMENT_TOUR} open={assessmentTour.open && !subjectTour.open} onClose={assessmentTour.close} />
              </>
            )}

            {/* Section List */}
            <div className="assessment-group-list">
              {!sectionsCollapsed && (
                <>
                  {assessmentGroups.length === 0 && (
                    <p style={{ color: "#999", padding: "10px" }}>
                      No sections for Term {currentTerm} yet.
                    </p>
                  )}
                  {assessmentGroups.map((group) => {
                    const managedWeight =
                      foundationWeightsByLabel[
                        String(group.label || "").toLowerCase()
                      ];
                    const displayWeight = isFoundationSubject
                      ? String(managedWeight ?? group.currentWeight)
                      : (assessmentWeightDrafts[group.key] ??
                        String(group.currentWeight));
                    const assessmentNameValue =
                      assessmentNameDrafts[group.key] ?? group.primaryGroupName;

                    return (
                      <div key={group.key} className="assessment-group-card">
                        <div className="assessment-group-header">
                          <div className="assessment-group-title">
                            <div className="group-title-row">
                              <span className="group-icon-avatar" aria-hidden="true">
                                <Icons.Clipboard />
                              </span>
                              <h4>{group.label}</h4>
                            </div>
                            <div className="assessment-group-meta">
                              <span className="assessment-chip">
                                <Icons.Layers />
                                <span>
                                  <strong>{group.sections.length}</strong>{" "}
                                  {group.sections.length === 1
                                    ? "section"
                                    : "sections"}
                                </span>
                              </span>
                              <span className="assessment-chip">
                                <span>
                                  Raw max <strong>{group.rawMax || 0}</strong>
                                </span>
                              </span>
                              <span className="assessment-chip weight-chip">
                                <span>
                                  {isFoundationSubject
                                    ? "CAPS weight"
                                    : "Weight"}{" "}
                                  <strong>{displayWeight}</strong>
                                </span>
                              </span>
                            </div>
                          </div>

                          <div className="assessment-inline-editors">
                            <div className="assessment-inline-editor">
                              <label htmlFor={`assessment-name-${group.key}`}>
                                Assessment Name
                              </label>
                              <input
                                id={`assessment-name-${group.key}`}
                                className="modern-input"
                                value={assessmentNameValue}
                                disabled={
                                  isFoundationSubject ||
                                  savingAssessmentNameKey === group.key
                                }
                                onChange={(e) =>
                                  setAssessmentNameDrafts((prev) => ({
                                    ...prev,
                                    [group.key]: e.target.value,
                                  }))
                                }
                                onBlur={() => {
                                  if (!isFoundationSubject) {
                                    handleUpdateAssessmentGroupName(group);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                                placeholder="e.g. Term 1 Test"
                              />
                            </div>

                            <div className="assessment-inline-editor">
                              <label htmlFor={`assessment-weight-${group.key}`}>
                                Assessment Weight
                              </label>
                              <input
                                id={`assessment-weight-${group.key}`}
                                className="modern-input"
                                type="number"
                                min="1"
                                step="0.01"
                                value={displayWeight}
                                disabled={
                                  isFoundationSubject ||
                                  savingAssessmentGroupKey === group.key
                                }
                                onChange={(e) =>
                                  setAssessmentWeightDrafts((prev) => ({
                                    ...prev,
                                    [group.key]: e.target.value,
                                  }))
                                }
                                onBlur={() => {
                                  if (!isFoundationSubject) {
                                    handleUpdateAssessmentGroupWeight(group);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                              />
                              <p className="helper-text">
                                {isFoundationSubject
                                  ? "CAPS weighting is controlled by the Foundation Phase template."
                                  : "Weight is how much the task will count towards the end of the term"}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="section-row-list">
                          {group.sections.map((s) => (
                            <div key={s.id} className="section-row">
                              <div
                                className="stacked-field"
                                style={{ minWidth: "200px" }}
                              >
                                <span className="row-field-label">
                                  Section Name
                                </span>
                                <input
                                  className="modern-input"
                                  value={
                                    sectionNameDrafts[s.id] ?? s.section_name
                                  }
                                  onChange={(e) =>
                                    setSectionNameDrafts((prev) => ({
                                      ...prev,
                                      [s.id]: e.target.value,
                                    }))
                                  }
                                  onBlur={() => handleUpdateSectionName(s.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      e.currentTarget.blur();
                                    }
                                  }}
                                />
                              </div>
                              <div className="stacked-field">
                                <span className="row-field-label">Max Mark</span>
                                <input
                                  className="modern-input"
                                  type="number"
                                  value={sectionMaxMarks[s.id] ?? s.max_mark}
                                  onChange={(e) =>
                                    handleUpdateSection(
                                      s.id,
                                      "max_mark",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div className="stacked-field">
                                <span className="row-field-label">
                                  {isFoundationSubject
                                    ? "CAPS Area"
                                    : "Category"}
                                </span>
                                {isFoundationSubject ? (
                                  <select
                                    className="modern-input"
                                    value={
                                      s.assessment_group || s.category || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateSectionGroup(
                                        s.id,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    {foundationAssessmentGroupOptions.map(
                                      (option) => (
                                        <option key={option} value={option}>
                                          {option}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                ) : (
                                  <select
                                    className="modern-input"
                                    value={s.category || ""}
                                    onChange={(e) =>
                                      handleUpdateSectionCategory(
                                        s.id,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    {SECTION_CATEGORIES.map((c) => (
                                      <option key={c} value={c}>
                                        {c || "-- None --"}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn-danger-icon"
                                onClick={() =>
                                  handleDeleteSection(s.id, s.section_name)
                                }
                                title="Delete this section"
                              >
                                <Icons.Trash />
                                <span>Delete</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Copy All Assessments Modal */}
      {showCopyModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(44,62,80,0.18)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              margin: "0 auto",
              width: "100%",
              maxWidth: "500px",
              padding: "24px",
              borderRadius: "14px",
              background: "#fff",
              position: "relative",
              border: "1.5px solid #4CAF50",
              boxSizing: "border-box",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3 style={{ margin: 0 }}>Copy All Assessments To...</h3>
              <button
                onClick={() => setShowCopyModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#999",
                }}
              >
                x
              </button>
            </div>

            <p
              style={{
                color: "#666",
                marginBottom: "16px",
                fontSize: "0.95rem",
              }}
            >
              Select a subject to copy all {sections.length} assessment(s)
              sections from <strong>{subjectname}</strong>{" "}
              {subjectClass && <span>({subjectClass})</span>}.
            </p>

            {otherSubjects.length === 0 ? (
              <p
                style={{ color: "#666", textAlign: "center", padding: "20px" }}
              >
                No other subjects available.
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "8px",
                }}
              >
                {otherSubjects.map((subj) => (
                  <button
                    key={subj.subject_id}
                    onClick={() => {
                      handleCopyAllAssessments(subj.subject_id);
                    }}
                    disabled={copyingSection}
                    style={{
                      padding: "12px 16px",
                      background: "#f5f5f5",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      cursor: copyingSection ? "not-allowed" : "pointer",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      opacity: copyingSection ? 0.6 : 1,
                      transition: "all 0.2s",
                    }}
                  >
                    <strong>{subj.name}</strong>{" "}
                    {subj.class_name && <span>({subj.class_name})</span>}{" "}
                    <span style={{ color: "#999", fontSize: "0.85rem" }}>
                      Grade {subj.grade}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Data Table */}
      <div className="section-block">
        <div className="section-heading">
          <div>
            <p className="section-kicker">Marks Entry</p>
            <h2>Learner marks for Term {currentTerm}</h2>
            {linkedStudent && currentTerm === requestedTerm && (
              <p className="pending-target-note">
                Opened from pending marking. The learner is highlighted and
                outstanding marks are shown in yellow.
              </p>
            )}
          </div>
        </div>
        {loading ? (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Loading Term {currentTerm} data...</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              {/* Top 10 Modal */}
              {showTop10 && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(44,62,80,0.18)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    className="stat_card"
                    style={{
                      margin: 0,
                      maxWidth: "420px",
                      minWidth: "260px",
                      padding: "32px 32px 24px 32px",
                      display: "flex",
                      alignItems: "center",
                      gap: "18px",
                      boxShadow: "0 8px 32px rgba(44,62,80,0.18)",
                      border: "1.5px solid var(--primary-green)",
                      borderRadius: "18px",
                      background: "#fff",
                      position: "relative",
                    }}
                  >
                    <button
                      onClick={() => setShowTop10(false)}
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "none",
                        border: "none",
                        fontSize: "1.5rem",
                        color: "#aaa",
                        cursor: "pointer",
                        zIndex: 2,
                      }}
                      aria-label="Close Top 10"
                    >
                      x
                    </button>
                    <div
                      className="icon_wrapper green"
                      style={{ marginRight: "12px" }}
                    >
                      <Icons.Chart />
                    </div>
                    <div className="card_content" style={{ flex: 1 }}>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.25rem",
                          color: "var(--text-dark)",
                          fontWeight: 700,
                          letterSpacing: "0.2px",
                          textAlign: "center",
                        }}
                      >
                        Top 10 Learners
                      </h3>
                      <ol
                        style={{
                          margin: "18px 0 0 20px",
                          padding: 0,
                          textAlign: "left",
                        }}
                      >
                        {top10Students.map((s, i) => (
                          <li
                            key={s.student_id}
                            style={{
                              marginBottom: "7px",
                              fontWeight: 500,
                              color: "var(--text-grey)",
                              fontSize: "1.05rem",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "var(--text-dark)",
                                fontWeight: 600,
                                minWidth: 32,
                              }}
                            >
                              {i + 1}. {s.first_name} {s.last_name}
                            </span>
                            <span
                              style={{
                                marginLeft: 12,
                                color: "var(--primary-green)",
                                fontWeight: 700,
                                fontSize: "1.08rem",
                              }}
                            >
                              {s.average !== null
                                ? `${Math.round(s.average)}%`
                                : "-"}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {/* At Risk Learners Modal */}
              {showBottom10 && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(44,62,80,0.18)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    padding: "20px",
                    boxSizing: "border-box",
                    overflowY: "auto",
                  }}
                  onClick={() => {
                    setShowBottom10(false);
                    setShowResourceModal(false);
                  }}
                >
                  <div
                    className="stat_card"
                    style={{
                      margin: "0 auto",
                      width: "100%",
                      maxWidth: "420px",
                      minWidth: "260px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      border: "1.5px solid #e74c3c",
                      borderRadius: "18px",
                      background: "#fff",
                      position: "relative",
                      boxSizing: "border-box",
                      maxHeight: "calc(100vh - 40px)",
                      overflow: "visible",
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        background: "#e74c3c",
                        borderBottom: "1px solid #c83d31",
                        padding: "20px 48px 16px 20px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => {
                          setShowBottom10(false);
                          setShowResourceModal(false);
                        }}
                        style={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          background: "none",
                          border: "none",
                          fontSize: "1.5rem",
                          color: "#fff",
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                        aria-label="Close At Risk Learners"
                      >
                        x
                      </button>
                      <div
                        className="icon_wrapper"
                        style={{ color: "#e74c3c", marginBottom: "8px" }}
                      >
                        <Icons.Chart />
                      </div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "1.25rem",
                          color: "#fff",
                          fontWeight: 700,
                          letterSpacing: "0.2px",
                        }}
                      >
                        At Risk Learners (&lt;={" "}
                        {activePassRequirements.support_threshold}%)
                      </h3>
                    </div>
                    <div
                      className="card_content"
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "16px 20px 24px 20px",
                      }}
                    >
                      {atRiskLearners.length === 0 ? (
                        <div style={{ color: "#888", textAlign: "center" }}>
                          No learners at or below{" "}
                          {activePassRequirements.support_threshold}%.
                        </div>
                      ) : (
                        <>
                          <ol
                            style={{
                              margin: "18px 0 0 20px",
                              padding: 0,
                              textAlign: "left",
                            }}
                          >
                            {atRiskLearners.map((s, i) => (
                              <li
                                key={s.student_id}
                                style={{
                                  marginBottom: "7px",
                                  fontWeight: 500,
                                  color: "var(--text-grey)",
                                  fontSize: "1.05rem",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <span
                                  style={{
                                    color: "var(--text-dark)",
                                    fontWeight: 600,
                                    minWidth: 32,
                                  }}
                                >
                                  {i + 1}. {s.first_name} {s.last_name}
                                </span>
                                <span
                                  style={{
                                    marginLeft: 12,
                                    color: "#e74c3c",
                                    fontWeight: 700,
                                    fontSize: "1.08rem",
                                  }}
                                >
                                  {s.average !== null
                                    ? `${Math.round(s.average)}%`
                                    : "-"}
                                </span>
                              </li>
                            ))}
                          </ol>
                          <div style={{ marginTop: 24, textAlign: "center" }}>
                            <button
                              className="btn at-risk-resource-btn"
                              onClick={() => {
                                const willShowResources = !showResourceModal;
                                setShowResourceModal(willShowResources);
                                if (willShowResources)
                                  fetchResourceSuggestions();
                              }}
                            >
                              Show Suggested Interventions & Resources
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={openRevisionPlan}
                            >
                              Open Revision Plan
                            </button>
                          </div>
                          {showResourceModal && (
                            <section
                              style={{
                                marginTop: 18,
                                padding: 16,
                                border: "1px solid #f0bbb5",
                                borderRadius: 12,
                                background: "#fff8f7",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 12,
                                }}
                              >
                                <h4
                                  style={{
                                    margin: 0,
                                    color: "var(--text-dark)",
                                    fontSize: "1rem",
                                  }}
                                >
                                  Suggested Interventions & Resources
                                </h4>
                                <button
                                  onClick={() => setShowResourceModal(false)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "var(--text-dark)",
                                    cursor: "pointer",
                                    fontSize: "1.25rem",
                                    lineHeight: 1,
                                  }}
                                  aria-label="Close Resource Suggestions"
                                >
                                  x
                                </button>
                              </div>
                              <div style={{ marginTop: 14 }}>
                                {loadingResources ? (
                                  <div
                                    style={{
                                      color: "#666",
                                      textAlign: "center",
                                      margin: 12,
                                    }}
                                  >
                                    Loading resources...
                                  </div>
                                ) : resourceSuggestions.length === 0 ? (
                                  <div
                                    style={{
                                      color: "#666",
                                      textAlign: "center",
                                      margin: 12,
                                    }}
                                  >
                                    No resources found for this subject and
                                    grade.
                                  </div>
                                ) : (
                                  <ul
                                    style={{
                                      padding: 0,
                                      margin: 0,
                                      listStyle: "none",
                                    }}
                                  >
                                    {resourceSuggestions.map((res) => (
                                      <li
                                        key={res.id}
                                        style={{
                                          marginBottom: 14,
                                          borderBottom: "1px solid #f0d8d5",
                                          paddingBottom: 10,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontWeight: 600,
                                            color: "var(--text-dark)",
                                            fontSize: "1rem",
                                          }}
                                        >
                                          {res.title}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "0.9rem",
                                            color: "#666",
                                            margin: "4px 0 6px",
                                          }}
                                        >
                                          Grade {res.grade_level} |{" "}
                                          {res.subject}
                                        </div>
                                        <a
                                          href={`https://marksure.tech/${res.file_path}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#247a3d",
                                            fontWeight: 600,
                                            textDecoration: "underline",
                                            fontSize: "0.95rem",
                                          }}
                                        >
                                          Download Resource
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </section>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="table-toolbar">
                <div className="table-toolbar-copy">
                  <span className="table-toolbar-title">
                    Term {currentTerm} Marks
                  </span>
                  <span className="table-toolbar-note">
                    {isFoundationSubject
                      ? "Foundation Phase columns are grouped by CAPS component, then by sub-section."
                      : hasGroupedColumnHeaders
                        ? "Columns are grouped by assessment or category, then by section."
                        : "Search with Ctrl+F or Cmd+F."}
                  </span>
                  <button
                    type="button"
                    className="compact-table-toggle no-print"
                    onClick={() => setShowTableResults((visible) => !visible)}
                    aria-pressed={showTableResults}
                  >
                    {showTableResults
                      ? "Hide results columns"
                      : "Show results columns"}
                  </button>
                  <button
                    type="button"
                    className="compact-table-toggle no-print"
                    onClick={() => setMarkedTestsView({ runId: null })}
                  >
                    Marked tests
                  </button>
                </div>
                <div
                  className="table-toolbar-actions no-print"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 166px)",
                    gridAutoRows: "42px",
                    gap: "0.6rem",
                  }}
                >
                  <button
                    className="btn table-toolbar-action"
                    data-tour="voice"
                    style={{ width: "100%", height: "42px" }}
                    onClick={openVoiceMarking}
                    disabled={!sections.length || !students.length}
                  >
                    Voice marking
                  </button>
                  <button
                    type="button"
                    className="learner-levels-mini"
                    onClick={() => setShowLearnerLevelsModal(true)}
                    disabled={!learnersWithLevels}
                    aria-label="Open learner levels graph"
                  >
                    <span>Learner levels</span>
                    <LearnerLevelsChart
                      distribution={levelDistribution}
                      highestCount={highestLevelCount}
                      compact
                    />
                  </button>
                  <button
                    className="btn table-toolbar-action"
                    style={{ width: "100%", height: "42px" }}
                    onClick={() => setShowTop10((v) => !v)}
                  >
                    {showTop10 ? "Hide" : "Show"} Top 10
                    <img src="/assets/star.svg" alt="" aria-hidden="true" />
                  </button>
                  <button
                    className="btn table-toolbar-action table-toolbar-risk-action"
                    style={{ width: "100%", height: "42px" }}
                    onClick={() => setShowBottom10((v) => !v)}
                  >
                    {showBottom10 ? "Hide" : "Show"} At Risk
                    <img src="/assets/danger.svg" alt="" aria-hidden="true" />
                  </button>
                  <button
                    className={`btn btn-primary table-toolbar-action${saveComplete ? " save-complete" : ""}`}
                    data-tour="save-marks"
                    style={{ width: "100%", height: "42px" }}
                    onClick={handleBulkSave}
                    disabled={saveAllDisabled}
                  >
                    {saveAllDisabled ? (
                      <Icons.Save />
                    ) : saveComplete ? (
                      <Icons.Check />
                    ) : (
                      <Icons.Save />
                    )}
                    {saveAllDisabled
                      ? "Saving..."
                      : saveComplete
                        ? "Saved"
                        : "Save Changes"}
                  </button>
                </div>
              </div>
              {markLimitNotice && (
                <div
                  role="alert"
                  style={{
                    margin: "0 0 12px",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    background: "#fff4e5",
                    border: "1px solid #f1c27d",
                    color: "#8a4b08",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  {markLimitNotice}
                </div>
              )}

              <table
                className={`modern-table ${showTableResults ? "" : "results-hidden"} ${isFoundationSubject ? "foundation-table" : ""}`}
              >
                <thead>
                  <tr>
                    <th
                      scope="col"
                      style={{
                        textAlign: "left",
                        paddingLeft: "0.65rem",
                        minWidth: "105px",
                      }}
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      style={{ textAlign: "left", minWidth: "105px" }}
                    >
                      Surname
                    </th>
                    {sections.map((s, index) => {
                      const meta = sectionHeaderMeta[s.id];
                      const previousMeta =
                        index > 0
                          ? sectionHeaderMeta[sections[index - 1]?.id]
                          : null;
                      const isNewGroupedHeader = !!(
                        meta?.showGroupLabel &&
                        (!previousMeta ||
                          previousMeta.groupKey !== meta.groupKey)
                      );

                      return (
                        <th
                          key={s.id}
                          style={{
                            background: meta?.tint,
                            borderLeft: isNewGroupedHeader
                              ? "3px solid #cdd9ce"
                              : undefined,
                          }}
                        >
                          {meta?.showGroupLabel ? (
                            <>
                              <div
                                style={{
                                  fontSize: "0.68rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  color: "#597060",
                                  fontWeight: "700",
                                  marginBottom: "0.28rem",
                                }}
                              >
                                {meta.groupLabel}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.92rem",
                                  lineHeight: 1.25,
                                }}
                              >
                                {s.section_name}
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: "0.95rem" }}>
                              {s.section_name}
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#999",
                              fontWeight: "400",
                            }}
                          >
                            Max: {s.max_mark}
                          </div>
                        </th>
                      );
                    })}
                    {showTableResults && (
                      <>
                        <th
                          style={{
                            width: "86px",
                            background: "#e8f5e9",
                            borderLeft: "2px solid #dfe6e9",
                          }}
                        >
                          Raw Total
                        </th>
                        {isFoundationSubject && (
                          <th style={{ width: "86px", background: "#eef7f0" }}>
                            CAPS Total
                          </th>
                        )}
                        <th style={{ width: "58px", background: "#e8f5e9" }}>
                          %
                        </th>
                        <th style={{ width: "52px", background: "#e8f5e9" }}>
                          Lvl
                        </th>
                        <th className="no-print" style={{ width: "88px" }}>
                          Actions
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {studentsWithComputed.map((student) => (
                    <tr
                      key={student.student_id}
                      className={
                        String(student.student_id) === linkedStudent &&
                        currentTerm === requestedTerm
                          ? "pending-target-row"
                          : undefined
                      }
                    >
                      <td
                        style={{
                          textAlign: "left",
                          paddingLeft: "0.65rem",
                          fontWeight: "500",
                        }}
                      >
                        {student.first_name}
                      </td>
                      <td style={{ textAlign: "left", fontWeight: "500" }}>
                        {student.last_name}
                      </td>
                      {sections.map((s, index) => {
                        const key = `${student.student_id}-${s.id}`;
                        const markObj = marksMap.get(key);
                        const val =
                          marksBeingEdited[key] !== undefined
                            ? marksBeingEdited[key]
                            : (markObj?.mark ?? "");
                        const ref = getMarkInputRef(s.id, student.student_id);
                        const markedRun = markedRunByCell.get(key);
                        const meta = sectionHeaderMeta[s.id];
                        const previousMeta =
                          index > 0
                            ? sectionHeaderMeta[sections[index - 1]?.id]
                            : null;
                        const isNewGroupedHeader = !!(
                          meta?.showGroupLabel &&
                          (!previousMeta ||
                            previousMeta.groupKey !== meta.groupKey)
                        );

                        return (
                          <td
                            key={s.id}
                            style={{
                              padding: 0,
                              position: "relative",
                              background: meta?.tint,
                              borderLeft: isNewGroupedHeader
                                ? "3px solid #cdd9ce"
                                : undefined,
                            }}
                          >
                            <input
                              ref={ref}
                              className={`cell-input${String(student.student_id) === linkedStudent && currentTerm === requestedTerm && (val === "" || val == null) ? " pending-target-missing" : ""}`}
                              aria-label={`${student.first_name} ${student.last_name}, ${s.section_name}`}
                              value={val}
                              placeholder="-"
                              onFocus={handleMarkInputFocus}
                              onChange={(e) =>
                                handleSectionMarkChange(
                                  student.student_id,
                                  s.id,
                                  e.target.value,
                                )
                              }
                              onBlur={() =>
                                handleMarkBlur(student.student_id, s.id)
                              }
                              onKeyDown={(e) =>
                                handleGridNavigation(
                                  e,
                                  student.student_id,
                                  s.id,
                                )
                              }
                            />
                            {markedRun && (
                              <button
                                type="button"
                                tabIndex={-1}
                                className={`marked-test-cell-link no-print${markedRun.markbook?.changed ? " is-changed" : ""}`}
                                title={
                                  markedRun.markbook?.changed
                                    ? "Open marked test (markbook mark changed)"
                                    : "Open marked test"
                                }
                                aria-label={`Open marked test for ${student.first_name} ${student.last_name}, ${s.section_name}`}
                                onClick={() =>
                                  setMarkedTestsView({ runId: markedRun.run_id })
                                }
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.1"
                                  aria-hidden="true"
                                >
                                  <path d="M2.5 1h4.8L9.5 3.2V11h-7z" />
                                  <path d="M4.2 6.6l1.2 1.2 2.2-2.4" />
                                </svg>
                              </button>
                            )}
                          </td>
                        );
                      })}

                      {/* Raw Total */}
                      <td
                        style={{
                          fontWeight: "bold",
                          color: "#555",
                          background: "#f8f9fa",
                          borderLeft: "2px solid #dfe6e9",
                        }}
                      >
                        {student.rawMaxSum > 0 ? (
                          <span>
                            {Number(student.rawMarkSum).toFixed(1)}{" "}
                            <span style={{ fontSize: "0.8em", color: "#999" }}>
                              / {student.rawMaxSum}
                            </span>
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      {isFoundationSubject && (
                        <td
                          style={{
                            fontWeight: "bold",
                            color: "#41644a",
                            background: "#f3faf4",
                          }}
                        >
                          {student.finalMaxSum > 0 ? (
                            <span>
                              {Number(student.finalMarkSum).toFixed(1)}{" "}
                              <span
                                style={{ fontSize: "0.8em", color: "#7b8d80" }}
                              >
                                / {student.finalMaxSum}
                              </span>
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}

                      <td
                        style={{
                          background: "#fcfcfc",
                          fontWeight: "bold",
                          color:
                            student.average !== null &&
                            student.average >= activePassRequirements.pass_mark
                              ? "var(--primary-green)"
                              : "#e74c3c",
                        }}
                      >
                        {student.average !== null
                          ? Math.round(student.average) + "%"
                          : "-"}
                        {top10Ids.includes(student.student_id) && (
                          <span className="top-badge" title="Top 10">
                            🌟
                          </span>
                        )}
                      </td>

                      <td style={{ background: "#fcfcfc", fontWeight: "bold" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: "24px",
                            height: "24px",
                            lineHeight: "24px",
                            borderRadius: "50%",
                            background:
                              student.level >= 4
                                ? "var(--light-green)"
                                : "#fee2e2",
                            color:
                              student.level >= 4
                                ? "var(--dark-green)"
                                : "#dc2626",
                            fontSize: "0.85rem",
                          }}
                        >
                          {formatGrade(student.level)}
                        </span>
                      </td>
                      <td className="no-print">
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <ISPGenerator
                            learnerId={student.student_id}
                            term={currentTerm}
                          />
                          <button
                            className="btn-danger"
                            style={{ borderRadius: "4px", padding: "4px 8px" }}
                            onClick={() =>
                              handleDeleteStudent(
                                student.student_id,
                                student.first_name,
                              )
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Performance Analytics (Dynamic per Term) */}
            {sections.length > 0 && (
              <div className="no-print" style={{ marginTop: "40px" }}>
                <h3 className="stats-section-title">
                  <Icons.Chart /> Term {currentTerm} Analytics
                </h3>
                <div className="analytics-grid">
                  {sections.map((section) => {
                    const stats = getSectionStats(section);
                    if (!stats) return null;

                    return (
                      <div key={section.id} className="stat-card">
                        <h4>{section.section_name}</h4>
                        <div className="stat-grid-inner">
                          <div className="stat-item">
                            <span className="stat-label">Average Mark</span>
                            <span className="stat-value">
                              {stats.avg.toFixed(1)}
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Avg %</span>
                            <span className="stat-value green">
                              {stats.avgPerc.toFixed(1)}%
                            </span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Highest</span>
                            <span className="stat-value">{stats.max}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Lowest</span>
                            <span className="stat-value red">{stats.min}</span>
                          </div>
                        </div>
                        <div className="submission-count">
                          <Icons.Check /> Submitted:{" "}
                          <strong>{stats.count}</strong> / {students.length}{" "}
                          students
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {showCompletionCelebration && (
        <div
          className="completion-celebration"
          aria-live="polite"
          role="status"
        >
          <div className="completion-celebration-message">
            <Icons.Check /> All marks captured — great work!
          </div>
          {Array.from({ length: 30 }, (_, index) => (
            <span
              className="completion-confetti"
              key={index}
              style={{
                "--left": `${(index * 37) % 101}%`,
                "--colour": [
                  "#4caf50",
                  "#f3b431",
                  "#4c8bf5",
                  "#e86b73",
                  "#8b5cc7",
                ][index % 5],
                "--rotate": `${(index * 53) % 180}deg`,
                "--delay": `${(index % 9) * 0.08}s`,
                "--drift": `${((index * 29) % 150) - 75}px`,
              }}
            />
          ))}
        </div>
      )}
      {showVoiceModal && (
        <WorkflowModal
          title="Voice marking"
          onClose={() => {
            stopVoiceListening();
            setShowVoiceModal(false);
          }}
        >
          <div className="voice-marking-modal">
            <p>
              Start once, then say one learner name and mark at a time. Each
              recognised mark saves automatically while the microphone continues
              listening.
            </p>
            <label className="stacked-field">
              <span>Assessment</span>
              <select
                className="modern-input"
                value={voiceSectionId}
                onChange={(event) => {
                  setVoiceSectionId(event.target.value);
                  setVoiceCandidate(null);
                }}
              >
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.section_name} (max {section.max_mark})
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={`btn btn-primary voice-listen-button${voiceListening ? " is-listening" : ""}`}
              onClick={
                voiceListening ? stopVoiceListening : startVoiceListening
              }
            >
              {voiceListening
                ? "Stop continuous marking"
                : "Start continuous marking"}
            </button>
            {voiceTranscript && (
              <div className="voice-transcript">
                <strong>Heard:</strong> “{voiceTranscript}”
              </div>
            )}
            <p className="voice-status" role="status">
              {voiceStatus}
            </p>
            {voiceCandidate && (
              <div className="voice-candidate">
                <span>Ready to save</span>
                <strong>
                  {voiceCandidate.student.first_name}{" "}
                  {voiceCandidate.student.last_name} — {voiceCandidate.mark} /{" "}
                  {voiceCandidate.section.max_mark}
                </strong>
                <button type="button" className="btn btn-primary" disabled>
                  {savingVoiceMark ? "Saving…" : "Saved automatically"}
                </button>
              </div>
            )}
          </div>
        </WorkflowModal>
      )}
      {markedTestsView && (
        <WorkflowModal
          title="Marked tests"
          onClose={() => setMarkedTestsView(null)}
        >
          <MarkedTests
            subjectId={id}
            initialTerm={currentTerm}
            initialRunId={markedTestsView.runId}
          />
        </WorkflowModal>
      )}
      {showLearnerLevelsModal && (
        <WorkflowModal
          title={`Learner levels — Term ${currentTerm}`}
          onClose={() => setShowLearnerLevelsModal(false)}
        >
          <div className="learner-levels-modal">
            <p>
              Distribution of learners by achievement level, calculated from the
              marks currently entered for this term.
            </p>
            <div className="learner-levels-modal__summary">
              <strong>{learnersWithLevels}</strong>
              <span>of {students.length} learners scored</span>
            </div>
            {learnersWithLevels > 0 ? (
              <LearnerLevelsChart
                distribution={levelDistribution}
                highestCount={highestLevelCount}
              />
            ) : (
              <p className="level-distribution-empty">
                Enter marks to see the level distribution.
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary learner-levels-export"
              onClick={exportLearnerLevels}
            >
              Export levels to Excel
            </button>
          </div>
        </WorkflowModal>
      )}
      {importResult && (
        <WorkflowModal
          title={`Import complete — Term ${importResult.term}`}
          onClose={() => setImportResult(null)}
        >
          <div className="import-result-modal">
            <div className="import-result-icon" aria-hidden="true">
              <Icons.Check />
            </div>
            <p className="import-result-summary">
              Your marks have finished processing and the subject has been
              refreshed.
            </p>
            <div className="import-result-grid">
              <div>
                <strong>{importResult.sectionsCreated}</strong>
                <span>New assessments</span>
              </div>
              <div>
                <strong>{importResult.studentsCreated}</strong>
                <span>New learners</span>
              </div>
              <div>
                <strong>{importResult.successCount}</strong>
                <span>Marks updated</span>
              </div>
              <div
                className={
                  importResult.rejectedMarks ? "import-result-warning" : ""
                }
              >
                <strong>{importResult.rejectedMarks}</strong>
                <span>Marks rejected</span>
              </div>
              <div>
                <strong>{importResult.skippedColumns}</strong>
                <span>Columns skipped</span>
              </div>
            </div>
            {importResult.rejectedMarks > 0 && (
              <p className="import-result-note">
                Rejected marks were outside the maximum allowed for their
                assessment.
              </p>
            )}
            <div className="form-actions import-result-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setImportResult(null)}
              >
                Done
              </button>
            </div>
          </div>
        </WorkflowModal>
      )}
      {showRosterScan && (
        <WorkflowModal
          title="Scan / Upload Classlist"
          onClose={closeRosterScan}
        >
          <div className="roster-scan-modal">
            {!rosterRows.length && !rosterScanning && (
              <>
                <p className="roster-scan-intro">
                  Upload a class list or capture a photo. You will review every
                  name before anything is added.
                </p>
                <div
                  className="roster-drop-zone"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    selectRosterImage(event.dataTransfer.files?.[0]);
                  }}
                >
                  {rosterPreview ? (
                    <img src={rosterPreview} alt="Selected class list" />
                  ) : (
                    <span>Drop a PNG, JPEG, or WEBP image here</span>
                  )}
                  <div className="roster-upload-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => rosterFileInputRef.current?.click()}
                    >
                      Choose image
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => rosterCameraInputRef.current?.click()}
                    >
                      Use camera
                    </button>
                  </div>
                  <input
                    ref={rosterFileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(event) =>
                      selectRosterImage(event.target.files?.[0])
                    }
                  />
                  <input
                    ref={rosterCameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(event) =>
                      selectRosterImage(event.target.files?.[0])
                    }
                  />
                </div>
                {rosterPreview && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRosterScan}
                  >
                    Scan class list
                  </button>
                )}
              </>
            )}
            {rosterScanning && (
              <div className="roster-scan-loading">
                <span className="spinner"></span>
                <strong>Scanning handwriting and reading names...</strong>
                <span>This can take a moment.</span>
              </div>
            )}
            {rosterError && (
              <p className="roster-scan-error" role="alert">
                {rosterError}
              </p>
            )}
            {!!rosterRows.length && !rosterScanning && (
              <>
                <p className="roster-scan-intro">
                  Check names and any detected marks before confirming. Use
                  “Assessment: mark” for each mark, separated by semicolons. A
                  scanned mark imports only when its assessment name matches a
                  section in this class.
                </p>
                <div className="roster-review-layout">
                  <div className="roster-image-pane">
                    {rosterPreview && (
                      <img
                        src={rosterPreview}
                        alt="Original uploaded class list"
                      />
                    )}
                  </div>
                  <div className="roster-grid-wrap">
                    <table className="roster-review-grid">
                      <thead>
                        <tr>
                          <th aria-label="Select learner"></th>
                          <th>First name</th>
                          <th>Surname</th>
                          <th>Display name</th>
                          <th>Detected marks</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rosterRows.map((row) => (
                          <tr
                            key={row.key}
                            className={row.needs_review ? "needs-review" : ""}
                          >
                            <td>
                              <input
                                aria-label={`Select ${row.first_name} ${row.last_name}`}
                                type="checkbox"
                                checked={row.selected}
                                onChange={(event) =>
                                  updateRosterRow(
                                    row.key,
                                    "selected",
                                    event.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={row.first_name}
                                onChange={(event) =>
                                  updateRosterRow(
                                    row.key,
                                    "first_name",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={row.last_name}
                                onChange={(event) =>
                                  updateRosterRow(
                                    row.key,
                                    "last_name",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <span>
                                {`${row.first_name} ${row.last_name}`.trim() ||
                                  row.raw_name ||
                                  "—"}
                                {row.needs_review && (
                                  <small>Check spelling</small>
                                )}
                              </span>
                            </td>
                            <td>
                              <input
                                aria-label={`Marks for ${row.first_name} ${row.last_name}`}
                                placeholder="Test 1: 75; Exam: 62"
                                value={row.marks_text}
                                onChange={(event) =>
                                  updateRosterRow(
                                    row.key,
                                    "marks_text",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="roster-delete"
                                aria-label="Delete learner row"
                                onClick={() =>
                                  setRosterRows((current) =>
                                    current.filter(
                                      (item) => item.key !== row.key,
                                    ),
                                  )
                                }
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="roster-review-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={addRosterRow}
                  >
                    + Add row
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={rosterImporting}
                    onClick={importRosterRows}
                  >
                    {rosterImporting
                      ? "Adding learners…"
                      : "Confirm & Add to Class"}
                  </button>
                </div>
              </>
            )}
          </div>
        </WorkflowModal>
      )}
      {successMessage && (
        <WorkflowModal
          title={successMessage.title}
          onClose={() => setSuccessMessage(null)}
        >
          <div className="success-confirmation-modal">
            <div className="import-result-icon" aria-hidden="true">
              <Icons.Check />
            </div>
            <p>{successMessage.message}</p>
            <div className="form-actions import-result-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setSuccessMessage(null)}
              >
                Done
              </button>
            </div>
          </div>
        </WorkflowModal>
      )}
      <FeatureTour steps={SUBJECT_TOUR} open={subjectTour.open} onClose={subjectTour.close} />
    </div>
  );
}
